import { useCloneActivity } from './canvas-clone-progress';
import './composer-toolbar.css';
import { checkBrandReady } from './brand-readiness';
import { isTourCompleted, syncTourCompletion } from '@/components/tour-completion';
import {canvasFollowUps} from './canvas-follow-ups';
import { ChatMessageLink } from './ChatMessageLink';
import { normalizeChatLinks } from './chat-links';
import { openChatPlatformLink } from './chat-platform-navigation';
import { ThinkingBlock, BottomStatusLine } from './canvas-chat-status';
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Element as HastElement } from 'hast';

import { useVideoEnabled, useVideoGateCopy } from '@/hooks/use-video-enabled';
import { AD_STYLES_18, AD_ANGLES_18 } from '@shared/constants/adStyles';
import { useCanvasStore } from './canvas-store';
import { editVideoCloneFrameFromChat, isVideoCloneFrameSrc, latestEditableDraftId, pushVideoBusyNotice, runVideoCloneChatEdit, runVideoCloneGenerate } from './video-clone-runner';
import { DiscoverSections } from './canvas-discover-sections';
import { BrandIdentityToggle } from './canvas-brand-identity-toggle';
import { FirstRunTour } from '@/components/first-run-tour';
import { useAuthUser } from '@/App';
import {
  useActiveProjectImages,
  useActiveProjectMessages,
  useActiveProjectQaBadges,
  useActiveProjectIsCanvasOpen,
  useActiveProjectActiveView,
  useActiveProjectHasPersistedImages,
  useActiveProjectId,
  useActiveProjectLoading,
} from './store-selectors';
import type { CanvasInlineChart, ChatArtifact, TaskStep, WebResearchSource, AgentActionCard, AgentTaskGroup, AgentTaskStep, AgentDocument, AdCardData, WorkflowState, WorkflowNudge, CanvasChatMessage, WorkflowThinkingEntry, AgentPlan, QAStatus, PlaybookProgressState } from './canvas-types';
import { AgentStepCard } from './AgentStepCard';
import { AgentPlanCard } from './AgentPlanCard';
import { WorkflowTaskGroup } from './WorkflowTaskCard';
import { TaskProgressCard } from './TaskProgressCard';
import { TaskBottomBar } from './TaskBottomBar';
import { DocumentCard } from './DocumentCard';
import { DocumentOverlay } from './DocumentOverlay';
import { SidebarPanel } from './SidebarPanel';
import type { SidebarPanelContent, AdViewData } from './SidebarPanel';
import { AdCardsRow } from '../../components/canvas/AdCard';
import { AnalyticsCard } from './AnalyticsCard';
import { AdDetailSidebar } from '../../components/canvas/AdDetailSidebar';
import { PlaybookTaskCard } from '@/components/canvas/PlaybookTaskCard';
import { SettingsModal } from '../../components/settings-modal';
import { ChatToolProgressCard } from '@/components/canvas/ChatToolProgressCard';
import { StagePicker } from '@/components/canvas/StagePicker';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useUsageBalance } from '@/hooks/use-usage';
import { SlidersHorizontal,
  ChevronDown,
  CheckCircle2,
  Globe,
  Loader2,
  Circle,
  ImageIcon,
  Plus,
  ArrowUp,
  Sparkles,
  XCircle,
  AlertCircle,
  WifiOff,
  X,
  BarChart2,
  TrendingUp,
  Monitor,
  ExternalLink,
  FileText,
  Play,
  MoreHorizontal,
  Search,
  Save,
  Image,
  PenLine,
  LineChart,
  Copy,
  FolderInput,
  History,
  Folder,
  Clapperboard,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart as ReLineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// v4.89 — home "Build Campaign" live progress (mirrors AdAnglesPanel).
const FF_STAGE_LABELS: Record<string, string> = {
  TOF: 'Top of Funnel',
  MOF: 'Middle of Funnel',
  BOF: 'Bottom of Funnel',
  RETARGETING: 'Retargeting',
  COMPETITOR: 'Competitor',
};

/**
 * Read the angle-set build SSE response to completion, invoking onEvent per
 * frame. Frames are `event: <name>` + `data: <json>` blocks with FLATTENED
 * `{type, ...fields}` data — same wire contract AdAnglesPanel's
 * readSSEEvents parses. Do NOT parse as canvas-chat's `{type, data}` envelope.
 */
async function readBuildSSEEvents(
  res: Response,
  onEvent: (eventName: string, data: unknown) => void,
): Promise<void> {
  const reader = res.body?.getReader();
  if (!reader) return;
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep;
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const chunk = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      let eventName = 'message';
      let data = '';
      for (const line of chunk.split('\n')) {
        if (line.startsWith('event:')) eventName = line.slice(6).trim();
        else if (line.startsWith('data:')) data += line.slice(5).trim();
      }
      if (data) {
        try {
          onEvent(eventName, JSON.parse(data));
        } catch {
          onEvent(eventName, data);
        }
      }
    }
  }
}

// ── Workflow UI Components ─────────────────────────────────────────────────────

const PHASE_ICONS = ['🔍', '📊', '🎯', '✍️', '🖼️'];

function WorkflowConfirmationCard({
  wf,
  onConfirm,
  onKeepSimple,
  disabled,
}: {
  wf: WorkflowState;
  onConfirm: () => void;
  onKeepSimple: () => void;
  disabled?: boolean;
}) {
  return (
    <div data-testid="workflow-confirmation-card" style={{ marginTop: 10 }}>
      {wf.description && (
        <p style={{ margin: '0 0 10px', fontSize: 13, color: '#5C5852', lineHeight: 1.5 }}>
          {wf.description}
        </p>
      )}
      {wf.phases && wf.phases.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 12 }}>
          {wf.phases.map((phase, i) => (
            <div key={i} className="flex items-center gap-2" style={{ opacity: 0.8 }}>
              <span style={{ fontSize: 13 }}>{PHASE_ICONS[i] || '◻'}</span>
              <span style={{ fontSize: 12, color: '#5C5852' }}>{phase.name}</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <button
          data-testid="workflow-confirm-start"
          disabled={disabled}
          onClick={onConfirm}
          style={{
            background: '#6366F1',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            padding: '8px 16px',
            fontSize: 13,
            fontWeight: 600,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
          }}
        >
          Let's go
        </button>
        <button
          data-testid="workflow-confirm-dismiss"
          disabled={disabled}
          onClick={onKeepSimple}
          style={{
            background: 'transparent',
            color: '#5C5852',
            border: '1px solid #D1CDC6',
            borderRadius: 8,
            padding: '8px 16px',
            fontSize: 13,
            fontWeight: 500,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
          }}
        >
          Keep it simple
        </button>
      </div>
    </div>
  );
}

function WorkflowProgressStepper({ wf }: { wf: WorkflowState }) {
  const phases = wf.phases || [];
  const currentIdx = wf.currentPhaseIndex ?? 0;
  const status = wf.status || 'running';

  return (
    <div
      data-testid="workflow-progress-stepper"
      style={{
        background: '#F9F8F5',
        border: '1px solid #E5E3DF',
        borderRadius: 12,
        padding: '14px 16px',
        marginTop: 8,
      }}
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4" style={{ color: '#7B1C2E' }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A' }}>
          {wf.name || 'Campaign Launch'}
        </span>
        {status === 'running' && (
          <span style={{ fontSize: 11, color: '#8C8578', marginLeft: 'auto' }}>
            Phase {currentIdx + 1} of {wf.totalPhases ?? phases.length}
          </span>
        )}
        {status === 'completed' && (
          <span style={{ fontSize: 11, color: '#22a35a', marginLeft: 'auto', fontWeight: 600 }}>Complete</span>
        )}
        {status === 'paused' && (
          <span style={{ fontSize: 11, color: '#b98b00', marginLeft: 'auto', fontWeight: 600 }}>Review needed</span>
        )}
        {status === 'cancelled' && (
          <span style={{ fontSize: 11, color: '#8C8578', marginLeft: 'auto' }}>Cancelled</span>
        )}
        {status === 'error' && (
          <span style={{ fontSize: 11, color: '#c0392b', marginLeft: 'auto' }}>Error</span>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {phases.map((phase, i) => {
          const isDone = status === 'completed' || i < currentIdx;
          const isActive = status === 'running' && i === currentIdx;
          const isPaused = status === 'paused' && i === currentIdx;
          return (
            <div key={i} className="flex items-center gap-2.5" style={{ opacity: i > currentIdx && status !== 'completed' ? 0.4 : 1 }}>
              <span style={{ fontSize: 14 }}>{PHASE_ICONS[i] || '◻'}</span>
              <span style={{ fontSize: 12.5, color: isDone ? '#22a35a' : isActive || isPaused ? '#1A1A1A' : '#8C8578', fontWeight: isActive || isPaused ? 600 : 400 }}>
                {phase.name}
              </span>
              {isDone && <CheckCircle2 className="w-3.5 h-3.5 ml-auto" style={{ color: '#22a35a' }} />}
              {isActive && (
                <Loader2 className="w-3.5 h-3.5 ml-auto animate-spin" style={{ color: '#7B1C2E' }} />
              )}
              {isPaused && (
                <span style={{ fontSize: 10, color: '#b98b00', marginLeft: 'auto', fontWeight: 600 }}>REVIEW</span>
              )}
            </div>
          );
        })}
      </div>
      {wf.errorMessage && (
        <div style={{ marginTop: 10, fontSize: 12, color: '#c0392b', background: '#fff0ee', borderRadius: 6, padding: '6px 10px' }}>
          {wf.errorMessage}
        </div>
      )}
    </div>
  );
}

function WorkflowNudgeMessage({
  nudge,
  onSend,
  onGoDeep,
  onKeepSimple,
  disabled,
}: {
  nudge: WorkflowNudge;
  onSend: (msg: string) => void;
  onGoDeep?: (skeletonType: string, originalMessage: string) => void;
  onKeepSimple?: (originalMessage: string) => void;
  disabled?: boolean;
}) {
  return (
    <div
      data-testid="workflow-nudge-message"
      style={{ marginTop: 10 }}
    >
      <p style={{ margin: '0 0 10px', fontSize: 13.5, color: '#3D3A36', lineHeight: 1.5 }}>
        {nudge.message}
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {nudge.options.map((opt, i) => {
          const displayLabel = opt.value === 'go_deep' ? 'Go deep' : opt.value === 'keep_simple' ? 'Keep it simple' : opt.label;
          return (
            <button
              key={i}
              data-testid={`workflow-nudge-option-${opt.value}`}
              disabled={disabled}
              onClick={() => {
                if (opt.value === 'go_deep' && nudge.skeletonType && onGoDeep) {
                  onGoDeep(nudge.skeletonType, nudge.originalMessage || nudge.message);
                } else if (opt.value === 'keep_simple' && onKeepSimple) {
                  onKeepSimple(nudge.originalMessage || opt.label);
                } else {
                  onSend(nudge.originalMessage || opt.label);
                }
              }}
              style={{
                background: opt.value === 'go_deep' ? '#6366F1' : 'transparent',
                border: opt.value === 'go_deep' ? 'none' : '1px solid #D1CDC6',
                borderRadius: 8,
                padding: '8px 16px',
                fontSize: 13,
                color: opt.value === 'go_deep' ? 'white' : '#5C5852',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                fontWeight: opt.value === 'go_deep' ? 600 : 500,
              }}
            >
              {displayLabel}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StrategyReviewCard({ payload, onSend, onCheckpointAction, disabled }: {
  payload: WorkflowState['checkpointPayload'];
  onSend: (msg: string) => void;
  onCheckpointAction?: (action: 'approved' | 'modified' | 'rejected', feedback?: string) => void;
  disabled?: boolean;
}) {
  const strategy = payload as {
    campaignTheme?: string;
    angles?: Array<{ name: string; hook: string; exampleHeadline?: string; targetEmotion?: string; creativeDirection?: string }>;
    recommendedAngle?: number;
    rationale?: string;
  };

  return (
    <div
      data-testid="strategy-review-card"
      style={{ marginTop: 10 }}
    >
      {strategy.campaignTheme && (
        <p style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: '#1A1A1A', lineHeight: 1.4 }}>{strategy.campaignTheme}</p>
      )}

      {strategy.angles && strategy.angles.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
          {strategy.angles.map((angle, i) => (
            <div
              key={i}
              style={{
                borderLeft: i === (strategy.recommendedAngle ?? 0) ? '3px solid #6366F1' : '3px solid transparent',
                paddingLeft: 10,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 700, color: '#1A1A1A' }}>{angle.name}</span>
              <span style={{ fontSize: 12.5, color: '#6B7280', marginLeft: 8 }}>{angle.hook}</span>
              {angle.exampleHeadline && (
                <p style={{ margin: '3px 0 0', fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>"{angle.exampleHeadline}"</p>
              )}
            </div>
          ))}
        </div>
      )}

      {strategy.rationale && (
        <p style={{ margin: '0 0 14px', fontSize: 12.5, color: '#6B7280', lineHeight: 1.6 }}>{strategy.rationale}</p>
      )}

      <button
        data-testid="strategy-action-approve-strategy"
        disabled={disabled}
        onClick={() => { if (onCheckpointAction) onCheckpointAction('approved'); else onSend('Looks great, approve strategy'); }}
        style={{
          background: '#6366F1',
          color: 'white',
          border: 'none',
          borderRadius: 8,
          padding: '8px 16px',
          fontSize: 13,
          fontWeight: 600,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        Approve
      </button>
    </div>
  );
}

function GalleryReviewCard({ payload, onSend, onCheckpointAction, disabled }: {
  payload: WorkflowState['checkpointPayload'];
  onSend: (msg: string) => void;
  onCheckpointAction?: (action: 'approved' | 'modified' | 'rejected', feedback?: string) => void;
  disabled?: boolean;
}) {
  const galleryData = payload as {
    images?: Array<{ briefId: string; imageUrl: string; title: string; headline: string; adCopy: string; cta: string }>;
  };
  const images = galleryData?.images || [];

  return (
    <div
      data-testid="gallery-review-card"
      style={{ marginTop: 10 }}
    >
      {images.length > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
          {images.map((img, i) => (
            <div
              key={i}
              data-testid={`gallery-image-${i}`}
              style={{ borderRadius: 8, overflow: 'hidden', background: '#F9F8F5', aspectRatio: '1' }}
            >
              {img.imageUrl ? (
                <img
                  src={img.imageUrl}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ImageIcon className="w-6 h-6" style={{ color: '#D1CDC6' }} />
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p style={{ margin: '0 0 12px', fontSize: 13, color: '#6B7280' }}>Images are ready for review.</p>
      )}

      <button
        data-testid="gallery-action-approve-all"
        disabled={disabled}
        onClick={() => { if (onCheckpointAction) onCheckpointAction('approved'); else onSend('Looks great, approve all images'); }}
        style={{
          background: '#6366F1',
          color: 'white',
          border: 'none',
          borderRadius: 8,
          padding: '8px 16px',
          fontSize: 13,
          fontWeight: 600,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        Approve
      </button>
    </div>
  );
}

function WorkflowThinkingFeed({ feed }: { feed: WorkflowThinkingEntry[] }) {
  const feedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [feed.length]);

  if (!feed || feed.length === 0) return null;

  const getIcon = (entry: WorkflowThinkingEntry) => {
    if (entry.type === 'tool_result') return '✓';
    if (entry.type === 'tool_start') {
      const name = (entry.toolName || '').toLowerCase();
      if (name.includes('search') || name.includes('discover') || name.includes('ad_library')) return '🔍';
      if (name.includes('analyz') || name.includes('research') || name.includes('competitor')) return '📊';
      if (name.includes('generat') || name.includes('image') || name.includes('creative')) return '🎨';
      return '💡';
    }
    if (entry.type === 'thinking') return '💡';
    return '';
  };

  return (
    <div
      ref={feedRef}
      data-testid="workflow-thinking-feed"
      style={{
        maxHeight: 200,
        overflowY: 'auto',
        marginTop: 8,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      {feed.map((entry) => {
        const icon = getIcon(entry);
        return (
          <div
            key={entry.id}
            style={{
              display: 'flex',
              gap: 6,
              alignItems: 'flex-start',
              borderLeft: '2px solid #E5E3DF',
              paddingLeft: 8,
              animation: 'wf-fade-in 0.25s ease',
            }}
          >
            {icon && <span style={{ fontSize: 11, lineHeight: '18px', flexShrink: 0 }}>{icon}</span>}
            <span style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.5 }}>{entry.text}</span>
          </div>
        );
      })}
      <style>{`@keyframes wf-fade-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }`}</style>
    </div>
  );
}

// ── End Workflow UI Components ─────────────────────────────────────────────────

interface Brand {
  id: number;
  name: string;
  industry?: string;
}

interface AdAccount {
  id: number;
  accountName: string;
  platformName?: string;
}

function formatTime(ts?: number): string {
  const d = ts ? new Date(ts) : new Date();
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function InlineChartView({ chart }: { chart: CanvasInlineChart }) {
  const data = chart.labels.map((label, i) => ({ label, value: chart.values[i] ?? 0 }));
  const Icon = chart.type === 'line' ? TrendingUp : BarChart2;

  return (
    <div className="my-2 bg-white rounded-xl p-3" style={{ border: '1px solid #E5E2DC' }}>
      <div className="flex items-center gap-1.5 mb-2">
        <Icon className="w-3.5 h-3.5" style={{ color: '#8C8578' }} />
        <span className="text-[11px] font-semibold text-[#1A1A1A]">{chart.title}</span>
      </div>
      <ResponsiveContainer width="100%" height={160}>
        {chart.type === 'line' ? (
          <ReLineChart data={data} margin={{ top: 0, right: 4, left: -28, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#B5B0A8' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 9, fill: '#B5B0A8' }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ fontSize: 10, background: '#fff', border: '1px solid #E5E2DC', borderRadius: 6, padding: '4px 8px' }} itemStyle={{ color: '#3D3A36' }} />
            <Line type="monotone" dataKey="value" stroke="#3D3A36" strokeWidth={2} dot={{ r: 2, fill: '#3D3A36' }} />
          </ReLineChart>
        ) : (
          <BarChart data={data} margin={{ top: 0, right: 4, left: -28, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#B5B0A8' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 9, fill: '#B5B0A8' }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ fontSize: 10, background: '#fff', border: '1px solid #E5E2DC', borderRadius: 6, padding: '4px 8px' }} itemStyle={{ color: '#3D3A36' }} />
            <Bar dataKey="value" fill="#3D3A36" radius={[3, 3, 0, 0]} maxBarSize={24} />
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

interface AdRefData {
  name: string;
  creativeUrl?: string;
  thumbnailUrl?: string;
  headline?: string;
  roas: string;
  ctr: string;
  spend: string;
  clicks: number;
}

function AdRefChip({ name, adAccountId }: { name: string; adAccountId?: number }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<AdRefData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const images = useActiveProjectImages();
  const { setSelectedImageIds, setPan, zoom } = useCanvasStore();

  const handleClick = async () => {
    const lowerName = name.toLowerCase();
    const match = images.find((img) =>
      img.name.toLowerCase().includes(lowerName) || lowerName.includes(img.name.toLowerCase().replace(/\.[^.]+$/, ""))
    );
    if (match) {
      setSelectedImageIds([match.id]);
      const targetX = -(match.x + match.displayWidth / 2) * zoom + window.innerWidth / 2;
      const targetY = -(match.y + match.displayHeight / 2) * zoom + window.innerHeight / 2;
      setPan(targetX, targetY);
      return;
    }

    setOpen((prev) => !prev);
    if (!open && adAccountId && !data && !loading) {
      setLoading(true);
      setError(null);
      try {
        const resp = await fetch(`/api/canvas/ad-ref/${adAccountId}?name=${encodeURIComponent(name)}`);
        if (!resp.ok) throw new Error("Not found");
        const json = await resp.json();
        if (!json) throw new Error("No ad data found");
        setData(json);
      } catch {
        setError("No ad data found");
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <span className="relative inline-block mr-1 mb-1">
      <button
        onClick={handleClick}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium cursor-pointer transition-colors"
        style={{ background: '#F5F3EE', border: '1px solid #E5E2DC', color: '#8C8578' }}
      >
        <Monitor className="w-2.5 h-2.5" />
        {name}
        <ExternalLink className="w-2 h-2 opacity-60" />
      </button>
      {open && (
        <div
          ref={popoverRef}
          className="absolute bottom-full left-0 mb-2 z-50 w-64 bg-white rounded-xl shadow-xl p-3 text-left"
          style={{ border: '1px solid #E5E2DC' }}
        >
          <div className="flex items-center gap-1.5 mb-2">
            <Monitor className="w-3.5 h-3.5" style={{ color: '#8C8578' }} />
            <span className="text-[11px] font-semibold text-[#1A1A1A] truncate">{name}</span>
          </div>
          {loading && <div className="text-[10px] text-center py-3" style={{ color: '#B5B0A8' }}><Loader2 className="w-3 h-3 animate-spin inline mr-1" />Loading…</div>}
          {error && <div className="text-[10px] text-red-400 text-center py-2">{error}</div>}
          {data && !loading && (
            <>
              {(data.thumbnailUrl || data.creativeUrl) && (
                <img src={data.thumbnailUrl || data.creativeUrl} alt={data.name} className="w-full h-28 object-cover rounded-lg mb-2" style={{ border: '1px solid #E5E2DC' }} />
              )}
              {data.headline && <div className="text-[10px] mb-2 font-medium truncate" style={{ color: '#8C8578' }}>{data.headline}</div>}
              <div className="grid grid-cols-2 gap-1.5">
                {[
                  { label: "ROAS", value: `${data.roas}x` },
                  { label: "CTR", value: `${data.ctr}%` },
                  { label: "Spend", value: `$${Number(data.spend).toLocaleString()}` },
                  { label: "Clicks", value: data.clicks.toLocaleString() },
                ].map(({ label, value }) => (
                  <div key={label} className="rounded-lg p-1.5" style={{ background: '#F5F3EE' }}>
                    <div className="text-[9px] uppercase tracking-wide" style={{ color: '#B5B0A8' }}>{label}</div>
                    <div className="text-[11px] font-semibold text-[#1A1A1A]">{value}</div>
                  </div>
                ))}
              </div>
            </>
          )}
          {!adAccountId && (
            <div className="text-[10px] text-center py-2" style={{ color: '#B5B0A8' }}>Select an ad account to view metrics</div>
          )}
        </div>
      )}
    </span>
  );
}

function ArtifactCard({ artifact }: { artifact: ChatArtifact }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const openDocumentOverlay = useCanvasStore((s) => s.openDocumentOverlay);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    if (menuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const handleCopy = () => {
    navigator.clipboard.writeText(artifact.content).catch(() => {});
    setMenuOpen(false);
  };

  const handleDownload = () => {
    const blob = new Blob([artifact.content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${artifact.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setMenuOpen(false);
  };

  const handleOpen = () => {
    openDocumentOverlay(artifact.title, artifact.content);
  };

  const paragraphs = artifact.content.split(/\n\n+/).filter(Boolean);
  const previewParagraphs = paragraphs.slice(0, 3);
  const hasMore = paragraphs.length > 3;

  return (
    <div
      className="my-3 bg-white rounded-xl overflow-hidden"
      style={{ border: '1px solid #E5E2DC', cursor: 'pointer' }}
      onClick={handleOpen}
      data-testid="artifact-card"
    >
      <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: '1px solid #E5E2DC' }}>
        <FileText className="w-4 h-4 flex-shrink-0" style={{ color: '#8C8578' }} />
        <span className="text-sm font-semibold text-[#1A1A1A] flex-1 truncate">{artifact.title}</span>
        <div className="relative" ref={menuRef}>
          <button
            onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o); }}
            className="p-1 rounded-md transition-colors"
            style={{ color: '#8C8578' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
            data-testid="artifact-card-menu-btn"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg py-1 w-40 z-20" style={{ border: '1px solid #E5E2DC' }}>
              <button onClick={(e) => { e.stopPropagation(); handleCopy(); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-[#1A1A1A] hover:bg-[#F5F3EE] transition-colors text-left">
                <Copy className="w-3 h-3" /> Copy content
              </button>
              <button onClick={(e) => { e.stopPropagation(); handleDownload(); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-[#1A1A1A] hover:bg-[#F5F3EE] transition-colors text-left">
                <ExternalLink className="w-3 h-3" /> Download .txt
              </button>
              <button onClick={(e) => { e.stopPropagation(); handleOpen(); setMenuOpen(false); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-[#1A1A1A] hover:bg-[#F5F3EE] transition-colors text-left">
                <FileText className="w-3 h-3" /> Open document
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="px-4 py-3 leading-[1.6]" style={{ fontSize: 14, color: '#3D3A36' }}>
        {previewParagraphs.map((para, i) => {
          if (para.startsWith('# ')) return <h2 key={i} className="text-base font-semibold text-[#1A1A1A] mt-3 mb-1">{para.slice(2)}</h2>;
          if (para.startsWith('## ')) return <h3 key={i} className="text-sm font-semibold text-[#1A1A1A] mt-2 mb-1">{para.slice(3)}</h3>;
          if (para.startsWith('### ')) return <h4 key={i} className="text-sm font-medium text-[#1A1A1A] mt-2 mb-0.5">{para.slice(4)}</h4>;
          const parts = para.split(/(\*\*[^*]+\*\*)/).map((part, j) => {
            if (part.startsWith('**') && part.endsWith('**')) return <strong key={j} className="font-semibold text-[#1A1A1A]">{part.slice(2, -2)}</strong>;
            if (part.startsWith('- ') || part.startsWith('• ')) return <span key={j} className="block pl-4 before:content-['•'] before:mr-2" style={{ color: '#B5B0A8' }}>{part.slice(2)}</span>;
            return <span key={j}>{part}</span>;
          });
          return <p key={i} className="mb-2">{parts}</p>;
        })}
        {hasMore && (
          <span className="mt-1 font-medium" style={{ fontSize: 12, color: '#8C8578' }}>
            +{paragraphs.length - 3} more sections — click to open
          </span>
        )}
      </div>
      <div className="px-4 pb-3" style={{ fontSize: 12, color: '#8C8578' }}>
        Open document →
      </div>
    </div>
  );
}

function AgentActionCardView({ card }: { card: AgentActionCard }) {
  const isRunning = card.status === 'running';
  const isSuccess = card.status === 'success';
  const isError = card.status === 'error';

  return (
    <div
      data-testid={`agent-card-${card.id}`}
      className="flex items-start gap-2 my-1 px-3 py-2 rounded-lg"
      style={{
        background: isError ? '#FFF0EE' : isSuccess ? '#F0FAF5' : '#F5F3EE',
        border: `1px solid ${isError ? '#FFC9C2' : isSuccess ? '#C2E8D0' : '#E5E2DC'}`,
        fontSize: 12,
      }}
    >
      <div className="flex-shrink-0 mt-0.5">
        {isRunning && <Loader2 className="w-3 h-3 animate-spin" style={{ color: '#8C8578' }} />}
        {isSuccess && <CheckCircle2 className="w-3 h-3" style={{ color: '#22C55E' }} />}
        {isError && <XCircle className="w-3 h-3" style={{ color: '#EF4444' }} />}
      </div>
      <div className="flex-1 min-w-0">
        <span
          className="font-medium"
          style={{ color: isError ? '#C0392B' : isSuccess ? '#166534' : '#3D3A36' }}
        >
          {card.friendlyLabel}
        </span>
        {isSuccess && card.summary && (
          <p className="mt-0.5 truncate" style={{ color: '#6B7280', fontSize: 11 }}>
            {card.summary}
          </p>
        )}
        {isError && card.error && (
          <p className="mt-0.5 truncate" style={{ color: '#C0392B', fontSize: 11 }}>
            {card.error}
          </p>
        )}
      </div>
    </div>
  );
}

function ThinkingStepItem({ step }: { step: AgentTaskStep }) {
  const [detailOpen, setDetailOpen] = useState(false);
  const hasDetail = !!(step.detail || step.summary);
  const canExpand = step.status !== 'running' && hasDetail;

  return (
    <div data-testid={`thinking-step-${step.id}`}>
      <div
        className="flex items-start gap-2 py-1"
        style={{ cursor: canExpand ? 'pointer' : 'default' }}
        onClick={() => { if (canExpand) setDetailOpen((v) => !v); }}
      >
        <div className="flex-shrink-0 mt-0.5" style={{ width: 14 }}>
          {step.status === 'running' && (
            <Loader2 className="w-3 h-3 animate-spin" style={{ color: '#6B7FE6' }} />
          )}
          {step.status === 'success' && (
            <CheckCircle2 className="w-3 h-3" style={{ color: '#22C55E' }} />
          )}
          {step.status === 'error' && (
            <XCircle className="w-3 h-3" style={{ color: '#EF4444' }} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <span style={{ fontSize: 12, color: step.status === 'error' ? '#C0392B' : step.status === 'success' ? '#3D6B4F' : '#4B5563', fontWeight: step.status === 'running' ? 500 : 400 }}>
            {step.friendlyLabel}
          </span>
          {!detailOpen && step.status === 'success' && step.summary && (
            <p className="truncate" style={{ fontSize: 11, color: '#9CA3AF', marginTop: 1 }}>
              {step.summary}
            </p>
          )}
          {!detailOpen && step.status === 'error' && step.error && (
            <p className="truncate" style={{ fontSize: 11, color: '#C0392B', marginTop: 1 }}>
              {step.error}
            </p>
          )}
        </div>
        {canExpand && (
          <ChevronDown
            className="w-3 h-3 flex-shrink-0 mt-0.5 transition-transform"
            style={{ color: '#C0C0C0', transform: detailOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
        )}
      </div>
      {detailOpen && (
        <div
          className="mb-1 rounded-lg overflow-auto"
          style={{ background: '#F5F3EE', border: '1px solid #E5E2DC', padding: '8px 10px', marginLeft: 20 }}
        >
          {step.detail ? (
            <pre style={{ fontSize: 10.5, color: '#4B5563', lineHeight: 1.5, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 200, overflowY: 'auto' }}>
              {step.detail}
            </pre>
          ) : (
            <p style={{ fontSize: 11, color: '#6B7280', margin: 0 }}>{step.summary || step.error}</p>
          )}
        </div>
      )}
    </div>
  );
}

function QAStatusItem({ qa }: { qa: QAStatus }) {
  const isReviewing = qa.status === 'reviewing';
  const isApproved = qa.status === 'approved';
  const isRegenerating = qa.status === 'regenerating';
  const isSkipped = qa.status === 'skipped';

  return (
    <div data-testid={`qa-status-${qa.stepId}`} className="flex items-start gap-2 py-1">
      <div className="flex-shrink-0 mt-0.5" style={{ width: 14 }}>
        {isReviewing && <Loader2 className="w-3 h-3 animate-spin" style={{ color: '#8B5CF6' }} />}
        {isApproved && <CheckCircle2 className="w-3 h-3" style={{ color: '#22C55E' }} />}
        {isRegenerating && <Loader2 className="w-3 h-3 animate-spin" style={{ color: '#F59E0B' }} />}
        {isSkipped && <XCircle className="w-3 h-3" style={{ color: '#EF4444' }} />}
      </div>
      <div className="flex-1 min-w-0">
        <span
          style={{
            fontSize: 12,
            color: isApproved ? '#3D6B4F' : isSkipped ? '#C0392B' : isRegenerating ? '#92400E' : '#6B21A8',
            fontWeight: isReviewing || isRegenerating ? 500 : 400,
          }}
        >
          {qa.label}
        </span>
        {qa.issues && qa.issues.length > 0 && !isApproved && (
          <p className="truncate" style={{ fontSize: 11, color: '#9CA3AF', marginTop: 1 }}>
            {qa.issues[0]}
          </p>
        )}
      </div>
    </div>
  );
}

function ThinkingTaskGroup({ group, defaultExpanded }: { group: AgentTaskGroup; defaultExpanded?: boolean }) {
  const [expanded, setExpanded] = useState(defaultExpanded ?? group.status === 'active');
  const prevStatus = useRef(group.status);
  useEffect(() => {
    if (prevStatus.current !== group.status) {
      prevStatus.current = group.status;
      if (group.status === 'active') {
        setExpanded(true);
      } else if (group.status === 'completed' || group.status === 'failed') {
        setExpanded(false);
      }
    }
  }, [group.status]);
  const isActive = group.status === 'active';
  const isComplete = group.status === 'completed';
  const isFailed = group.status === 'failed';
  const successCount = group.steps.filter((s) => s.status === 'success').length;
  const totalSteps = group.steps.length;

  return (
    <div
      className="rounded-xl overflow-hidden"
      data-testid={`task-group-${group.id}`}
      style={{ border: '1px solid #E5E2DC', background: '#FAFAF8', marginBottom: 6 }}
    >
      <button
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
        style={{ background: 'transparent', cursor: 'pointer' }}
        onClick={() => setExpanded((v) => !v)}
        data-testid={`task-group-toggle-${group.id}`}
      >
        <div className="flex-shrink-0" style={{ width: 16 }}>
          {isActive && <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: '#6B7FE6' }} />}
          {isComplete && <CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#22C55E' }} />}
          {isFailed && <XCircle className="w-3.5 h-3.5" style={{ color: '#EF4444' }} />}
          {group.status === 'pending' && <Circle className="w-3.5 h-3.5" style={{ color: '#D1D5DB' }} />}
        </div>
        <span className="flex-1 font-medium" style={{ fontSize: 12.5, color: '#1A1A1A' }}>
          {isComplete && totalSteps > 0 ? `Completed ${successCount} step${successCount !== 1 ? 's' : ''}` : group.label}
        </span>
        {totalSteps > 0 && isActive && (
          <span style={{ fontSize: 11, color: '#9CA3AF', marginRight: 4 }}>
            {`${successCount}/${totalSteps}`}
          </span>
        )}
        <ChevronDown
          className="w-3.5 h-3.5 transition-transform"
          style={{ color: '#9CA3AF', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>
      {expanded && (group.steps.length > 0 || group.qaStatus) && (
        <div className="px-3 pb-2" style={{ borderTop: '1px solid #F0EDE8' }}>
          {group.steps.map((step) => (
            <ThinkingStepItem key={step.id} step={step} />
          ))}
          {group.qaStatus && <QAStatusItem qa={group.qaStatus} />}
        </div>
      )}
    </div>
  );
}

function ThinkingStepsView({ groups }: { groups: AgentTaskGroup[] }) {
  if (!groups || groups.length === 0) return null;
  return (
    <div className="my-2" data-testid="thinking-steps-view">
      {groups.map((group) => (
        <ThinkingTaskGroup
          key={group.id}
          group={group}
          defaultExpanded={group.status === 'active'}
        />
      ))}
    </div>
  );
}

function TaskProgressBarView({
  groups,
  isStreaming,
}: {
  groups: AgentTaskGroup[];
  isStreaming: boolean;
}) {
  const [visible, setVisible] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeGroup = groups.find((g) => g.status === 'active');
  const activeStep = activeGroup?.steps.find((s) => s.status === 'running');
  const completedGroups = groups.filter((g) => g.status === 'completed').length;
  const totalGroups = groups.length;

  useEffect(() => {
    if (groups.length > 0 && isStreaming) {
      setVisible(true);
      setFadeOut(false);
      if (timerRef.current) clearTimeout(timerRef.current);
    } else if (groups.length > 0 && !isStreaming) {
      timerRef.current = setTimeout(() => {
        setFadeOut(true);
        timerRef.current = setTimeout(() => { setVisible(false); setExpanded(false); }, 400);
      }, 3000);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [groups.length, isStreaming]);

  if (!visible || groups.length === 0) return null;

  const label = activeStep?.friendlyLabel || activeGroup?.label || (isStreaming ? 'Working…' : 'Done');

  return (
    <div
      className="rounded-xl mx-4 mb-2 overflow-hidden"
      style={{
        background: '#F5F3EE',
        border: '1px solid #E5E2DC',
        opacity: fadeOut ? 0 : 1,
        transition: 'opacity 0.4s ease',
      }}
      data-testid="task-progress-bar"
    >
      {/* Summary bar row */}
      <div className="flex items-center gap-2 px-3 py-2" style={{ fontSize: 12 }}>
        {isStreaming ? (
          <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" style={{ color: '#6B7FE6' }} />
        ) : (
          <CheckCircle2 className="w-3 h-3 flex-shrink-0" style={{ color: '#22C55E' }} />
        )}
        <span className="flex-1 truncate" style={{ color: '#3D3A36' }}>
          {label}
        </span>
        {totalGroups > 0 && (
          <span style={{ color: '#9CA3AF', flexShrink: 0, marginRight: 6 }}>
            {completedGroups}/{totalGroups}
          </span>
        )}
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center justify-center rounded p-0.5"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}
          data-testid="task-progress-bar-expand"
          title={expanded ? 'Collapse task list' : 'Expand task list'}
        >
          <ChevronDown
            className="w-3.5 h-3.5 transition-transform"
            style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
        </button>
      </div>
      {/* Expanded task list */}
      {expanded && (
        <div className="px-3 pb-2" style={{ borderTop: '1px solid #EAE7E2' }}>
          {groups.map((group) => (
            <div key={group.id} className="flex items-start gap-2 py-1" data-testid={`progress-group-${group.id}`}>
              <div className="flex-shrink-0 mt-0.5" style={{ width: 14 }}>
                {group.status === 'active' && <Loader2 className="w-3 h-3 animate-spin" style={{ color: '#6B7FE6' }} />}
                {group.status === 'completed' && <CheckCircle2 className="w-3 h-3" style={{ color: '#22C55E' }} />}
                {group.status === 'failed' && <XCircle className="w-3 h-3" style={{ color: '#EF4444' }} />}
                {group.status === 'pending' && <Circle className="w-3 h-3" style={{ color: '#D1D5DB' }} />}
              </div>
              <span style={{ fontSize: 12, color: '#3D3A36' }}>{group.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DocumentModal({ doc, onClose }: { doc: AgentDocument; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const generatedAt = useMemo(() => new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), []);
  const wordCount = useMemo(() => doc.content.split(/\s+/).filter(Boolean).length, [doc.content]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isFullscreen) setIsFullscreen(false);
        else onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, isFullscreen]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(doc.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch {}
  };

  const handleDownload = () => {
    const blob = new Blob([doc.content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(doc.title || 'report').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const panelStyle: React.CSSProperties = isFullscreen
    ? { width: '100vw', height: '100vh', borderRadius: 0, maxWidth: '100vw' }
    : { width: '72vw', maxWidth: 920, height: '84vh', borderRadius: 20 };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 500, background: 'rgba(0,0,0,0.12)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="document-modal-backdrop"
    >
      <div
        className="flex flex-col overflow-hidden shadow-xl"
        style={{ background: '#FAFAF8', border: '1px solid #E5E2DC', ...panelStyle, transition: 'width 0.2s, height 0.2s, border-radius 0.2s' }}
        data-testid="document-modal"
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-3.5" style={{ borderBottom: '1px solid #EAE7E0', flexShrink: 0 }}>
          <FileText className="w-4 h-4 flex-shrink-0" style={{ color: '#6B7FE6' }} />
          <div className="flex-1 min-w-0">
            <h2 style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A', margin: 0, lineHeight: 1.3 }}>{doc.title}</h2>
            <p style={{ fontSize: 10.5, color: '#A09A93', margin: 0, marginTop: 1 }}>
              Generated {generatedAt} · {wordCount.toLocaleString()} words
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={handleShare}
              className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5"
              style={{ background: '#F0EDE8', border: '1px solid #E5E2DC', cursor: 'pointer', fontSize: 12, color: '#6B6560' }}
              data-testid="document-modal-share"
              title="Copy link"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              {shareCopied ? 'Copied!' : 'Share'}
            </button>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5"
              style={{ background: '#F0EDE8', border: '1px solid #E5E2DC', cursor: 'pointer', fontSize: 12, color: '#6B6560' }}
              data-testid="document-modal-copy"
              title="Copy markdown"
            >
              {copied ? <CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#22C55E' }} /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button
              onClick={handleDownload}
              className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5"
              style={{ background: '#F0EDE8', border: '1px solid #E5E2DC', cursor: 'pointer', fontSize: 12, color: '#6B6560' }}
              data-testid="document-modal-download"
              title="Download as Markdown"
            >
              <Save className="w-3.5 h-3.5" />
              .md
            </button>
            <button
              onClick={() => setIsFullscreen((v) => !v)}
              className="rounded-lg p-1.5 flex items-center justify-center"
              style={{ background: '#F0EDE8', border: '1px solid #E5E2DC', cursor: 'pointer' }}
              data-testid="document-modal-fullscreen"
              title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen'}
            >
              <Monitor className="w-3.5 h-3.5" style={{ color: '#6B6560' }} />
            </button>
            <div style={{ width: 1, height: 20, background: '#E5E2DC', margin: '0 2px' }} />
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 flex items-center justify-center"
              style={{ background: '#F0EDE8', border: '1px solid #E5E2DC', cursor: 'pointer' }}
              data-testid="document-modal-close"
              title="Close (Esc)"
            >
              <X className="w-3.5 h-3.5" style={{ color: '#6B6560' }} />
            </button>
          </div>
        </div>
        {/* Body */}
        <div
          className="flex-1 overflow-y-auto"
          style={{ padding: '24px 32px', fontSize: 14, color: '#1A1A1A', lineHeight: '1.75' }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ children }) => <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 12, marginTop: 20, color: '#1A1A1A' }}>{children}</h1>,
              h2: ({ children }) => <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 10, marginTop: 18, color: '#1A1A1A' }}>{children}</h2>,
              h3: ({ children }) => <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, marginTop: 14, color: '#2D2A26' }}>{children}</h3>,
              p: ({ children }) => <p style={{ marginBottom: 12, lineHeight: '1.75' }}>{children}</p>,
              ul: ({ children }) => <ul style={{ paddingLeft: 20, marginBottom: 12 }}>{children}</ul>,
              ol: ({ children }) => <ol style={{ paddingLeft: 20, marginBottom: 12 }}>{children}</ol>,
              li: ({ children }) => <li style={{ marginBottom: 4 }}>{children}</li>,
              strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
              blockquote: ({ children }) => <blockquote style={{ borderLeft: '3px solid #E5E2DC', paddingLeft: 16, color: '#6B6560', marginLeft: 0, marginBottom: 12 }}>{children}</blockquote>,
              code: ({ children, className }) => {
                const isBlock = className?.startsWith('language-');
                return isBlock
                  ? <code style={{ display: 'block', background: '#F0EDE8', padding: '12px 16px', borderRadius: 8, fontSize: 13, overflowX: 'auto', marginBottom: 12 }}>{children}</code>
                  : <code style={{ background: '#F0EDE8', padding: '1px 5px', borderRadius: 4, fontSize: 12.5 }}>{children}</code>;
              },
            }}
          >
            {doc.content}
          </ReactMarkdown>
        </div>
        {/* Footer */}
        <div
          className="flex items-center justify-between px-5 py-2.5"
          style={{ borderTop: '1px solid #EAE7E0', flexShrink: 0, background: '#F5F3EE' }}
        >
          <span style={{ fontSize: 11, color: '#B0A99F' }}>AI-generated report · Review before sharing</span>
          <span style={{ fontSize: 11, color: '#B0A99F' }}>{wordCount.toLocaleString()} words</span>
        </div>
      </div>
    </div>
  );
}

function InlineDocumentCard({ doc }: { doc: AgentDocument }) {
  const [open, setOpen] = useState(false);
  const lines = doc.summary.split(/\n+/).filter(Boolean);
  const preview = lines.slice(0, 3).join(' ');

  return (
    <>
      <div
        className="rounded-xl overflow-hidden cursor-pointer my-2"
        style={{ border: '1px solid #E5E2DC', background: '#FAFAF8', transition: 'box-shadow 0.2s' }}
        onClick={() => setOpen(true)}
        data-testid="inline-document-card"
      >
        <div className="px-4 pt-4 pb-3">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 flex-shrink-0" style={{ color: '#6B7FE6' }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A' }}>{doc.title}</span>
          </div>
          <p
            style={{
              fontSize: 12.5,
              color: '#6B6560',
              lineHeight: '1.6',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              margin: 0,
            }}
          >
            {preview}
          </p>
        </div>
        <div className="px-4 pb-3 flex items-center gap-1" style={{ borderTop: '1px solid #F0EDE8', paddingTop: 10 }}>
          <span style={{ fontSize: 12, color: '#6B7FE6', fontWeight: 500 }}>View full report</span>
          <ExternalLink className="w-3 h-3" style={{ color: '#6B7FE6' }} />
        </div>
      </div>
      {open && <DocumentModal doc={doc} onClose={() => setOpen(false)} />}
    </>
  );
}

interface KpiCardsData {
  spend?: number;
  roas?: number;
  ctr?: number;
  cpc?: number;
  impressions?: number;
  clicks?: number;
  purchases?: number;
  revenue?: number;
  frequency?: number;
}

function ChatKpiCards({ data }: { data: KpiCardsData }) {
  const metrics: Array<{ key: keyof KpiCardsData; label: string; format: (v: number) => string }> = [
    { key: 'spend', label: 'Spend', format: (v) => `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
    { key: 'revenue', label: 'Revenue', format: (v) => `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
    { key: 'roas', label: 'ROAS', format: (v) => `${v.toFixed(2)}x` },
    { key: 'ctr', label: 'CTR', format: (v) => `${v.toFixed(2)}%` },
    { key: 'cpc', label: 'CPC', format: (v) => `$${v.toFixed(2)}` },
    { key: 'impressions', label: 'Impressions', format: (v) => v.toLocaleString() },
    { key: 'clicks', label: 'Clicks', format: (v) => v.toLocaleString() },
    { key: 'purchases', label: 'Purchases', format: (v) => v.toLocaleString() },
    { key: 'frequency', label: 'Frequency', format: (v) => v.toFixed(2) },
  ];

  const visible = metrics.filter(({ key }) => data[key] !== undefined && data[key] !== null && data[key] !== 0);
  if (visible.length === 0) return null;

  return (
    <div
      className="my-2 flex flex-wrap gap-2"
      data-testid="chat-kpi-cards"
    >
      {visible.map(({ key, label, format }) => (
        <div
          key={key}
          className="flex flex-col rounded-lg px-3 py-2 flex-shrink-0"
          style={{ background: '#F5F3EE', border: '1px solid #E5E2DC', minWidth: 72 }}
          data-testid={`chat-kpi-card-${key}`}
        >
          <span style={{ fontSize: 9, color: '#B5B0A8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
            {label}
          </span>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#1A1A1A', marginTop: 2 }}>
            {format(data[key] as number)}
          </span>
        </div>
      ))}
    </div>
  );
}

type ContentToken =
  | { kind: 'text'; value: string }
  | { kind: 'chart'; chart: CanvasInlineChart }
  | { kind: 'ad'; name: string }
  | { kind: 'ad_view'; adId: string; adName: string }
  | { kind: 'artifact'; artifact: ChatArtifact }
  | { kind: 'kpi_cards'; data: KpiCardsData };

function stripCommandTags(text: string): string {
  return text
    .replace(/\[SIDEBAR_OPEN:\s*\{[\s\S]*?\}\s*\]/g, "")
    .replace(/\[GENERATE_IMAGE:\s*\{[\s\S]*?\}\s*\]/g, "")
    .replace(/\[EDIT_IMAGE:\s*\{[\s\S]*?\}\s*\]/g, "")
    .trim();
}

function tokenizeContent(text: string): ContentToken[] {
  const safeText = stripCommandTags(text);
  const tokens: ContentToken[] = [];
  const tagRegex = /(\[KPI_CARDS:\s*(\{[\s\S]*?\})\s*\]|\[CHART:\s*(\{[\s\S]*?\})\s*\]|\[AD:\s*([^\]]+)\]|\[ARTIFACT:\s*(\{[\s\S]*?\})\s*\]|\[ad_view:([^\]]+)\]|\[SIDEBAR_OPEN:[^\]]*\])/g;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = tagRegex.exec(safeText)) !== null) {
    if (match.index > last) {
      tokens.push({ kind: 'text', value: safeText.slice(last, match.index) });
    }
    if (match[2]) {
      try {
        const data = JSON.parse(match[2]) as KpiCardsData;
        tokens.push({ kind: 'kpi_cards', data });
      } catch { /* skip malformed */ }
    } else if (match[3]) {
      try {
        const chart = JSON.parse(match[3]) as CanvasInlineChart;
        tokens.push({ kind: 'chart', chart });
      } catch { /* skip malformed */ }
    } else if (match[4]) {
      tokens.push({ kind: 'ad', name: match[4].trim() });
    } else if (match[5]) {
      try {
        const artifact = JSON.parse(match[5]) as ChatArtifact;
        tokens.push({ kind: 'artifact', artifact });
      } catch { /* skip malformed */ }
    } else if (match[6]) {
      const params = match[6].trim();
      const idMatch = params.match(/id=([^,\]]+)/);
      const nameMatch = params.match(/name=([^\]]+)/);
      const adId = idMatch ? idMatch[1].trim() : '';
      const adName = nameMatch ? nameMatch[1].trim() : params;
      tokens.push({ kind: 'ad_view', adId, adName });
    }
    // [SIDEBAR_OPEN:...] tags are stripped — they were already processed in the store
    last = match.index + match[0].length;
  }
  if (last < safeText.length) {
    tokens.push({ kind: 'text', value: safeText.slice(last) });
  }
  return tokens;
}

function renderTextWithSourcePills(
  text: string,
  sourceMap: Map<string, WebResearchSource>,
  keyPrefix: string
): React.ReactNode[] {
  const bracketRegex = /\[([^\]]+)\]/g;
  const parts: React.ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  let idx = 0;
  while ((match = bracketRegex.exec(text)) !== null) {
    if (match.index > last) {
      parts.push(text.slice(last, match.index));
    }
    const label = match[1];
    const source = sourceMap.get(label.toLowerCase());
    if (source) {
      parts.push(
        <a
          key={`${keyPrefix}-pill-${idx}`}
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`source-pill-${label}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 3,
            padding: '1px 6px',
            borderRadius: 20,
            background: '#F5F3EE',
            border: '1px solid #E5E2DC',
            fontSize: 11,
            color: '#6B6760',
            textDecoration: 'none',
            fontWeight: 500,
            verticalAlign: 'middle',
            lineHeight: 1.4,
            whiteSpace: 'nowrap',
            cursor: 'pointer',
          }}
        >
          {source.favicon && (
            <img
              src={source.favicon}
              alt=""
              style={{ width: 10, height: 10, borderRadius: 2, flexShrink: 0 }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          )}
          {label}
        </a>
      );
    } else {
      parts.push(
        <span
          key={`${keyPrefix}-ref-${idx}`}
          style={{ fontSize: 11, color: '#B5B0A8', verticalAlign: 'middle' }}
        >
          [{label}]
        </span>
      );
    }
    last = match.index + match[0].length;
    idx++;
  }
  if (last < text.length) {
    parts.push(text.slice(last));
  }
  return parts;
}

function processChildrenWithPills(
  children: React.ReactNode,
  sourceMap: Map<string, WebResearchSource>,
  keyPrefix: string
): React.ReactNode {
  if (!sourceMap.size) return children;
  return React.Children.map(children, (child, ci) => {
    if (typeof child === 'string') {
      const parts = renderTextWithSourcePills(child, sourceMap, `${keyPrefix}-${ci}`);
      return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : <>{parts}</>;
    }
    return child;
  });
}

function MarkdownText({ content, sources }: { content: string; sources?: WebResearchSource[] }) {
  const sourceMap = useMemo(() => {
    const m = new Map<string, WebResearchSource>();
    if (!sources) return m;
    for (const s of sources) {
      const domain = s.domain || (() => {
        try { return new URL(s.url).hostname.replace(/^www\./, ''); } catch { return ''; }
      })();
      if (domain) m.set(domain.toLowerCase(), s);
    }
    return m;
  }, [sources]);

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ href, children }) => <ChatMessageLink href={href} onOpenPlatform={openChatPlatformLink}>{children}</ChatMessageLink>,
        img: ({ src, alt }) => <ChatMessageLink href={typeof src === 'string' ? src : undefined} image onOpenPlatform={openChatPlatformLink}>{alt}</ChatMessageLink>,
        h1: ({ children }) => (
          <h1 style={{ fontSize: 18, fontWeight: 700, color: '#1A1A1A', margin: '12px 0 6px' }}>{children}</h1>
        ),
        h2: ({ children }) => (
          <h2 style={{ fontSize: 16, fontWeight: 700, color: '#1A1A1A', margin: '10px 0 5px' }}>{children}</h2>
        ),
        h3: ({ children }) => (
          <h3 style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A', margin: '8px 0 4px' }}>{children}</h3>
        ),
        strong: ({ children }) => (
          <strong style={{ fontWeight: 600, color: '#1A1A1A' }}>{children}</strong>
        ),
        em: ({ children }) => (
          <em style={{ fontStyle: 'italic' }}>{children}</em>
        ),
        blockquote: ({ children }) => (
          <blockquote style={{ borderLeft: '3px solid #D1CDC6', paddingLeft: 12, margin: '8px 0', color: '#8C8578' }}>{children}</blockquote>
        ),
        ul: ({ children }) => (
          <ul style={{ paddingLeft: 20, margin: '6px 0', listStyleType: 'disc' }}>{children}</ul>
        ),
        ol: ({ children }) => (
          <ol style={{ paddingLeft: 20, margin: '6px 0', listStyleType: 'decimal' }}>{children}</ol>
        ),
        li: ({ children }) => (
          <li style={{ margin: '2px 0', lineHeight: 1.6 }}>{children}</li>
        ),
        code: ({ children }) => (
          <code style={{ background: '#F5F3EE', border: '1px solid #E5E2DC', borderRadius: 4, padding: '1px 5px', fontSize: 12, fontFamily: 'monospace', color: '#3D3A36' }}>{children}</code>
        ),
        p: ({ children, node }) => {
          const hastNode = node as HastElement | undefined;
          const astChildren = hastNode?.children ?? [];
          const isStandaloneHeadline =
            astChildren.length === 1 &&
            astChildren[0]?.type === 'element' &&
            (astChildren[0] as HastElement).tagName === 'strong';
          if (isStandaloneHeadline) {
            return (
              <p style={{ margin: '24px 0 8px', lineHeight: 1.7 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#1A1A1A' }}>
                  {processChildrenWithPills(children, sourceMap, 'p-headline')}
                </span>
              </p>
            );
          }
          return (
            <p style={{ margin: '0 0 16px', lineHeight: 1.7 }}>
              {processChildrenWithPills(children, sourceMap, 'p')}
            </p>
          );
        },
        table: ({ children }) => (
          <div style={{ overflowX: 'auto', margin: '8px 0' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>{children}</table>
          </div>
        ),
        thead: ({ children }) => (
          <thead style={{ background: '#F5F3EE' }}>{children}</thead>
        ),
        tbody: ({ children }) => (
          <tbody>{children}</tbody>
        ),
        tr: ({ children }) => (
          <tr style={{ borderBottom: '1px solid #E5E2DC' }}>{children}</tr>
        ),
        th: ({ children }) => (
          <th style={{ padding: '6px 10px', fontWeight: 600, color: '#1A1A1A', textAlign: 'left', border: '1px solid #E5E2DC' }}>{children}</th>
        ),
        td: ({ children }) => (
          <td style={{ padding: '5px 10px', color: '#3D3A36', border: '1px solid #E5E2DC' }}>{children}</td>
        ),
      }}
    >
      {normalizeChatLinks(content)}
    </ReactMarkdown>
  );
}

function AdViewCard({
  adId,
  adName,
  adData,
  onOpenSidebar,
}: {
  adId: string;
  adName: string;
  adData?: AdViewData;
  onOpenSidebar: (content: SidebarPanelContent) => void;
}) {
  const handleClick = () => {
    onOpenSidebar({
      type: 'ad_view',
      title: adData?.name || adName,
      ad: adData || {
        id: adId,
        name: adName,
      },
    });
  };

  return (
    <button
      data-testid={`ad-view-card-${adId}`}
      onClick={handleClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        verticalAlign: 'middle',
        margin: '2px 4px',
        padding: '4px 10px 4px 6px',
        borderRadius: 8,
        border: '1px solid #E5E2DC',
        background: '#FAFAF9',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'background 0.15s, border-color 0.15s',
        maxWidth: 300,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = '#F0EDE8';
        (e.currentTarget as HTMLButtonElement).style.borderColor = '#C8C4BC';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = '#FAFAF9';
        (e.currentTarget as HTMLButtonElement).style.borderColor = '#E5E2DC';
      }}
    >
      {adData?.thumbnailUrl || adData?.creativeUrl ? (
        <img
          src={adData.thumbnailUrl || adData.creativeUrl}
          alt={adName}
          style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      ) : (
        <div style={{ width: 28, height: 28, borderRadius: 4, background: '#E5E2DC', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Monitor style={{ width: 12, height: 12, color: '#8C8578' }} />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#1A1A1A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {adData?.name || adName}
        </div>
        {adData?.campaignName && (
          <div style={{ fontSize: 10, color: '#8C8578', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {adData.campaignName}
          </div>
        )}
        {adData && (adData.roas !== undefined || adData.ctr !== undefined || adData.spend !== undefined) && (
          <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
            {adData.roas !== undefined && (
              <span style={{ fontSize: 9, color: '#6B7280', background: '#F5F3EE', padding: '1px 5px', borderRadius: 4 }}>
                ROAS {adData.roas.toFixed(2)}x
              </span>
            )}
            {adData.ctr !== undefined && (
              <span style={{ fontSize: 9, color: '#6B7280', background: '#F5F3EE', padding: '1px 5px', borderRadius: 4 }}>
                CTR {adData.ctr.toFixed(2)}%
              </span>
            )}
            {adData.spend !== undefined && (
              <span style={{ fontSize: 9, color: '#6B7280', background: '#F5F3EE', padding: '1px 5px', borderRadius: 4 }}>
                ${adData.spend.toLocaleString()}
              </span>
            )}
          </div>
        )}
      </div>
      <span style={{ fontSize: 10, color: '#8C8578', flexShrink: 0, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 2 }}>
        View details <ExternalLink style={{ width: 9, height: 9 }} />
      </span>
    </button>
  );
}

function AssistantMessageContent({
  content,
  adAccountId,
  sources,
  adDataCache,
  onOpenSidebar,
}: {
  content: string;
  adAccountId?: number;
  sources?: WebResearchSource[];
  adDataCache?: Map<string, AdViewData>;
  onOpenSidebar?: (content: SidebarPanelContent) => void;
}) {
  const tokens = tokenizeContent(content);
  return (
    <>
      {tokens.map((token, i) => {
        if (token.kind === 'text') return <MarkdownText key={i} content={token.value} sources={sources} />;
        if (token.kind === 'chart') return <InlineChartView key={i} chart={token.chart} />;
        if (token.kind === 'artifact') return <ArtifactCard key={i} artifact={token.artifact} />;
        if (token.kind === 'kpi_cards') return <ChatKpiCards key={i} data={token.data} />;
        if (token.kind === 'ad_view') {
          const adData = adDataCache?.get(token.adId) || adDataCache?.get(token.adName);
          return (
            <AdViewCard
              key={i}
              adId={token.adId}
              adName={token.adName}
              adData={adData}
              onOpenSidebar={onOpenSidebar || (() => {})}
            />
          );
        }
        return <AdRefChip key={i} name={token.name} adAccountId={adAccountId} />;
      })}
    </>
  );
}

// v7.35 — VIDEO composer dials (Dan's spec, the repeatable format for every
// video model we add): Model · Standard/Turbo · Ratio · Resolution · Time ·
// Audio · Video Ref. Options mirror the server registry
// (server/services/videoModel.ts) and the seeded pricing rows; credit hints
// are display-only. Self-contained: reads/writes the store directly so both
// composer sites render it with zero prop threading.
type VideoFamilyValue = 'seedance-2.5' | 'minimax-h3';
const VIDEO_MODEL_OPTIONS: Array<{ value: VideoFamilyValue; label: string; desc: string }> = [
  { value: 'seedance-2.5', label: 'Seedance 2.5', desc: 'ByteDance — top quality, up to 4K/30s' },
  { value: 'minimax-h3', label: 'MiniMax H3', desc: 'Cheapest — native audio, up to 768p/15s' },
];
// v7.36 — every dial's options are FAMILY-keyed (mirror of the server
// registry server/services/videoModel.ts; credit hints mirror the seeded
// pricing rows and are display-only).
const VIDEO_VARIANT_OPTIONS: Record<VideoFamilyValue, Array<{ value: string; label: string; desc: string }>> = {
  'seedance-2.5': [
    { value: 'standard', label: 'Standard', desc: 'Best quality · up to 4K' },
    { value: 'turbo', label: 'Turbo', desc: 'Faster · 720p/1080p' },
  ],
  'minimax-h3': [
    { value: 'standard', label: 'Standard', desc: 'One tier' },
  ],
};
const VIDEO_RATIO_OPTIONS: Record<VideoFamilyValue, Array<{ value: string; label: string; desc: string }>> = {
  'seedance-2.5': [
    { value: '9:16', label: '9:16', desc: 'Vertical (Reels/TikTok)' },
    { value: '16:9', label: '16:9', desc: 'Landscape' },
    { value: '1:1', label: '1:1', desc: 'Square' },
    { value: '4:3', label: '4:3', desc: 'Classic' },
    { value: '3:4', label: '3:4', desc: 'Portrait' },
    { value: '21:9', label: '21:9', desc: 'Cinematic' },
  ],
  'minimax-h3': [
    { value: '9:16', label: '9:16', desc: 'Vertical (Reels/TikTok)' },
    { value: '16:9', label: '16:9', desc: 'Landscape' },
    { value: '1:1', label: '1:1', desc: 'Square' },
    { value: '4:3', label: '4:3', desc: 'Classic' },
    { value: '3:4', label: '3:4', desc: 'Portrait' },
    { value: '21:9', label: '21:9', desc: 'Cinematic' },
    { value: '9:21', label: '9:21', desc: 'Tall cinematic' },
  ],
};
const VIDEO_RESOLUTION_OPTIONS: Record<string, Array<{ value: string; label: string; desc: string }>> = {
  'seedance-2.5:standard': [
    { value: '480p', label: '480p', desc: '~26 cr/s' },
    { value: '720p', label: '720p', desc: '~53 cr/s' },
    { value: '1080p', label: '1080p', desc: '~131 cr/s' },
    { value: '4k', label: '4K', desc: '~263 cr/s' },
  ],
  'seedance-2.5:turbo': [
    { value: '720p', label: '720p', desc: '~29 cr/s' },
    { value: '1080p', label: '1080p', desc: '~31 cr/s' },
  ],
  'minimax-h3:standard': [
    { value: '480p', label: '480p', desc: '~6 cr/s' },
    { value: '768p', label: '768p', desc: '~12 cr/s' },
  ],
};
const VIDEO_DURATION_OPTIONS: Record<VideoFamilyValue, Array<{ value: string; label: string; desc: string }>> = {
  'seedance-2.5': [
    { value: '5', label: '5s', desc: '' },
    { value: '10', label: '10s', desc: '' },
    { value: '15', label: '15s', desc: '' },
    { value: '20', label: '20s', desc: '' },
    { value: '30', label: '30s', desc: '' },
  ],
  'minimax-h3': [
    { value: '3', label: '3s', desc: '' },
    { value: '5', label: '5s', desc: '' },
    { value: '10', label: '10s', desc: '' },
    { value: '15', label: '15s', desc: '' },
  ],
};
const VIDEO_FAMILY_DEFAULT_RESOLUTION: Record<VideoFamilyValue, string> = {
  'seedance-2.5': '720p',
  'minimax-h3': '480p',
};
const VIDEO_FAMILY_AUDIO_TOGGLE: Record<VideoFamilyValue, boolean> = {
  'seedance-2.5': true,
  'minimax-h3': false, // native audio, steered by the prompt — no toggle in the API
};
const VIDEO_FAMILY_MAX_REF_VIDEOS: Record<VideoFamilyValue, number> = {
  'seedance-2.5': 10,
  'minimax-h3': 3,
};
const VIDEO_AUDIO_OPTIONS = [
  { value: 'on', label: 'Audio', desc: 'Generate synced audio' },
  { value: 'off', label: 'Muted', desc: 'No audio track' },
];

function VideoDialChip({ dial, title, display, options, selected, openDial, setOpenDial, onPick }: {
  dial: string;
  title: string;
  display: string;
  options: Array<{ value: string; label: string; desc: string }>;
  selected: string;
  openDial: string | null;
  setOpenDial: (d: string | null) => void;
  onPick: (v: string) => void;
}) {
  const open = openDial === dial;
  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <button
        data-testid={`video-${dial}-selector-btn`}
        onClick={() => setOpenDial(open ? null : dial)}
        className="flex items-center gap-1 transition-colors"
        style={{ height: 28, padding: '0 8px', borderRadius: 8, background: open ? '#E5E2DC' : '#F5F3EE', border: '1px solid #E5E2DC', fontSize: 11, fontWeight: 600, color: '#3D3A36', cursor: 'pointer' }}
        title={title}
      >
        {display}
        <ChevronDown className="w-2.5 h-2.5" style={{ color: '#8C8578' }} />
      </button>
      {open && (
        <div style={{ position: 'absolute', bottom: '100%', right: 0, marginBottom: 6, background: '#FFFFFF', border: '1px solid #E5E2DC', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 190, zIndex: 100, overflow: 'hidden' }}>
          {options.map((opt) => (
            <button
              key={opt.value}
              data-testid={`video-${dial}-option-${opt.value.replace(':', '-')}`}
              onClick={() => { onPick(opt.value); setOpenDial(null); }}
              className="flex items-center gap-2.5 w-full transition-colors"
              style={{ padding: '9px 14px', fontSize: 12, color: '#1A1A1A', background: selected === opt.value ? '#F5F3EE' : 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = selected === opt.value ? '#F5F3EE' : 'transparent'; }}
            >
              <div style={{ width: 14, height: 14, borderRadius: '50%', border: `1.5px solid ${selected === opt.value ? '#1A1A1A' : '#C5C2BC'}`, background: 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {selected === opt.value && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#1A1A1A' }} />}
              </div>
              <div>
                <span style={{ fontWeight: 600, marginRight: 6 }}>{opt.label}</span>
                {opt.desc && <span style={{ color: '#8C8578' }}>{opt.desc}</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function VideoComposerDials() {
  const {
    videoModel, videoVariant, videoResolution, videoDuration, videoAudio, videoAspectRatio,
    videoDialsByProject, setVideoModel, setVideoVariant, setVideoResolution, setVideoDuration,
    setVideoAudio, setVideoAspectRatio, setProjectVideoDials,
    videoRefs, addVideoRef, removeVideoRef,
  } = useCanvasStore();
  const projectId = useActiveProjectId();
  const [openDial, setOpenDial] = useState<string | null>(null);
  const [uploadingRef, setUploadingRef] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (rowRef.current && !rowRef.current.contains(e.target as Node)) setOpenDial(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  // Per-task stamp wins for display; picking writes the stamp AND the global
  // (same convention as the image model picker).
  const dials = projectId != null ? videoDialsByProject[projectId] : undefined;
  const family: VideoFamilyValue = dials?.model ?? videoModel;
  const variant = dials?.variant ?? videoVariant;
  const resolution = dials?.resolution ?? videoResolution;
  const duration = dials?.duration ?? videoDuration;
  const audio = dials?.audio ?? videoAudio;
  const ratio = dials?.aspectRatio ?? videoAspectRatio;
  const write = (patch: Partial<{ model: VideoFamilyValue; variant: 'standard' | 'turbo'; resolution: string; duration: number; audio: boolean; aspectRatio: string }>) => {
    const merged = { model: family, variant, resolution, duration, audio, aspectRatio: ratio, ...patch };
    // v7.36 — a family switch resets dials to that family's defaults (ratio
    // kept when the new family supports it); within a family, clamp any value
    // its variants can't render so the chips never show what the server would
    // silently rewrite.
    if (patch.model && patch.model !== family) {
      merged.variant = 'standard';
      merged.resolution = VIDEO_FAMILY_DEFAULT_RESOLUTION[patch.model];
      merged.duration = 5;
      merged.audio = true;
      if (!VIDEO_RATIO_OPTIONS[patch.model].some((o) => o.value === merged.aspectRatio)) merged.aspectRatio = '9:16';
    }
    const resOpts = VIDEO_RESOLUTION_OPTIONS[`${merged.model}:${merged.variant}`] ?? VIDEO_RESOLUTION_OPTIONS[`${merged.model}:standard`];
    if (!resOpts.some((o) => o.value === merged.resolution)) merged.resolution = resOpts[0].value;
    const durOpts = VIDEO_DURATION_OPTIONS[merged.model];
    if (!durOpts.some((o) => Number(o.value) === merged.duration)) merged.duration = 5;
    if (projectId != null) setProjectVideoDials(projectId, merged);
    setVideoModel(merged.model);
    setVideoVariant(merged.variant);
    setVideoResolution(merged.resolution);
    setVideoDuration(merged.duration);
    setVideoAudio(merged.audio);
    setVideoAspectRatio(merged.aspectRatio);
  };

  const uploadRef = async (file: File) => {
    setUploadingRef(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/canvas/upload-video-ref', { method: 'POST', body: fd });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Upload failed (${res.status})`);
      }
      const data = await res.json();
      addVideoRef({ url: data.url, durationSec: data.durationSec ?? undefined, name: file.name });
    } catch (e) {
      console.warn('[video-ref] upload failed:', e);
    } finally {
      setUploadingRef(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const resOptions = VIDEO_RESOLUTION_OPTIONS[`${family}:${variant}`] ?? VIDEO_RESOLUTION_OPTIONS[`${family}:standard`];
  const variantOptions = VIDEO_VARIANT_OPTIONS[family];
  const familyLabel = VIDEO_MODEL_OPTIONS.find((o) => o.value === family)?.label ?? family;
  const maxRefVideos = VIDEO_FAMILY_MAX_REF_VIDEOS[family];
  return (
    <div ref={rowRef} className="flex items-center gap-1.5" style={{ flexShrink: 1, minWidth: 0, flexWrap: 'wrap' }}>
      <div className="composer-toolbar-group">
      <VideoDialChip dial="model" title="Video model" display={familyLabel} options={VIDEO_MODEL_OPTIONS} selected={family} openDial={openDial} setOpenDial={setOpenDial} onPick={(v) => write({ model: v as VideoFamilyValue })} />
      {variantOptions.length > 1 && (
        <VideoDialChip dial="variant" title="Speed / quality" display={variant === 'turbo' ? 'Turbo' : 'Standard'} options={variantOptions} selected={variant} openDial={openDial} setOpenDial={setOpenDial} onPick={(v) => write({ variant: v as 'standard' | 'turbo' })} />
      )}
      </div>
      <div className="composer-toolbar-group">
      <VideoDialChip dial="ratio" title="Aspect ratio" display={ratio} options={VIDEO_RATIO_OPTIONS[family]} selected={ratio} openDial={openDial} setOpenDial={setOpenDial} onPick={(v) => write({ aspectRatio: v })} />
      <VideoDialChip dial="resolution" title="Resolution" display={resolution === '4k' ? '4K' : resolution} options={resOptions} selected={resolution} openDial={openDial} setOpenDial={setOpenDial} onPick={(v) => write({ resolution: v })} />
      <VideoDialChip dial="duration" title="Video length" display={`${duration}s`} options={VIDEO_DURATION_OPTIONS[family]} selected={String(duration)} openDial={openDial} setOpenDial={setOpenDial} onPick={(v) => write({ duration: Number(v) })} />
      </div>
      <div className="composer-toolbar-group">
      {VIDEO_FAMILY_AUDIO_TOGGLE[family] && (
        <VideoDialChip dial="audio" title="Audio" display={audio ? 'Audio' : 'Muted'} options={VIDEO_AUDIO_OPTIONS} selected={audio ? 'on' : 'off'} openDial={openDial} setOpenDial={setOpenDial} onPick={(v) => write({ audio: v === 'on' })} />
      )}
      <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/webm" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadRef(f); }} />
      <button
        data-testid="video-ref-attach-btn"
        onClick={() => fileRef.current?.click()}
        disabled={uploadingRef || videoRefs.length >= maxRefVideos}
        className="flex items-center gap-1 transition-colors"
        style={{ height: 28, padding: '0 8px', borderRadius: 8, background: '#F5F3EE', border: '1px solid #E5E2DC', fontSize: 11, fontWeight: 600, color: uploadingRef ? '#8C8578' : '#3D3A36', cursor: uploadingRef ? 'wait' : 'pointer', flexShrink: 0 }}
        title="Attach a reference video (≤30s) — guides motion and style"
      >
        <Clapperboard className="w-3 h-3" style={{ color: '#8C8578' }} />
        {uploadingRef ? 'Uploading…' : 'Video Ref'}
      </button>
      </div>
      {videoRefs.map((r) => (
        <div key={r.url} className="flex items-center gap-1" style={{ height: 28, padding: '0 6px 0 8px', borderRadius: 8, background: '#EDEAE3', border: '1px solid #E5E2DC', fontSize: 10, color: '#3D3A36', flexShrink: 0, maxWidth: 140 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name || 'ref video'}{typeof r.durationSec === 'number' ? ` · ${Math.round(r.durationSec)}s` : ''}</span>
          <button onClick={() => removeVideoRef(r.url)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#8C8578', fontSize: 12, lineHeight: 1, padding: 0 }} title="Remove">×</button>
        </div>
      ))}
    </div>
  );
}

const ASPECT_RATIO_OPTIONS = [
  { value: '1:1',  label: '1:1',  desc: 'Square',             thumbW: 140, thumbH: 140 },
  { value: '9:16', label: '9:16', desc: 'Vertical',            thumbW: 90,  thumbH: 160 },
  { value: '16:9', label: '16:9', desc: 'Landscape',           thumbW: 160, thumbH: 90  },
  { value: '4:5',  label: '4:5',  desc: 'Instagram Feed',      thumbW: 112, thumbH: 140 },
  { value: '3:4',  label: '3:4',  desc: 'Pinterest',           thumbW: 105, thumbH: 140 },
];

function getRatioDimensions(ratio: string): { w: number; h: number } {
  const opt = ASPECT_RATIO_OPTIONS.find((r) => r.value === ratio);
  return opt ? { w: opt.thumbW, h: opt.thumbH } : { w: 140, h: 140 };
}

// v7.16 — composer image-model dropdown (Dan's spec): pick which model renders
// images. Applies to image playbooks AND the video pipeline's image steps
// (opening frames, casting cards) — the video tab keeps the picker for that
// reason. Values are the server's family keys (server/services/imageModel.ts).
type ImageModelValue = 'nano-banana-2' | 'gpt-image-2';
const IMAGE_MODEL_OPTIONS: Array<{ value: ImageModelValue; label: string; desc: string }> = [
  { value: 'nano-banana-2', label: 'NanoBanana 2', desc: 'Google — fast, strong identity' },
  { value: 'gpt-image-2', label: 'GPT Image 2', desc: 'OpenAI — 4K, high quality' },
];

/** Shared footer dropdown for both composers (home + in-task); mirrors the
 *  aspect-ratio selector's button/popover styling exactly. */
function ImageModelPicker({ value, open, setOpen, btnRef, onPick }: {
  value: ImageModelValue;
  open: boolean;
  setOpen: (updater: (v: boolean) => boolean) => void;
  btnRef: React.RefObject<HTMLDivElement | null>;
  onPick: (m: ImageModelValue) => void;
}) {
  const current = IMAGE_MODEL_OPTIONS.find((o) => o.value === value) ?? IMAGE_MODEL_OPTIONS[0];
  return (
    <div ref={btnRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        data-testid="image-model-selector-btn"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 transition-colors"
        style={{
          height: 28,
          padding: '0 8px',
          borderRadius: 8,
          background: open ? '#E5E2DC' : '#F5F3EE',
          border: '1px solid #E5E2DC',
          fontSize: 11,
          fontWeight: 600,
          color: '#3D3A36',
          cursor: 'pointer',
        }}
        title="Image model"
      >
        {current.label}
        <ChevronDown className="w-2.5 h-2.5" style={{ color: '#8C8578' }} />
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            right: 0,
            marginBottom: 6,
            background: '#FFFFFF',
            border: '1px solid #E5E2DC',
            borderRadius: 10,
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
            minWidth: 230,
            zIndex: 100,
            overflow: 'hidden',
          }}
        >
          {IMAGE_MODEL_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              data-testid={`image-model-option-${opt.value}`}
              onClick={() => { onPick(opt.value); setOpen(() => false); }}
              className="flex items-center gap-2.5 w-full transition-colors"
              style={{
                padding: '9px 14px',
                fontSize: 12,
                color: '#1A1A1A',
                background: value === opt.value ? '#F5F3EE' : 'transparent',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = value === opt.value ? '#F5F3EE' : 'transparent'; }}
            >
              <div
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  border: `1.5px solid ${value === opt.value ? '#1A1A1A' : '#C5C2BC'}`,
                  background: 'transparent',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {value === opt.value && (
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#1A1A1A' }} />
                )}
              </div>
              <div>
                <span style={{ fontWeight: 600, marginRight: 6 }}>{opt.label}</span>
                <span style={{ color: '#8C8578' }}>{opt.desc}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// v7.20 — the model's SECOND dial (Dan's spec): GPT gets a quality selector
// (low/med/high — resolution stays 4K), nano gets a resolution selector
// (1k/2k/4k — its vendor cost is resolution-keyed). Credit hints mirror the
// seeded pricing rows (real cost x 2.967 markup at the Starter rate); they're
// display-only — billing always resolves from the live model_pricing row.
const IMAGE_TIER_OPTIONS: Record<ImageModelValue, Array<{ value: string; label: string; desc: string }>> = {
  'gpt-image-2': [
    { value: 'high', label: 'High', desc: '4K · ~240 cr/img' },
    { value: 'medium', label: 'Medium', desc: '4K · ~60 cr/img' },
    { value: 'low', label: 'Low', desc: '4K · ~10 cr/img' },
  ],
  'nano-banana-2': [
    { value: '1k', label: '1K', desc: '~23 cr/img' },
    { value: '2k', label: '2K', desc: '~35 cr/img' },
    { value: '4k', label: '4K', desc: '~47 cr/img' },
  ],
};

/** Quality/resolution dropdown rendered beside the model picker; same
 *  button/popover styling. A null tier displays the family default (first
 *  option). */
function ImageTierPicker({ family, value, open, setOpen, btnRef, onPick }: {
  family: ImageModelValue;
  value: string | null;
  open: boolean;
  setOpen: (updater: (v: boolean) => boolean) => void;
  btnRef: React.RefObject<HTMLDivElement | null>;
  onPick: (t: string) => void;
}) {
  const options = IMAGE_TIER_OPTIONS[family];
  const current = options.find((o) => o.value === value) ?? options[0];
  return (
    <div ref={btnRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        data-testid="image-tier-selector-btn"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 transition-colors"
        style={{
          height: 28,
          padding: '0 8px',
          borderRadius: 8,
          background: open ? '#E5E2DC' : '#F5F3EE',
          border: '1px solid #E5E2DC',
          fontSize: 11,
          fontWeight: 600,
          color: '#3D3A36',
          cursor: 'pointer',
        }}
        title={family === 'gpt-image-2' ? 'Image quality' : 'Image resolution'}
      >
        {current.label}
        <ChevronDown className="w-2.5 h-2.5" style={{ color: '#8C8578' }} />
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            right: 0,
            marginBottom: 6,
            background: '#FFFFFF',
            border: '1px solid #E5E2DC',
            borderRadius: 10,
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
            minWidth: 190,
            zIndex: 100,
            overflow: 'hidden',
          }}
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              data-testid={`image-tier-option-${opt.value}`}
              onClick={() => { onPick(opt.value); setOpen(() => false); }}
              className="flex items-center gap-2.5 w-full transition-colors"
              style={{
                padding: '9px 14px',
                fontSize: 12,
                color: '#1A1A1A',
                background: current.value === opt.value ? '#F5F3EE' : 'transparent',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = current.value === opt.value ? '#F5F3EE' : 'transparent'; }}
            >
              <div
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  border: `1.5px solid ${current.value === opt.value ? '#1A1A1A' : '#C5C2BC'}`,
                  background: 'transparent',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {current.value === opt.value && (
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#1A1A1A' }} />
                )}
              </div>
              <div>
                <span style={{ fontWeight: 600, marginRight: 6 }}>{opt.label}</span>
                <span style={{ color: '#8C8578' }}>{opt.desc}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// v7.37 — composer "Advanced" angle/style filter (Dan's spec, image tab only).
// Default is All (exactly today's behavior); selecting a subset restricts the
// run to those angle × style combos. The panel is CLICK-TOGGLED and stays
// open (it is deliberately NOT in the outside-click closer) — it spans the
// input box's width, so it mounts under the footer row, not as a popover.
type AdFilterValue = { styles: string[] | null; angles: string[] | null };
const slug = (v: string) => v.toLowerCase().replace(/[^a-z0-9]+/g, '-');

function AdvancedButton({ open, filter, onToggle }: { open: boolean; filter: AdFilterValue; onToggle: () => void }) {
  // v7.38 — compact label (Dan: the long "1 angle · 1 style" wrapped the
  // send arrow onto a second line). Filtered → "Advanced · A · S" where A/S
  // are the angle/style counts, "All" for an axis left open; the wording
  // lives in the tooltip.
  const filtered = !!(filter.angles?.length || filter.styles?.length);
  const nA = filter.angles?.length ? String(filter.angles.length) : 'All';
  const nS = filter.styles?.length ? String(filter.styles.length) : 'All';
  const tooltip = filtered
    ? `${filter.angles?.length ?? 'All'} angle${filter.angles?.length === 1 ? '' : 's'} · ${filter.styles?.length ?? 'All'} style${filter.styles?.length === 1 ? '' : 's'} — click to edit`
    : 'Restrict this run to specific angles and styles';
  return (
    <button
      data-testid="composer-advanced-btn"
      onClick={onToggle}
      className="flex items-center gap-1 transition-colors"
      style={{
        height: 28,
        padding: '0 8px',
        borderRadius: 8,
        background: open || filtered ? '#E5E2DC' : '#F5F3EE',
        border: `1px solid ${filtered ? '#C5C2BC' : '#E5E2DC'}`,
        fontSize: 11,
        fontWeight: 600,
        color: '#3D3A36',
        cursor: 'pointer',
        flexShrink: 0,
      }}
      title={tooltip}
    >
      <SlidersHorizontal className="w-3 h-3" style={{ color: '#8C8578' }} />
      {filtered ? `Advanced · ${nA} · ${nS}` : 'Advanced'}
    </button>
  );
}


// Hoisted out of AdvancedPanel on purpose: an inline component type is
// recreated every render, which remounts every checkbox (kills focus and
// makes the DOM churn). Top-level = stable identity, in-place updates.
function AdvancedColumn({ axis, title, items, filter, onToggle, onAll }: {
axis: 'styles' | 'angles'; title: string; items: readonly string[];
filter: AdFilterValue; onToggle: (axis: 'styles' | 'angles', v: string) => void; onAll: (axis: 'styles' | 'angles') => void;
}) {
  const cur = filter[axis];
  const isAll = cur == null;
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8C8578' }}>{title}</span>
        <label className="flex items-center gap-1.5" style={{ fontSize: 11.5, color: '#3D3A36', cursor: 'pointer' }}>
          <input
            type="checkbox"
            data-testid={`adv-${axis}-all`}
            checked={isAll}
            onChange={() => onAll(axis)}
            style={{ accentColor: '#1A1A1A', width: 13, height: 13 }}
          />
          All
        </label>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '3px 10px' }}>
        {items.map((v) => {
          const checked = isAll ? false : cur!.includes(v);
          return (
            <label
              key={v}
              className="flex items-center gap-1.5"
              style={{ fontSize: 12, color: isAll ? '#8C8578' : checked ? '#1A1A1A' : '#5B5750', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '22px' }}
              title={v}
            >
              <input
                type="checkbox"
                data-testid={`adv-${axis === 'styles' ? 'style' : 'angle'}-${slug(v)}`}
                checked={checked}
                onChange={() => onToggle(axis, v)}
                style={{ accentColor: '#1A1A1A', width: 13, height: 13, flexShrink: 0 }}
              />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{v}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function AdvancedPanel({ filter, onChange }: { filter: AdFilterValue; onChange: (f: AdFilterValue) => void }) {
  // Toggle semantics: from All (null) checking one item → [item]; unchecking
  // the last item → All; a list equal to the full taxonomy → All.
  const toggle = (axis: 'styles' | 'angles', value: string) => {
    const full = axis === 'styles' ? AD_STYLES_18 : AD_ANGLES_18;
    const cur = filter[axis];
    let next: string[] | null;
    if (cur == null) next = [value];
    else if (cur.includes(value)) next = cur.filter((v) => v !== value);
    else next = full.filter((v) => v === value || cur.includes(v));
    if (next && (next.length === 0 || next.length >= full.length)) next = null;
    onChange({ ...filter, [axis]: next });
  };
  const setAll = (axis: 'styles' | 'angles') => onChange({ ...filter, [axis]: null });

  const nA = filter.angles?.length ?? AD_ANGLES_18.length;
  const nS = filter.styles?.length ?? AD_STYLES_18.length;
  const filtered = !!(filter.angles || filter.styles);
  return (
    <div
      data-testid="composer-advanced-panel"
      style={{ width: '100%', padding: 12, background: '#F5F3EE', border: '1px solid #E5E2DC', borderRadius: 10 }}
    >
      <div className="flex gap-4" style={{ alignItems: 'flex-start' }}>
        <AdvancedColumn axis="angles" title="Angles" items={AD_ANGLES_18} filter={filter} onToggle={toggle} onAll={setAll} />
        <div style={{ width: 1, alignSelf: 'stretch', background: '#E5E2DC' }} />
        <AdvancedColumn axis="styles" title="Styles" items={AD_STYLES_18} filter={filter} onToggle={toggle} onAll={setAll} />
      </div>
      <div className="flex items-center justify-between" style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #E5E2DC' }}>
        <span style={{ fontSize: 11.5, color: '#8C8578' }}>
          {filtered
            ? <>Ads will use <strong style={{ color: '#3D3A36' }}>{nA} angle{nA === 1 ? '' : 's'}</strong> × <strong style={{ color: '#3D3A36' }}>{nS} style{nS === 1 ? '' : 's'}</strong></>
            : 'All angles and styles — AdMoji picks the mix, as today'}
        </span>
        {filtered && (
          <button
            data-testid="adv-reset"
            onClick={() => onChange({ styles: null, angles: null })}
            style={{ background: 'none', border: 'none', color: '#8f2740', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', padding: 0 }}
          >
            Reset to All
          </button>
        )}
      </div>
    </div>
  );
}

function detectRatioFromPrompt(message: string): string | null {
  const m = message.toLowerCase();
  if (/\b(9[:\/]16|story|tiktok|portrait)\b/.test(m)) return '9:16';
  if (/\b(16[:\/]9|landscape|banner|horizontal|youtube|widescreen)\b/.test(m)) return '16:9';
  if (/\b(4[:\/]5|instagram feed|ig feed)\b/.test(m)) return '4:5';
  if (/\b(3[:\/]4|pinterest)\b/.test(m)) return '3:4';
  if (/\b(1[:\/]1|square)\b/.test(m)) return '1:1';
  return null;
}

function ChatImageSlotView({ slot, aspectRatio }: { slot: { index: number; resolved?: string; failed?: boolean; errorMsg?: string }; aspectRatio?: string }) {
  const images = useActiveProjectImages();
  const imageQaBadges = useActiveProjectQaBadges();
  const panToImage = useCanvasStore((s) => s.panToImage);
  const [showTooltip, setShowTooltip] = useState(false);
  // Natural aspect ratio captured when the image loads — overrides the
  // msg-level aspectRatio prop so each thumbnail matches the actual image
  // produced by WaveSpeed (concepts with different ratios display correctly).
  const [naturalRatio, setNaturalRatio] = useState<{ w: number; h: number } | null>(null);

  const handleClick = () => {
    if (!slot.resolved) return;
    // Prefer exact src match, then fall back to a lenient "same filename"
    // match — WaveSpeed URLs sometimes get proxied / CDN-rewritten and the
    // store's img.src may not be byte-identical to slot.resolved.
    const sameUrl = (a: string, b: string) => {
      if (a === b) return true;
      try {
        const filenameA = a.split("?")[0].split("/").pop();
        const filenameB = b.split("?")[0].split("/").pop();
        return !!filenameA && filenameA === filenameB;
      } catch {
        return false;
      }
    };
    const match = images.find((img) => sameUrl(img.src, slot.resolved!));
    if (match) {
      panToImage(match.id);
    } else {
      // Diagnostic — helps us see why the match failed without breaking the user flow
      console.warn("[ChatImageSlotView] No canvas image matched slot.resolved", {
        slotResolved: slot.resolved,
        storeImageSrcs: images.slice(0, 8).map((img) => img.src),
      });
    }
  };

  const fallback = getRatioDimensions(aspectRatio || '1:1');
  // Fixed "height basis" so thumbnails stay a consistent size in the strip while
  // different-ratio images render at their own width. Chose 96px height to match
  // prior square thumbnails — landscape expands wider, portrait narrower.
  const BASIS_H = 96;
  const thumbH = naturalRatio ? BASIS_H : fallback.h;
  const thumbW = naturalRatio
    ? Math.round((naturalRatio.w / naturalRatio.h) * BASIS_H)
    : fallback.w;

  const qaBadge = slot.resolved ? imageQaBadges[slot.resolved] : undefined;

  if (slot.failed) {
    return (
      <div className="rounded-xl bg-red-50 flex-shrink-0 border border-red-200 flex flex-col items-center justify-center gap-1" style={{ width: fallback.w, height: fallback.h }}>
        <XCircle className="w-6 h-6 text-red-400" />
        <span className="text-[10px] text-red-400 text-center px-2 leading-tight">Generation failed</span>
      </div>
    );
  }
  if (slot.resolved) {
    return (
      <div className="relative flex-shrink-0" style={{ width: thumbW, height: thumbH }}>
        <img
          src={slot.resolved}
          alt="Generated"
          onClick={handleClick}
          onLoad={(e) => {
            const el = e.currentTarget;
            if (el.naturalWidth > 0 && el.naturalHeight > 0) {
              setNaturalRatio({ w: el.naturalWidth, h: el.naturalHeight });
            }
          }}
          className="rounded-xl w-full h-full object-contain cursor-pointer hover:scale-[1.02] hover:shadow-md transition-all duration-200"
          style={{ border: '1px solid #E5E2DC', animation: 'fadeInImg 0.3s ease-out', background: '#fafaf8' }}
        />
        {qaBadge && (
          <div
            data-testid="chat-qa-badge"
            style={{
              position: 'absolute',
              top: 6,
              right: 6,
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: qaBadge.passed ? '#22c55e' : '#ef4444',
              border: '2px solid #fff',
              boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              color: '#fff',
              fontWeight: 700,
              cursor: 'pointer',
              zIndex: 5,
            }}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            {qaBadge.passed ? '✓' : '✗'}
            {showTooltip && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 24,
                  right: 0,
                  background: 'rgba(17,24,39,0.95)',
                  color: '#fff',
                  fontSize: 10,
                  lineHeight: 1.4,
                  padding: '5px 8px',
                  borderRadius: 5,
                  whiteSpace: 'pre-wrap',
                  maxWidth: 180,
                  zIndex: 20,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  pointerEvents: 'none',
                }}
              >
                {qaBadge.reason}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="rounded-xl flex-shrink-0 overflow-hidden relative" style={{ width: thumbW, height: thumbH, border: '1px solid #E5E2DC' }}>
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(90deg, #F5F3EE 25%, #E5E2DC 50%, #F5F3EE 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite linear',
        }}
      />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
        <ImageIcon className="w-6 h-6" style={{ color: '#B5B0A8' }} />
        <span className="font-medium" style={{ fontSize: 10, color: '#B5B0A8' }}>Generating…</span>
      </div>
    </div>
  );
}

function SubTaskIcon({ icon }: { icon: string }) {
  const cls = "w-3.5 h-3.5 flex-shrink-0";
  if (icon === 'search') return <Search className={cls} />;
  if (icon === 'save') return <Save className={cls} />;
  if (icon === 'generate') return <Image className={cls} />;
  if (icon === 'write') return <PenLine className={cls} />;
  if (icon === 'analyze') return <LineChart className={cls} />;
  return <Circle className={cls} />;
}

function InlineTaskSteps({ steps }: { steps: TaskStep[] }) {
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [visibleCount, setVisibleCount] = useState(0);
  const prevCountRef = useRef(0);

  useEffect(() => {
    if (steps.length > prevCountRef.current) {
      const newSteps = steps.length - prevCountRef.current;
      let revealed = 0;
      const revealNext = () => {
        if (revealed < newSteps) {
          revealed++;
          setVisibleCount(prevCountRef.current + revealed);
          setTimeout(revealNext, 200);
        } else {
          prevCountRef.current = steps.length;
        }
      };
      revealNext();
    } else {
      setVisibleCount(steps.length);
      prevCountRef.current = steps.length;
    }
  }, [steps.length]);

  const toggleStep = (stepId: string) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) next.delete(stepId);
      else next.add(stepId);
      return next;
    });
  };

  return (
    <div className="mt-3 overflow-hidden" style={{ border: '1px solid #E5E2DC', borderRadius: 12 }}>
      {steps.slice(0, visibleCount).map((step, index) => {
        const isExpanded = expandedSteps.has(step.id);
        const canExpand = !!(step.subTasks?.length || step.description);
        return (
          <div
            key={step.id}
            style={{
              borderBottom: '1px solid #E5E2DC',
              animation: 'fadeSlideInStep 0.35s ease both',
            }}
            className="last:border-b-0"
          >
            <button
              onClick={() => canExpand && toggleStep(step.id)}
              className={`w-full flex items-center gap-2 px-3 text-left transition-colors ${canExpand ? 'hover:bg-[#F5F3EE]' : 'cursor-default'}`}
              style={{ padding: '10px 12px' }}
            >
              {step.status === 'completed' ? (
                <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#22C55E' }} />
              ) : step.status === 'running' ? (
                <Loader2 className="w-3.5 h-3.5 flex-shrink-0 animate-spin" style={{ color: '#8C8578' }} />
              ) : step.status === 'failed' ? (
                <AlertCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
              ) : (
                <Circle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#D1CDC6' }} strokeWidth={1.5} />
              )}
              <span style={{
                fontSize: 14,
                flex: 1,
                color: step.status === 'failed' ? '#EF4444' : step.status === 'pending' ? '#B5B0A8' : '#1A1A1A',
                fontWeight: step.status === 'running' ? 500 : 400,
              }}>
                {step.label}
              </span>
              {canExpand && (
                <ChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} style={{ color: '#B5B0A8' }} />
              )}
            </button>
            {isExpanded && (
              <div className="pb-2 pl-8 pr-3 space-y-1">
                {step.description && (
                  <p style={{ fontSize: 11, color: '#8C8578', lineHeight: 1.6, padding: '4px 0' }}>{step.description}</p>
                )}
                {step.subTasks?.map((sub) => (
                  <div key={sub.id} className="flex items-center gap-2 rounded-lg px-3 py-1.5" style={{ background: '#F5F3EE' }}>
                    <SubTaskIcon icon={sub.icon} />
                    <span style={{ fontSize: 13, color: '#3D3A36' }} className="truncate flex-1">{sub.label}</span>
                    {sub.status === 'running' && <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" style={{ color: '#8C8578' }} />}
                    {sub.status === 'completed' && <CheckCircle2 className="w-3 h-3 flex-shrink-0" style={{ color: '#22C55E' }} />}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const WEB_RESEARCH_KEYFRAMES = `
@keyframes fadeSlideInStep {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes fadeSlideInSource {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}
`;

function WebResearchSources({
  sources,
  status,
  onOpenReport,
  hasReport,
}: {
  sources: WebResearchSource[];
  status?: 'searching' | 'analyzing' | 'complete';
  onOpenReport?: () => void;
  hasReport?: boolean;
}) {
  const successSources = sources.filter((s) => s.status !== 'failed');
  const failedCount = sources.filter((s) => s.status === 'failed').length;

  return (
    <div className="mb-3" data-testid="web-research-sources">
      <style>{WEB_RESEARCH_KEYFRAMES}</style>
      <div className="flex items-center gap-2 mb-2">
        {status === 'searching' ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" style={{ color: '#8C8578' }} />
        ) : status === 'complete' ? (
          <Globe className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#22C55E' }} />
        ) : (
          <Globe className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#8C8578' }} />
        )}
        <span style={{ fontSize: 12, color: '#8C8578', fontWeight: 500 }}>
          {status === 'searching' && 'Searching the web…'}
          {status === 'analyzing' && `Found ${successSources.length} source${successSources.length !== 1 ? 's' : ''}`}
          {status === 'complete' && `Found ${successSources.length} source${successSources.length !== 1 ? 's' : ''}${failedCount > 0 ? ` (${failedCount} failed)` : ''}`}
        </span>
      </div>

      {sources.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {sources.map((source, index) => (
            <a
              key={source.url || index}
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`web-research-source-card-${index}`}
              className="flex items-center gap-2 rounded-xl px-3 py-2 text-decoration-none transition-all hover:scale-[1.02]"
              style={{
                background: source.status === 'failed' ? '#FEF2F2' : '#F5F3EE',
                border: `1px solid ${source.status === 'failed' ? '#FECACA' : '#E5E2DC'}`,
                maxWidth: 220,
                minWidth: 140,
                cursor: source.status === 'failed' ? 'default' : 'pointer',
                textDecoration: 'none',
                animation: 'fadeSlideInSource 0.35s ease both',
                animationDelay: `${index * 0.1}s`,
              }}
              onClick={(e) => { if (source.status === 'failed') e.preventDefault(); }}
            >
              {source.favicon ? (
                <img
                  src={source.favicon}
                  alt=""
                  width={14}
                  height={14}
                  className="flex-shrink-0 rounded-sm"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                <Globe className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#B5B0A8' }} />
              )}
              <span
                className="truncate"
                style={{ fontSize: 12, color: source.status === 'failed' ? '#EF4444' : '#3D3A36', flex: 1 }}
                title={source.title || source.domain || source.url}
              >
                {source.title || source.domain || source.url}
              </span>
              {source.status === 'failed' ? (
                <AlertCircle className="w-3 h-3 flex-shrink-0 text-red-400" />
              ) : source.status === 'pending' ? (
                <Loader2 className="w-3 h-3 flex-shrink-0 animate-spin" style={{ color: '#B5B0A8' }} />
              ) : (
                <CheckCircle2 className="w-3 h-3 flex-shrink-0" style={{ color: '#22C55E' }} />
              )}
            </a>
          ))}
        </div>
      )}

      {status === 'analyzing' && sources.length > 0 && (
        <div className="flex items-center gap-2 mt-2" style={{ animation: 'fadeSlideInSource 0.35s ease both' }}>
          <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" style={{ color: '#6366F1' }} />
          <span style={{ fontSize: 12, color: '#6366F1', fontWeight: 500 }}>
            Analyzing {successSources.length} source{successSources.length !== 1 ? 's' : ''}…
          </span>
        </div>
      )}

      {status === 'complete' && hasReport && onOpenReport && (
        <button
          data-testid="open-full-report-btn"
          onClick={onOpenReport}
          className="mt-3 flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors hover:bg-[#EDE9E3]"
          style={{ background: '#F5F3EE', border: '1px solid #E5E2DC', color: '#3D3A36', fontSize: 13 }}
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Open Full Report
        </button>
      )}
    </div>
  );
}

function GenerationProgressBar({ slots }: { slots: Array<{ index: number; resolved?: string; failed?: boolean }> }) {
  const total = slots.length;
  if (total === 0) return null;
  const done = slots.filter((s) => s.resolved || s.failed).length;
  const pct = total > 0 ? (done / total) * 100 : 0;
  return (
    <div className="mt-2 space-y-1">
      <div className="flex items-center gap-2" style={{ fontSize: 12, color: '#8C8578' }}>
        <Image className="w-3.5 h-3.5" style={{ color: '#8C8578' }} />
        <span>Generating image {Math.min(done + 1, total)} of {total}…</span>
      </div>
      <div className="h-[3px] rounded-full overflow-hidden" style={{ background: '#E5E2DC' }}>
        {pct > 0 ? (
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: '#1A1A1A' }} />
        ) : (
          <div className="h-full rounded-full" style={{
            width: '30%',
            background: '#1A1A1A',
            animation: 'indeterminate 1.5s infinite ease-in-out',
          }} />
        )}
      </div>
    </div>
  );
}

function ChatSkeleton() {
  return (
    <div
      data-testid="chat-skeleton"
      className="flex flex-col h-full overflow-hidden"
      style={{ background: '#F8F8F7' }}
    >
    <div
      className="flex flex-col h-full overflow-hidden"
      style={{ maxWidth: 740, width: '100%', margin: '0 auto', padding: '20px 16px', gap: 24 }}
    >
        {/* User bubble — right aligned */}
        <div className="flex justify-end">
          <div
            style={{
              width: '55%',
              height: 36,
              borderRadius: 18,
              background: 'linear-gradient(90deg, #E5E7EB 25%, #F3F4F6 50%, #E5E7EB 75%)',
              backgroundSize: '200% 100%',
              animation: 'chatSkeletonShimmer 1.6s ease-in-out infinite',
            }}
          />
        </div>

        {/* AI row — avatar circle + response bar */}
        <div className="flex items-start gap-3">
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              flexShrink: 0,
              background: 'linear-gradient(90deg, #E5E7EB 25%, #F3F4F6 50%, #E5E7EB 75%)',
              backgroundSize: '200% 100%',
              animation: 'chatSkeletonShimmer 1.6s ease-in-out infinite',
            }}
          />
          <div
            style={{
              flex: 1,
              height: 20,
              borderRadius: 10,
              background: 'linear-gradient(90deg, #E5E7EB 25%, #F3F4F6 50%, #E5E7EB 75%)',
              backgroundSize: '200% 100%',
              animation: 'chatSkeletonShimmer 1.6s ease-in-out infinite',
            }}
          />
        </div>

        {/* Paragraph lines */}
        {[
          '92%',
          '78%',
          '85%',
          '62%',
        ].map((w, i) => (
          <div
            key={i}
            style={{
              width: w,
              height: 14,
              borderRadius: 7,
              background: 'linear-gradient(90deg, #E5E7EB 25%, #F3F4F6 50%, #E5E7EB 75%)',
              backgroundSize: '200% 100%',
              animation: `chatSkeletonShimmer 1.6s ease-in-out ${i * 0.1}s infinite`,
              marginLeft: 40,
            }}
          />
        ))}

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Muted input bar */}
        <div
          style={{
            height: 44,
            borderRadius: 22,
            background: 'linear-gradient(90deg, #E5E7EB 25%, #F3F4F6 50%, #E5E7EB 75%)',
            backgroundSize: '200% 100%',
            animation: 'chatSkeletonShimmer 1.6s ease-in-out infinite',
            flexShrink: 0,
          }}
        />
    </div>
    </div>
  );
}

export function CanvasChat({ fullWidth }: { fullWidth?: boolean }) {
  const {
    typingByProject, sendMessage,
    activeBrandId,
    setActiveBrandId,
    activeAdAccountId,
    attachedFile, setAttachedFile,
    chatAttachments, removeChatAttachment, setChatAttachments, addChatAttachment,
    sseConnected, sseError,
    anglePickerStages,
    folderProjectId,
    setFolderProjectId,
    openDocumentOverlay,
    dismissResearchTask,
    showDocumentOverlay,
    documentOverlayContent,
    documentOverlayTitle,
    brandSelectionNeeded,
    clearBrandSelection,
    sendResearchMessage,
    sendRoutedResearchMessage,
    sendAgentMessage,
    setActiveView,
    setPendingBrandsTab,
    openSidebarPanel,
    closeSidebarPanel,
    selectedAspectRatio,
    selectedAspectRatios,
    setSelectedAspectRatio,
    toggleSelectedAspectRatio,
    generationMedium,
    setGenerationMedium,
    generationMediumByProject,
    imageModel,
    imageTier,
    setImageModel,
    imageModelByProject,
    imageTierByProject,
    setProjectImageModel,
    setImageTier,
    setProjectImageTier,
    adFilter,
    adFilterByProject,
    setAdFilter,
    setProjectAdFilter,
    setCanvasOpen,
    appendCanvasAdCopyVariation,
    setShowCopySidebar,
    setAdCopyGenerating,
    clearStaleTypingForProject,
  } = useCanvasStore();
  // Comment-mode treats canvas images as comment TARGETS, not chat
  // reference images. Suppress the "X canvas images selected as reference"
  // bar while comment mode is active so it doesn't confuse the user.
  const commentMode = useCanvasStore((s) => s.commentMode);

  const messages = useActiveProjectMessages();
  const projectLoading = useActiveProjectLoading();
  const isLoadingChat = projectLoading;
  const conversationLoading = projectLoading;
  const persistenceProjectId = useActiveProjectId();
  const images = useActiveProjectImages();
  // v6.22 — the medium THIS chat runs in: the task's stamped medium once a
  // task exists (missing entry = image, covering every pre-existing task),
  // else the new-task screen's pending tab choice.
  const activeTaskMedium: 'image' | 'video' = persistenceProjectId != null
    ? (generationMediumByProject[persistenceProjectId] ?? 'image')
    : generationMedium;

  // v7.04 — video is admin-only while it's being built (see use-video-enabled).
  const videoEnabled = useVideoEnabled();
  const videoGateCopy = useVideoGateCopy();

  // Low-credit indicator — renders nothing on loading/error so chat remains usable.
  const usageBalanceQuery = useUsageBalance();
  const usageBalance = usageBalanceQuery.data?.balance;
  const showLowCredits = usageBalance !== undefined && usageBalance < 50;
  const selectedImageIds = useCanvasStore((s) => s.selectedImageIds);
  const setSelectedImageIds = useCanvasStore((s) => s.setSelectedImageIds);
  const pendingVariantsConfirm = useCanvasStore((s) => s.pendingVariantsConfirm);
  const dismissVariantsConfirm = useCanvasStore((s) => s.dismissVariantsConfirm);
  const confirmVariantsAndSend = useCanvasStore((s) => s.confirmVariantsAndSend);
  const isTyping = typingByProject[persistenceProjectId ?? 'new'] ?? false;
  const cloneIsWorking = useCloneActivity(state => persistenceProjectId != null && (state.runs[persistenceProjectId]?.length ?? 0) > 0);
  const isWorking = isTyping || cloneIsWorking;
  const researchTasksByConversation = useCanvasStore((s) => s.researchTasksByConversation);
  const adDataCacheByProject = useCanvasStore((s) => s.adDataCacheByProject);
  const convKey: string | number = persistenceProjectId ?? 'new';
  const _rawResearchTask = researchTasksByConversation[convKey];
  const activeResearchTask = _rawResearchTask && !_rawResearchTask.dismissed ? _rawResearchTask : null;
  const rawAdCache = adDataCacheByProject[convKey] || {};
  const adDataCache = useMemo(() => {
    const m = new Map<string, AdViewData>();
    for (const [k, v] of Object.entries(rawAdCache)) {
      m.set(k, v);
    }
    return m;
  }, [rawAdCache]);

  const lastAgentMessage = useMemo(() => {
    const agentMsgs = messages.filter((m) => m.isAgentMessage);
    return agentMsgs.length > 0 ? agentMsgs[agentMsgs.length - 1] : null;
  }, [messages]);
  const activeAgentTaskGroups = lastAgentMessage?.agentTaskGroups ?? [];

  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  // v4.54 — upload failures render this in the composer instead of vanishing.
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);
  const [projectSubOpen, setProjectSubOpen] = useState(false);
  // v4.32 — home "+ → Ad Angles": pick a copy-set folder, composer collapses
  // to a preview chip, submit becomes "Build Campaign" (same full-funnel
  // build as the folder's own Build button).
  const [anglesSubOpen, setAnglesSubOpen] = useState(false);
  const anglesSubCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [selectedAngleSet, setSelectedAngleSet] = useState<{ id: number; name: string; angleCount: number } | null>(null);
  const [angleBuildStarting, setAngleBuildStarting] = useState(false);
  const [angleBuildError, setAngleBuildError] = useState<string | null>(null);
  // v3.91 — Connect Meta Account welcome shortcut opens Settings on Connections.
  const [metaSettingsOpen, setMetaSettingsOpen] = useState(false);
  const [assigningProject, setAssigningProject] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  const [pendingFolderId, setPendingFolderId] = useState<number | null>(null);
  const [selectedAdCard, setSelectedAdCard] = useState<AdCardData | null>(null);
  const [ratioPopoverOpen, setRatioPopoverOpen] = useState(false);
  const ratioBtnRef = useRef<HTMLDivElement>(null);
  // v7.16 — image-model dropdown (shared by home + in-task composers; only
  // one renders at a time). Per-task stamp wins for display; picking writes
  // the task stamp AND the global so the choice sticks for the next task.
  const [modelPopoverOpen, setModelPopoverOpen] = useState(false);
  const modelBtnRef = useRef<HTMLDivElement>(null);
  const activeImageModel = persistenceProjectId != null
    ? (imageModelByProject[persistenceProjectId] ?? imageModel)
    : imageModel;
  const pickImageModel = (m: 'nano-banana-2' | 'gpt-image-2') => {
    if (persistenceProjectId != null) setProjectImageModel(persistenceProjectId, m);
    setImageModel(m); // also resets the global tier (see store)
    // v7.20 — a family switch invalidates the other family's tier dial.
    if (persistenceProjectId != null) setProjectImageTier(persistenceProjectId, null);
  };

  // v7.20 — the model's second dial (gpt quality / nano resolution).
  const [tierPopoverOpen, setTierPopoverOpen] = useState(false);
  const tierBtnRef = useRef<HTMLDivElement>(null);
  const activeImageTier = persistenceProjectId != null
    ? (imageTierByProject[persistenceProjectId] ?? imageTier)
    : imageTier;
  const pickImageTier = (t: string) => {
    if (persistenceProjectId != null) setProjectImageTier(persistenceProjectId, t);
    setImageTier(t);
  };

  // v7.37 — composer Advanced angle/style filter (image tab). Click-toggled
  // panel that STAYS open; per-task stamp wins for display, picking writes
  // task + global (same law as the model/tier dials).
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const activeAdFilter = persistenceProjectId != null
    ? (adFilterByProject[persistenceProjectId] ?? adFilter)
    : adFilter;
  // Scope law (differs from the model/tier dials on purpose): a filter set
  // INSIDE a task stays on that task; only the home composer writes the
  // global, which is what the next new task is stamped with. Otherwise a
  // one-off "Pain Point only" run would quietly narrow every later "make 5
  // ads" for the rest of the session.
  const changeAdFilter = (f: { styles: string[] | null; angles: string[] | null }) => {
    if (persistenceProjectId != null) setProjectAdFilter(persistenceProjectId, f);
    else setAdFilter(f);
  };
  useEffect(() => {
    if (activeTaskMedium === 'video') setAdvancedOpen(false);
  }, [activeTaskMedium]);

  const openSidebar = useCallback((content: SidebarPanelContent) => {
    openSidebarPanel(content);
  }, [openSidebarPanel]);

  const closeSidebar = useCallback(() => {
    closeSidebarPanel();
  }, [closeSidebarPanel]);

  // Typewriter placeholder animation
  const PLACEHOLDER_FULL = 'What kind of ads do you want to make today?';
  const [placeholderText, setPlaceholderText] = useState('');
  const [placeholderDone, setPlaceholderDone] = useState(false);
  const placeholderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    let idx = 0;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      idx++;
      setPlaceholderText(PLACEHOLDER_FULL.slice(0, idx));
      if (idx < PLACEHOLDER_FULL.length) {
        const delay = 40 + Math.random() * 30;
        placeholderTimerRef.current = setTimeout(tick, delay);
      } else {
        setPlaceholderDone(true);
      }
    };
    placeholderTimerRef.current = setTimeout(tick, 300);
    return () => {
      cancelled = true;
      if (placeholderTimerRef.current) clearTimeout(placeholderTimerRef.current);
    };
  }, []);

  const cancelPlaceholderAnimation = useCallback(() => {
    // Only cancel if the component has fully mounted (avoids autoFocus cancelling animation)
    if (!mountedRef.current) return;
    if (!placeholderDone) {
      if (placeholderTimerRef.current) clearTimeout(placeholderTimerRef.current);
      setPlaceholderText(PLACEHOLDER_FULL);
      setPlaceholderDone(true);
    }
  }, [placeholderDone]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const taskProgressCardRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const plusMenuRef = useRef<HTMLDivElement>(null);
  const prevIsTypingRef = useRef(isTyping);
  const subMenuCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const queryClient = useQueryClient();
  const prevPersistenceProjectIdRef = useRef<number | null | undefined>(undefined);
  const isLoadingChatRef = useRef(isLoadingChat);
  isLoadingChatRef.current = isLoadingChat;

  // Reset local loading gate whenever the user navigates to a different chat.
  //
  // CRITICAL: always derive from the store's actual loading flag — never set
  // unconditionally to true. If we set true and the destination project is
  // already in memory (e.g. user clicked away, then came back), the store's
  // `loading` is already false, the cleanup effect on isLoadingChat won't
  // fire (dependency didn't change), and the skeleton gets stuck forever.
  //
  // This was the "forever loading" bug: switching to a previously-loaded chat
  // skipped the fetch, never flipped store.loading from false→false, never
  // triggered the cleanup, never cleared the skeleton.
  useEffect(() => {
    const prevId = prevPersistenceProjectIdRef.current;
    prevPersistenceProjectIdRef.current = persistenceProjectId;

    if (!persistenceProjectId) {
      // Brand new chat — no loading needed, show welcome screen immediately
      setIsLoading(false);
    } else {
      // Any navigation: mirror the store's loading flag for this project.
      // - Already-loaded project → false → no skeleton.
      // - Mid-fetch → true → skeleton until fetch completes, then the
      //   isLoadingChat cleanup effect below clears it.
      setIsLoading(isLoadingChatRef.current);
    }
  }, [persistenceProjectId]);

  // Sync with the store's isLoadingChat: once it finishes, clear local gate
  useEffect(() => {
    if (!isLoadingChat) {
      setIsLoading(false);
    }
  }, [isLoadingChat]);

  // Clear stale thinking state when navigating to a project (or on initial mount).
  // If the store thinks this project is "typing" but there is no live request
  // (e.g. user reloaded the page or navigated away mid-generation), reset it.
  useEffect(() => {
    const key = persistenceProjectId ?? 'new';
    clearStaleTypingForProject(key);
  }, [persistenceProjectId, clearStaleTypingForProject]);

  const isInsideProject = !!folderProjectId;

  // Fetch active project details to show project + brand name in header
  const { data: activeProject } = useQuery<{ id: number; name: string; brandId?: number | null }>({
    queryKey: ['/api/canvas/projects', folderProjectId],
    queryFn: async () => {
      const resp = await fetch(`/api/canvas/projects/${folderProjectId}`);
      if (!resp.ok) return null;
      return resp.json();
    },
    staleTime: 60000,
    enabled: isInsideProject,
  });

  const { data: folders = [] } = useQuery<Array<{ id: number; name: string; color?: string }>>({
    queryKey: ['/api/canvas/projects', 'rootOnly'],
    queryFn: async () => {
      const resp = await fetch('/api/canvas/projects?rootOnly=1');
      if (!resp.ok) return [];
      return resp.json();
    },
    staleTime: 30000,
    enabled: plusMenuOpen,
  });

  const { data: brands = [], isSuccess: brandsLoaded } = useQuery<Brand[]>({
    queryKey: ['/api/brands'],
    staleTime: 60000,
  });

  // v7.44 — first-run tour (the numbered pins). Auto-arms ONCE per mount for
  // a non-admin who has never finished/skipped it, only on this welcome
  // screen, only after /api/brands settles (the fork depends on it). Admins
  // arrive via the Dev Tools "Start tour" item, which sets 'forced'.
  const tourAuthUser = useAuthUser();
  const tourRequest = useCanvasStore((s) => s.tourRequest);
  const setTourRequest = useCanvasStore((s) => s.setTourRequest);
  const tourAutoArmedRef = useRef(false);
  const tourEligible = Boolean(
    tourAuthUser && !tourAuthUser.isAdmin && tourAuthUser.tourCompleted === false && !isTourCompleted(tourAuthUser.id, tourAuthUser.tourCompleted) && !tourAuthUser.needsOnboarding,
  );
  useEffect(() => {
    if (!tourAuthUser) return;
    const sync = () => { void syncTourCompletion(tourAuthUser.id, tourAuthUser.tourCompleted); };
    sync();
    window.addEventListener('online', sync);
    return () => window.removeEventListener('online', sync);
  }, [tourAuthUser?.id, tourAuthUser?.tourCompleted]);
  useEffect(() => {
    if (tourAutoArmedRef.current || !tourEligible || !brandsLoaded) return;
    if (!(fullWidth && messages.length === 0 && !persistenceProjectId)) return;
    tourAutoArmedRef.current = true;
    setTourRequest('auto');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourEligible, brandsLoaded, fullWidth, messages.length, persistenceProjectId]);

  // v6.62 — brand-first gate: set when a new-task send is blocked because no
  // brand is attached; cleared as soon as one is.
  const [brandGateError, setBrandGateError] = useState<string | null>(null);

  // v3.87 — fall back to the project row's brandId: on a REOPENED canvas the
  // store's activeBrandId is only lazily set at first send, so the chip showed
  // a bare "+" even though the brand was attached (user report).
  const activeBrand = brands.find((b) => b.id === (activeBrandId ?? activeProject?.brandId ?? undefined));

  // v6.62 — attaching a brand resolves the gate: clear the error live.
  useEffect(() => {
    if (activeBrand) setBrandGateError(null);
  }, [activeBrand]);

  // v4.32 — angle-set folders for the home "+ → Ad Angles" submenu (lazy).
  const { data: angleSetsResp } = useQuery<{ sets: Array<{ id: number; name: string; status: string; angleCount: number }> }>({
    queryKey: [`/api/brands/${activeBrand?.id}/angle-sets`],
    enabled: plusMenuOpen && !!activeBrand,
    staleTime: 30_000,
  });
  const buildableAngleSets = (angleSetsResp?.sets ?? []).filter((st) => st.status === 'ready' && st.angleCount > 0);

  // v4.89 — the home "Build Campaign" flow mirrors AdAnglesPanel's handleBuild:
  // same in-chat progress card, canvas force-open, and throttled live tile
  // refresh. The v4.32 drain-only version discarded every event except
  // project_resolved and parked the user on a blank chat pane for the whole
  // build ("i went to run full campaign ... and just get this screen").
  const startHomeAngleBuild = useCallback(async () => {
    if (!selectedAngleSet) return;
    const setForBuild = selectedAngleSet;
    setAngleBuildError(null);
    setAngleBuildStarting(true);
    try {
      // No projectId — from home it's ALWAYS a new task, so let the server
      // auto-create the "Full Funnel — {set}" canvas (resolveFullFunnelProject)
      // and pick the id off the stream's project_resolved event (~1s in).
      const resp = await fetch(`/api/angle-sets/${setForBuild.id}/build`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!resp.ok || !resp.body) {
        const err = await resp.json().catch(() => ({} as Record<string, unknown>));
        throw new Error(String((err as { message?: string }).message ?? `Build failed (${resp.status})`));
      }
      const brandIdForSwitch = activeBrand?.id ?? null;
      const setName = setForBuild.name;

      // Drain in the background — the build survives this composer unmounting
      // (same no-abort contract as the folder Build button). All card/canvas
      // state lives in this closure, same shape as the panel's handleBuild.
      void (async () => {
        let resolvedProjectId: number | null = null;
        let cardCreated = false;
        let doneCount = 0;
        let totalAds = setForBuild.angleCount || 0;
        let lastTileReload = 0;
        let completedResult: { generated?: number; failed?: number; projectId?: number } | null = null;
        let streamError: string | null = null;
        const userMsgId = `m-ffc-user-${Date.now()}`;
        const cardMsgId = `m-ffc-${Date.now()}`;
        const cardProgress: PlaybookProgressState = {
          playbookName: 'Full Funnel Campaign',
          totalPhases: 0,
          phases: [],
          phaseStatuses: {},
          phaseStatusTexts: {},
          activePhaseId: null,
          qaResults: [],
          conceptProgress: { current: 0, total: totalAds },
          completedSummary: null,
          isDone: false,
        };
        let cardContent = 'Building Full Funnel Campaign…';
        let cardStreaming = true;
        const syncCard = () => {
          if (resolvedProjectId == null || !cardCreated) return;
          const userMsg: CanvasChatMessage = {
            id: userMsgId,
            role: 'user',
            content: `Build Full Funnel Campaign — ${setName}`,
            timestamp: Date.now(),
          };
          const cardMsg: CanvasChatMessage = {
            id: cardMsgId,
            role: 'assistant',
            content: cardContent,
            isStreaming: cardStreaming,
            timestamp: Date.now(),
            playbookProgress: {
              ...cardProgress,
              phaseStatuses: { ...cardProgress.phaseStatuses },
              phaseStatusTexts: { ...cardProgress.phaseStatusTexts },
              conceptProgress: cardProgress.conceptProgress ? { ...cardProgress.conceptProgress } : null,
            },
          };
          useCanvasStore.getState()._writeMessages(resolvedProjectId, (msgs) => {
            let next = msgs.some((m) => m.id === userMsgId) ? msgs : [...msgs, userMsg];
            next = next.some((m) => m.id === cardMsgId)
              ? next.map((m) => (m.id === cardMsgId ? cardMsg : m))
              : [...next, cardMsg];
            return next;
          });
        };
        const finalizeCard = (summary: string, failed: boolean) => {
          if (!cardCreated || resolvedProjectId == null) return;
          for (const id of Object.keys(cardProgress.phaseStatuses)) {
            if (cardProgress.phaseStatuses[id] !== 'completed') {
              cardProgress.phaseStatuses[id] = failed ? 'failed' : 'completed';
            }
          }
          cardProgress.activePhaseId = null;
          cardProgress.isDone = true;
          cardProgress.completedSummary = summary;
          cardContent = summary;
          cardStreaming = false;
          syncCard();
          useCanvasStore.getState().saveConversationHistory(resolvedProjectId).catch(() => {});
        };
        try {
          await readBuildSSEEvents(resp, (eventName, evtData) => {
            if (eventName === 'project_resolved') {
              const pid = (evtData as { projectId?: number })?.projectId;
              if (typeof pid === 'number') {
                resolvedProjectId = pid;
                useCanvasStore.getState().switchProject(pid, { brandId: brandIdForSwitch })
                  .then(async () => {
                    // A fresh build project has no loaded images yet, so
                    // switchProject's auto-open never fires and the user lands
                    // in the empty CHAT pane while ads build. Force the canvas
                    // open (same patch AdAnglesPanel's handleBuild applies).
                    useCanvasStore.getState()._writeProjectView(pid, {
                      isCanvasOpen: true,
                      activeView: 'chat-canvas' as const,
                    });
                    await useCanvasStore.getState().reloadProject(pid).catch(() => {});
                    // Card goes in only AFTER the conversation has loaded —
                    // switchProject/reloadProject REPLACE the message list.
                    cardCreated = true;
                    syncCard();
                  })
                  .catch(() => {});
              }
            } else if (eventName === 'playbook_started') {
              const d = evtData as { totalAds?: number; stages?: string[] };
              if (typeof d?.totalAds === 'number' && d.totalAds > 0) totalAds = d.totalAds;
              const stages = Array.isArray(d?.stages) ? d.stages : [];
              cardProgress.totalPhases = stages.length;
              cardProgress.phases = stages.map((s) => ({
                id: s,
                name: FF_STAGE_LABELS[s] ?? s,
                phaseType: 'generation',
              }));
              cardProgress.phaseStatuses = Object.fromEntries(stages.map((s) => [s, 'pending' as const]));
              cardProgress.conceptProgress = { current: doneCount, total: totalAds };
              syncCard();
            } else if (eventName === 'canvas_image_ready' || eventName === 'canvas_image_failed') {
              doneCount += 1;
              const stage = (evtData as { stage?: string })?.stage;
              if (stage && cardProgress.phaseStatuses[stage] === 'pending') {
                cardProgress.phaseStatuses[stage] = 'running';
                cardProgress.activePhaseId = stage;
              }
              cardProgress.conceptProgress = { current: doneCount, total: totalAds };
              syncCard();
              // This SSE stream is detached from the canvas store, so tiles
              // only appear when the project's images refresh. Non-destructive
              // refresh (throttled) so the canvas fills in live.
              if (resolvedProjectId != null && Date.now() - lastTileReload > 4000) {
                lastTileReload = Date.now();
                useCanvasStore.getState().refreshProjectImages(resolvedProjectId).catch(() => {});
              }
            } else if (eventName === 'stage_completed') {
              const stage = (evtData as { stage?: string })?.stage;
              if (stage && cardProgress.phaseStatuses[stage]) {
                cardProgress.phaseStatuses[stage] = 'completed';
                if (cardProgress.activePhaseId === stage) cardProgress.activePhaseId = null;
                syncCard();
              }
            } else if (eventName === 'completed') {
              completedResult = evtData as { generated?: number; failed?: number; projectId?: number };
            } else if (eventName === 'error') {
              streamError = (evtData as { message?: string })?.message ?? 'Build failed';
            }
            // `playbook_completed` is intentionally ignored — `completed` (the
            // route-level frame) is the terminal signal.
          });
          if (streamError) {
            finalizeCard(`Build failed: ${streamError}`, true);
          } else if (completedResult) {
            const gen = completedResult.generated ?? doneCount;
            const failedN = completedResult.failed ?? 0;
            finalizeCard(
              `Full Funnel Campaign complete — ${gen} ads generated${failedN > 0 ? `, ${failedN} failed` : ''}.`,
              false,
            );
            // Final tile refresh — non-destructive, never replaces the chat.
            if (resolvedProjectId != null) {
              useCanvasStore.getState().refreshProjectImages(resolvedProjectId).catch(() => {});
            }
          } else {
            finalizeCard('Build ended unexpectedly.', true);
          }
        } catch (e) {
          // A dropped stream is NOT a failed build (server keeps going), but
          // the card can't update anymore — close it out honestly.
          finalizeCard(
            `Lost connection to the build stream — the build continues server-side. ${e instanceof Error ? e.message : String(e)}`,
            true,
          );
        }
      })();
      setSelectedAngleSet(null);
    } catch (e) {
      setAngleBuildError(e instanceof Error ? e.message : 'Build failed to start');
    } finally {
      setAngleBuildStarting(false);
    }
  }, [selectedAngleSet, activeBrand]);

  const { data: historyImages = [] } = useQuery<Array<{
    clientId: string; src: string; prompt?: string; name: string; createdAt?: string;
  }>>({
    queryKey: ['/api/canvas/projects', persistenceProjectId, 'images', 'history'],
    queryFn: async () => {
      const resp = await fetch(`/api/canvas/projects/${persistenceProjectId}/images`);
      if (!resp.ok) return [];
      const data = await resp.json();
      return Array.isArray(data) ? data.filter((img: any) => img.src && !img.src.startsWith('blob:')) : [];
    },
    staleTime: 5000,
    enabled: showHistory && !!persistenceProjectId,
  });

  const { data: activeTask } = useQuery<{ id: number; name: string; parentId?: number | null; brandId?: number | null } | null>({
    queryKey: ['/api/canvas/projects', persistenceProjectId],
    queryFn: async () => {
      if (!persistenceProjectId) return null;
      const resp = await fetch(`/api/canvas/projects/${persistenceProjectId}`);
      if (!resp.ok) return null;
      return resp.json();
    },
    staleTime: 5000,
    enabled: !!persistenceProjectId,
  });

  const breadcrumbBrand = brands.find((brand) => brand.id === (activeTask?.brandId ?? activeProject?.brandId ?? activeBrandId));

  const panToImage = useCanvasStore((s) => s.panToImage);

  useEffect(() => {
    if (!userScrolledUp) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping, userScrolledUp]);

  // v4.99 — a freshly-loaded conversation always starts at the BOTTOM (most
  // recent messages). Without this, layout-induced scroll events during
  // hydration fire the onScroll handler at scrollTop=0, which sets
  // userScrolledUp=true and permanently suppresses the auto-scroll above —
  // "it's always loading on the top". One instant jump per project load;
  // double rAF so run cards/thumbnails have laid out before we measure.
  const initialScrollKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const key = String(persistenceProjectId ?? 'none');
    if (messages.length === 0) return;
    if (initialScrollKeyRef.current === key) return;
    initialScrollKeyRef.current = key;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const el = messagesContainerRef.current;
      if (el) el.scrollTop = el.scrollHeight;
      setUserScrolledUp(false);
    }));
  }, [persistenceProjectId, messages.length]);

  useEffect(() => {
    if (prevIsTypingRef.current && !isTyping) {
      const el = messagesContainerRef.current;
      const atBottom = el ? el.scrollHeight - el.scrollTop - el.clientHeight < 60 : true;
      if (atBottom || !userScrolledUp) {
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          inputRef.current?.focus();
        }, 100);
      } else {
        inputRef.current?.focus();
      }
    }
    prevIsTypingRef.current = isTyping;
  }, [isTyping, userScrolledUp]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (plusMenuRef.current && !plusMenuRef.current.contains(e.target as Node)) {
        setPlusMenuOpen(false);
        setProjectSubOpen(false);
      }
      if (ratioBtnRef.current && !ratioBtnRef.current.contains(e.target as Node)) {
        setRatioPopoverOpen(false);
      }
      if (modelBtnRef.current && !modelBtnRef.current.contains(e.target as Node)) {
        setModelPopoverOpen(false);
      }
      if (tierBtnRef.current && !tierBtnRef.current.contains(e.target as Node)) {
        setTierPopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const confirmWorkflow = useCallback(async (workflowRunId: number) => {
    const resp = await fetch(`/api/workflows/${workflowRunId}/confirm`, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'text/event-stream' },
    });
    if (!resp.ok || !resp.body) return;
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    const updateMsg = (fn: (m: CanvasChatMessage) => CanvasChatMessage) => {
      const projectId = useCanvasStore.getState().activeProjectId;
      useCanvasStore.getState()._writeMessages(projectId, (msgs) =>
        msgs.map((m) =>
          m.workflowState?.workflowRunId === workflowRunId ? fn(m) : m
        ),
      );
    };
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const parts = buf.split('\n\n');
      buf = parts.pop() || '';
      for (const part of parts) {
        const lines = part.split('\n');
        let eventName = 'message';
        let dataBuf = '';
        for (const line of lines) {
          if (line.startsWith('event:')) eventName = line.slice(6).trim();
          else if (line.startsWith('data:')) dataBuf += line.slice(5).trim();
        }
        try {
          const data = JSON.parse(dataBuf) as Record<string, unknown>;
          if (eventName === 'workflow_started') {
            setCanvasOpen(true);
            updateMsg((m) => ({
              ...m,
              agentThinking: 'Starting workflow…',
              workflowState: m.workflowState
                ? { ...m.workflowState, status: 'running' as const }
                : { status: 'running' as const, totalPhases: data.totalPhases as number, workflowRunId },
            }));
          } else if (eventName === 'workflow_phase_start') {
            updateMsg((m) => ({
              ...m,
              agentThinking: `Running: ${data.phaseName}…`,
              workflowThinkingFeed: [],
              workflowState: m.workflowState
                ? { ...m.workflowState, status: 'running' as const, currentPhaseIndex: data.phaseIndex as number, currentPhaseName: data.phaseName as string }
                : { status: 'running' as const, currentPhaseIndex: data.phaseIndex as number, currentPhaseName: data.phaseName as string, workflowRunId },
            }));
          } else if (eventName === 'workflow_phase_thinking') {
            updateMsg((m) => ({
              ...m,
              workflowThinkingFeed: [...(m.workflowThinkingFeed || []), { id: `t-${Date.now()}-${Math.random()}`, type: 'thinking' as const, text: String(data.text || ''), timestamp: Date.now() }],
            }));
          } else if (eventName === 'workflow_phase_tool_start') {
            updateMsg((m) => ({
              ...m,
              workflowThinkingFeed: [...(m.workflowThinkingFeed || []), { id: `ts-${Date.now()}-${Math.random()}`, type: 'tool_start' as const, text: String(data.friendlyLabel || data.toolName || data.text || ''), toolName: String(data.toolName || ''), timestamp: Date.now() }],
            }));
          } else if (eventName === 'workflow_phase_tool_result') {
            updateMsg((m) => ({
              ...m,
              workflowThinkingFeed: [...(m.workflowThinkingFeed || []), { id: `tr-${Date.now()}-${Math.random()}`, type: 'tool_result' as const, text: String(data.summary || data.text || data.toolName || 'Done'), toolName: String(data.toolName || ''), timestamp: Date.now() }],
            }));
          } else if (eventName === 'workflow_phase_text') {
            updateMsg((m) => ({
              ...m,
              workflowThinkingFeed: [...(m.workflowThinkingFeed || []), { id: `tx-${Date.now()}-${Math.random()}`, type: 'text' as const, text: String(data.text || ''), timestamp: Date.now() }],
            }));
          } else if (eventName === 'workflow_phase_complete') {
            updateMsg((m) => ({ ...m, agentThinking: `Done: ${data.phaseName}` }));
          } else if (eventName === 'workflow_checkpoint') {
            updateMsg((m) => ({
              ...m,
              isStreaming: false,
              agentThinking: undefined,
              workflowState: m.workflowState
                ? { ...m.workflowState, status: 'paused' as const, checkpointId: data.checkpointId as number, checkpointType: data.checkpointType as string, checkpointPayload: data.payload as WorkflowState['checkpointPayload'] }
                : { status: 'paused' as const, checkpointId: data.checkpointId as number, checkpointType: data.checkpointType as string, checkpointPayload: data.payload as WorkflowState['checkpointPayload'], workflowRunId },
            }));
          } else if (eventName === 'workflow_complete') {
            updateMsg((m) => ({ ...m, isStreaming: false, agentThinking: undefined, workflowState: m.workflowState ? { ...m.workflowState, status: 'completed' as const } : { status: 'completed' as const, workflowRunId } }));
          } else if (eventName === 'workflow_cancelled') {
            updateMsg((m) => ({ ...m, isStreaming: false, agentThinking: undefined, workflowState: m.workflowState ? { ...m.workflowState, status: 'cancelled' as const } : { status: 'cancelled' as const, workflowRunId } }));
          } else if (eventName === 'workflow_error') {
            updateMsg((m) => ({ ...m, isStreaming: false, agentThinking: undefined, workflowState: m.workflowState ? { ...m.workflowState, status: 'error' as const, errorMessage: data.error as string } : { status: 'error' as const, errorMessage: data.error as string, workflowRunId } }));
          } else if (eventName === 'agent_reasoning') {
            const pd = data as { text?: string; classificationTimeMs?: number };
            updateMsg((m) => ({
              ...m,
              classifierReasoning: pd.text ?? undefined,
              classificationTimeMs: pd.classificationTimeMs ?? undefined,
            }));
          } else if (eventName === 'agent_ack') {
            const pd = data as { text?: string };
            updateMsg((m) => ({ ...m, ackText: pd.text ?? undefined }));
          } else if (eventName === 'playbook_start') {
            const pd = data as { playbookName?: string; totalPhases?: number; phases?: Array<{ id: string; name: string; phaseType: string }> };
            updateMsg((m) => ({
              ...m,
              playbookProgress: {
                playbookName: pd.playbookName ?? 'Campaign Playbook',
                totalPhases: pd.totalPhases ?? (pd.phases?.length ?? 0),
                phases: pd.phases ?? [],
                phaseStatuses: Object.fromEntries((pd.phases ?? []).map((p) => [p.id, 'pending' as const])),
                phaseStatusTexts: {},
                activePhaseId: null,
                qaResults: [],
                conceptProgress: null,
                completedSummary: null,
                isDone: false,
              },
            }));
          } else if (eventName === 'playbook_phase_start') {
            const pd = data as { phaseId?: string; statusText?: string };
            updateMsg((m) => {
              if (!m.playbookProgress) return m;
              return {
                ...m,
                playbookProgress: {
                  ...m.playbookProgress,
                  activePhaseId: pd.phaseId ?? null,
                  phaseStatuses: { ...m.playbookProgress.phaseStatuses, ...(pd.phaseId ? { [pd.phaseId]: 'running' as const } : {}) },
                  phaseStatusTexts: { ...m.playbookProgress.phaseStatusTexts, ...(pd.phaseId && pd.statusText ? { [pd.phaseId]: pd.statusText } : {}) },
                },
              };
            });
          } else if (eventName === 'playbook_phase_progress') {
            const pd = data as { phaseId?: string; conceptIndex?: number; totalConcepts?: number; statusText?: string };
            updateMsg((m) => {
              if (!m.playbookProgress) return m;
              return {
                ...m,
                playbookProgress: {
                  ...m.playbookProgress,
                  conceptProgress: pd.totalConcepts != null ? { current: pd.conceptIndex ?? 0, total: pd.totalConcepts } : m.playbookProgress.conceptProgress,
                  phaseStatusTexts: { ...m.playbookProgress.phaseStatusTexts, ...(pd.phaseId && pd.statusText ? { [pd.phaseId]: pd.statusText } : {}) },
                },
              };
            });
          } else if (eventName === 'playbook_phase_complete') {
            const pd = data as { phaseId?: string; success?: boolean };
            updateMsg((m) => {
              if (!m.playbookProgress) return m;
              const newStatus: 'completed' | 'failed' = pd.success ? 'completed' : 'failed';
              return {
                ...m,
                playbookProgress: {
                  ...m.playbookProgress,
                  phaseStatuses: { ...m.playbookProgress.phaseStatuses, ...(pd.phaseId ? { [pd.phaseId]: newStatus } : {}) },
                  activePhaseId: m.playbookProgress.activePhaseId === pd.phaseId ? null : m.playbookProgress.activePhaseId,
                },
              };
            });
          } else if (eventName === 'playbook_qa_result') {
            const pd = data as { conceptIndex?: number; attempt?: number; score?: number; passed?: boolean; status?: 'passed' | 'regenerating' | 'needs_review' };
            updateMsg((m) => {
              if (!m.playbookProgress) return m;
              const existing = m.playbookProgress.qaResults.filter((r) => r.conceptIndex !== (pd.conceptIndex ?? -1));
              return {
                ...m,
                playbookProgress: {
                  ...m.playbookProgress,
                  qaResults: [...existing, {
                    conceptIndex: pd.conceptIndex ?? 0,
                    attempt: pd.attempt ?? 1,
                    score: pd.score ?? 0,
                    passed: pd.passed ?? false,
                    status: pd.status ?? 'needs_review',
                  }].sort((a, b) => a.conceptIndex - b.conceptIndex),
                },
              };
            });
          } else if (eventName === 'playbook_complete') {
            const pd = data as { summary?: string };
            updateMsg((m) => {
              if (!m.playbookProgress) return m;
              return {
                ...m,
                playbookProgress: {
                  ...m.playbookProgress,
                  isDone: true,
                  activePhaseId: null,
                  completedSummary: pd.summary ?? null,
                },
              };
            });
            // Recover any images the live SSE stream dropped during a long run.
            void useCanvasStore.getState().reconcileImagesFromServer(useCanvasStore.getState().activeProjectId);
          } else if (eventName === 'playbook_brief_ready') {
            const pd = data as { briefId?: string; title?: string; content?: string };
            if (pd.briefId && pd.title && pd.content) {
              updateMsg((m) => ({
                ...m,
                playbookProgress: m.playbookProgress
                  ? {
                      ...m.playbookProgress,
                      campaignBrief: { briefId: pd.briefId!, title: pd.title!, content: pd.content! },
                    }
                  : m.playbookProgress,
              }));
            }
          } else if (eventName === 'canvas_image_pending') {
            const projectIdNow = useCanvasStore.getState().activeProjectId;
            useCanvasStore.getState()._writeProjectView(projectIdNow, { isCanvasOpen: true, activeView: 'chat-canvas' });
          } else if (eventName === 'canvas_image_ready') {
            const { imageUrl: imgUrl, prompt: imgPrompt, width: imgW, height: imgH, x: imgX, y: imgY, displayWidth: imgDW, displayHeight: imgDH, clientId: imgClientId, adStyle, adAngle, headline, bodyCopy, ctaText, conceptName, wavespeedPrompt } = data as { imageUrl?: string; prompt?: string; width?: number; height?: number; x?: number; y?: number; displayWidth?: number; displayHeight?: number; clientId?: string; adStyle?: string; adAngle?: string; headline?: string; bodyCopy?: string; ctaText?: string; conceptName?: string; wavespeedPrompt?: string };
            if (imgUrl) {
              const origW = imgW || 1080;
              const origH = imgH || 1080;
              const scale = Math.min(300 / origW, 300 / origH, 1);
              const dw = imgDW || Math.round(origW * scale);
              const dh = imgDH || Math.round(origH * scale);
              const posX = imgX ?? 60;
              const posY = imgY ?? 60;
              const newImgId = imgClientId || `img-gen-${Date.now()}-${Math.random().toString(36).slice(2)}`;
              const projectIdNow = useCanvasStore.getState().activeProjectId;
              const projState = projectIdNow != null ? useCanvasStore.getState().projects[projectIdNow] : null;
              const alreadyPresent = projState?.images.some((i) => i.id === newImgId) ?? false;
              if (!alreadyPresent) {
                const colorIndex = projState?.images.length ?? 0;
                const newImg = { id: newImgId, name: `agent_gen_${Date.now()}.jpg`, src: imgUrl, x: posX, y: posY, displayWidth: dw, displayHeight: dh, originalWidth: origW, originalHeight: origH, colorIndex, prompt: imgPrompt as string | undefined, adStyle, adAngle, headline, bodyCopy, ctaText, conceptName, wavespeedPrompt };
                useCanvasStore.getState()._writeImages(projectIdNow, (imgs) => [...imgs, newImg]);
                useCanvasStore.getState()._writeProjectView(projectIdNow, {
                  isCanvasOpen: true,
                  activeView: 'chat-canvas' as const,
                  pendingFitToRow: { minX: posX, minY: posY, maxX: posX + dw, maxY: posY + dh },
                });
              }
            }
          }
        } catch { /* ignore parse errors */ }
      }
    }
  }, []);

  const dismissWorkflow = useCallback(async (workflowRunId: number) => {
    const dismissProjId = useCanvasStore.getState().activeProjectId;
    useCanvasStore.getState()._writeMessages(dismissProjId, (msgs) =>
      msgs.map((m) =>
        m.workflowState?.workflowRunId === workflowRunId
          ? { ...m, workflowState: { ...m.workflowState, status: 'cancelled' as const } }
          : m
      )
    );
    await fetch(`/api/workflows/${workflowRunId}/cancel`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {});
  }, []);

  const handleKeepSimple = useCallback(async (workflowRunId: number, originalMessage: string) => {
    await fetch(`/api/workflows/${workflowRunId}/cancel`, { method: 'POST', credentials: 'include' }).catch(() => {});
    const assistantMsgId = `m-keep-simple-${Date.now()}`;
    const assistantMsg: CanvasChatMessage = {
      id: assistantMsgId,
      role: 'assistant' as const,
      content: '',
      isStreaming: true,
      isAgentMessage: true,
      agentThinking: 'Working on it…',
      timestamp: Date.now(),
    };
    {
      const projectIdAtStart = useCanvasStore.getState().activeProjectId;
      useCanvasStore.getState()._writeMessages(projectIdAtStart, (msgs) => [...msgs, assistantMsg]);
      useCanvasStore.setState((s) => ({
        typingByProject: { ...s.typingByProject, [projectIdAtStart ?? 'new']: true },
      }));
    }
    const updateMsg = (fn: (m: CanvasChatMessage) => CanvasChatMessage) => {
      const updateProjId = useCanvasStore.getState().activeProjectId;
      useCanvasStore.getState()._writeMessages(updateProjId, (msgs) =>
        msgs.map((m) => m.id === assistantMsgId ? fn(m) : m)
      );
    };
    try {
      const state = useCanvasStore.getState();
      const resp = await fetch('/api/canvas/chat', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: originalMessage,
          nudgeAction: 'keep_simple',
          brandId: state.activeBrandId,
          adAccountId: state.activeAdAccountId,
          projectId: state.activeProjectId,
        }),
      });
      if (!resp.ok || !resp.body) {
        updateMsg((m) => ({ ...m, isStreaming: false, agentThinking: undefined, content: 'Something went wrong. Please try again.' }));
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let accContent = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop() || '';
        for (const part of parts) {
          const lines = part.split('\n');
          let dataBuf = '';
          for (const line of lines) {
            if (line.startsWith('data:')) dataBuf += line.slice(5).trim();
          }
          try {
            const data = JSON.parse(dataBuf) as Record<string, unknown>;
            if (typeof data.content === 'string') {
              accContent += data.content;
              updateMsg((m) => ({ ...m, content: accContent }));
            } else if (data.done) {
              updateMsg((m) => ({ ...m, isStreaming: false, agentThinking: undefined }));
            }
          } catch { /* ignore */ }
        }
      }
      updateMsg((m) => ({ ...m, isStreaming: false, agentThinking: undefined }));
    } catch {
      updateMsg((m) => ({ ...m, isStreaming: false, agentThinking: undefined, content: 'Something went wrong. Please try again.' }));
    } finally {
      useCanvasStore.setState((s) => {
        const pKey = s.activeProjectId ?? 'new';
        return { typingByProject: { ...s.typingByProject, [pKey]: false } };
      });
      // Bulletproof: reconcile images on EVERY stream exit — including an
      // errored/aborted run where playbook_complete never fires — so anything
      // the live stream dropped still snaps in. No-op when already in sync.
      void useCanvasStore.getState().reconcileImagesFromServer(useCanvasStore.getState().activeProjectId);
    }
  }, []);

  const handleNudgeKeepSimple = useCallback(async (originalMessage: string) => {
    const assistantMsgId = `m-keep-simple-nudge-${Date.now()}`;
    const assistantMsg: CanvasChatMessage = {
      id: assistantMsgId,
      role: 'assistant' as const,
      content: '',
      isStreaming: true,
      isAgentMessage: true,
      agentThinking: 'Working on it…',
      timestamp: Date.now(),
    };
    {
      const projectIdAtStart = useCanvasStore.getState().activeProjectId;
      useCanvasStore.getState()._writeMessages(projectIdAtStart, (msgs) => [...msgs, assistantMsg]);
      useCanvasStore.setState((s) => ({
        typingByProject: { ...s.typingByProject, [projectIdAtStart ?? 'new']: true },
      }));
    }
    const updateMsg = (fn: (m: CanvasChatMessage) => CanvasChatMessage) => {
      const updateProjId = useCanvasStore.getState().activeProjectId;
      useCanvasStore.getState()._writeMessages(updateProjId, (msgs) =>
        msgs.map((m) => m.id === assistantMsgId ? fn(m) : m)
      );
    };
    try {
      const state = useCanvasStore.getState();
      const resp = await fetch('/api/canvas/chat', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: originalMessage,
          nudgeAction: 'keep_simple',
          brandId: state.activeBrandId,
          adAccountId: state.activeAdAccountId,
          projectId: state.activeProjectId,
        }),
      });
      if (!resp.ok || !resp.body) {
        updateMsg((m) => ({ ...m, isStreaming: false, agentThinking: undefined, content: 'Something went wrong. Please try again.' }));
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let accContent = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop() || '';
        for (const part of parts) {
          const lines = part.split('\n');
          let dataBuf = '';
          for (const line of lines) {
            if (line.startsWith('data:')) dataBuf += line.slice(5).trim();
          }
          try {
            const data = JSON.parse(dataBuf) as Record<string, unknown>;
            if (typeof data.content === 'string') {
              accContent += data.content;
              updateMsg((m) => ({ ...m, content: accContent }));
            } else if (data.done) {
              updateMsg((m) => ({ ...m, isStreaming: false, agentThinking: undefined }));
            }
          } catch { /* ignore */ }
        }
      }
      updateMsg((m) => ({ ...m, isStreaming: false, agentThinking: undefined }));
    } catch {
      updateMsg((m) => ({ ...m, isStreaming: false, agentThinking: undefined, content: 'Something went wrong. Please try again.' }));
    } finally {
      useCanvasStore.setState((s) => {
        const pKey = s.activeProjectId ?? 'new';
        return { typingByProject: { ...s.typingByProject, [pKey]: false } };
      });
      // Bulletproof: reconcile images on EVERY stream exit — including an
      // errored/aborted run where playbook_complete never fires — so anything
      // the live stream dropped still snaps in. No-op when already in sync.
      void useCanvasStore.getState().reconcileImagesFromServer(useCanvasStore.getState().activeProjectId);
    }
  }, []);

  const handleNudgeGoDeep = useCallback(async (skeletonType: string, originalMessage: string) => {
    const assistantMsgId = `m-nudge-deep-${Date.now()}`;
    const assistantMsg = {
      id: assistantMsgId,
      role: 'assistant' as const,
      content: '',
      isStreaming: true,
      isAgentMessage: true,
      agentThinking: 'Preparing your campaign launch…',
      timestamp: Date.now(),
    };
    {
      const projectIdAtStart = useCanvasStore.getState().activeProjectId;
      useCanvasStore.getState()._writeMessages(projectIdAtStart, (msgs) => [...msgs, assistantMsg]);
      useCanvasStore.setState((s) => ({
        typingByProject: { ...s.typingByProject, [projectIdAtStart ?? 'new']: true },
      }));
    }

    const updateMsg = (fn: (m: CanvasChatMessage) => CanvasChatMessage) => {
      const updateProjId = useCanvasStore.getState().activeProjectId;
      useCanvasStore.getState()._writeMessages(updateProjId, (msgs) =>
        msgs.map((m) => m.id === assistantMsgId ? fn(m) : m)
      );
    };

    try {
      const state = useCanvasStore.getState();
      const resp = await fetch('/api/canvas/chat', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: originalMessage,
          nudgeAction: 'go_deep',
          nudgeSkeletonType: skeletonType,
          brandId: state.activeBrandId,
          adAccountId: state.activeAdAccountId,
          projectId: state.activeProjectId,
        }),
      });
      if (!resp.ok || !resp.body) {
        updateMsg((m) => ({ ...m, isStreaming: false, agentThinking: undefined, content: 'Failed to start workflow.' }));
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop() || '';
        for (const part of parts) {
          const lines = part.split('\n');
          let eventName = 'message';
          let dataBuf = '';
          for (const line of lines) {
            if (line.startsWith('event:')) eventName = line.slice(6).trim();
            else if (line.startsWith('data:')) dataBuf += line.slice(5).trim();
          }
          try {
            const data = JSON.parse(dataBuf) as Record<string, unknown>;
            if (eventName === 'workflow_started') {
              setCanvasOpen(true);
              updateMsg((m) => ({
                ...m,
                agentThinking: 'Campaign launch running…',
                workflowState: {
                  workflowRunId: data.workflowRunId as number,
                  skeletonKey: skeletonType,
                  status: 'running' as const,
                  totalPhases: data.totalPhases as number,
                },
              }));
            // workflow_confirmation is no longer emitted (playbooks fire immediately now)
            } else if (eventName === 'workflow_phase_start') {
              updateMsg((m) => ({
                ...m,
                agentThinking: `Running: ${data.phaseName}…`,
                workflowThinkingFeed: [],
                workflowState: m.workflowState
                  ? { ...m.workflowState, status: 'running' as const, currentPhaseIndex: data.phaseIndex as number, currentPhaseName: data.phaseName as string }
                  : { status: 'running' as const, currentPhaseIndex: data.phaseIndex as number, currentPhaseName: data.phaseName as string },
              }));
            } else if (eventName === 'workflow_phase_thinking') {
              updateMsg((m) => ({
                ...m,
                workflowThinkingFeed: [...(m.workflowThinkingFeed || []), { id: `t-${Date.now()}-${Math.random()}`, type: 'thinking' as const, text: String(data.text || ''), timestamp: Date.now() }],
              }));
            } else if (eventName === 'workflow_phase_tool_start') {
              updateMsg((m) => ({
                ...m,
                workflowThinkingFeed: [...(m.workflowThinkingFeed || []), { id: `ts-${Date.now()}-${Math.random()}`, type: 'tool_start' as const, text: String(data.friendlyLabel || data.toolName || data.text || ''), toolName: String(data.toolName || ''), timestamp: Date.now() }],
              }));
            } else if (eventName === 'workflow_phase_tool_result') {
              updateMsg((m) => ({
                ...m,
                workflowThinkingFeed: [...(m.workflowThinkingFeed || []), { id: `tr-${Date.now()}-${Math.random()}`, type: 'tool_result' as const, text: String(data.summary || data.text || data.toolName || 'Done'), toolName: String(data.toolName || ''), timestamp: Date.now() }],
              }));
            } else if (eventName === 'workflow_phase_text') {
              updateMsg((m) => ({
                ...m,
                workflowThinkingFeed: [...(m.workflowThinkingFeed || []), { id: `tx-${Date.now()}-${Math.random()}`, type: 'text' as const, text: String(data.text || ''), timestamp: Date.now() }],
              }));
            } else if (eventName === 'workflow_checkpoint') {
              updateMsg((m) => ({
                ...m,
                isStreaming: false,
                agentThinking: undefined,
                workflowState: m.workflowState
                  ? { ...m.workflowState, status: 'paused' as const, checkpointId: data.checkpointId as number, checkpointType: data.checkpointType as string, checkpointPayload: data.payload as WorkflowState['checkpointPayload'] }
                  : { status: 'paused' as const, checkpointId: data.checkpointId as number, checkpointType: data.checkpointType as string, checkpointPayload: data.payload as WorkflowState['checkpointPayload'] },
              }));
            } else if (eventName === 'workflow_complete') {
              updateMsg((m) => ({ ...m, isStreaming: false, agentThinking: undefined, workflowState: m.workflowState ? { ...m.workflowState, status: 'completed' as const } : { status: 'completed' as const } }));
            } else if (eventName === 'workflow_cancelled') {
              updateMsg((m) => ({ ...m, isStreaming: false, agentThinking: undefined, workflowState: m.workflowState ? { ...m.workflowState, status: 'cancelled' as const } : { status: 'cancelled' as const } }));
            } else if (eventName === 'workflow_error') {
              updateMsg((m) => ({ ...m, isStreaming: false, agentThinking: undefined, workflowState: m.workflowState ? { ...m.workflowState, status: 'error' as const, errorMessage: data.error as string } : { status: 'error' as const, errorMessage: data.error as string } }));
            } else if (eventName === 'agent_reasoning') {
              const pd = data as { text?: string; classificationTimeMs?: number };
              updateMsg((m) => ({
                ...m,
                classifierReasoning: pd.text ?? undefined,
                classificationTimeMs: pd.classificationTimeMs ?? undefined,
              }));
            } else if (eventName === 'agent_ack') {
              const pd = data as { text?: string };
              updateMsg((m) => ({ ...m, ackText: pd.text ?? undefined }));
            } else if (eventName === 'playbook_start') {
              const pd = data as { playbookName?: string; totalPhases?: number; phases?: Array<{ id: string; name: string; phaseType: string }> };
              updateMsg((m) => ({
                ...m,
                playbookProgress: {
                  playbookName: pd.playbookName ?? 'Campaign Playbook',
                  totalPhases: pd.totalPhases ?? (pd.phases?.length ?? 0),
                  phases: pd.phases ?? [],
                  phaseStatuses: Object.fromEntries((pd.phases ?? []).map((p) => [p.id, 'pending' as const])),
                  phaseStatusTexts: {},
                  activePhaseId: null,
                  qaResults: [],
                  conceptProgress: null,
                  completedSummary: null,
                  isDone: false,
                },
              }));
            } else if (eventName === 'playbook_phase_start') {
              const pd = data as { phaseId?: string; statusText?: string };
              updateMsg((m) => {
                if (!m.playbookProgress) return m;
                return { ...m, playbookProgress: { ...m.playbookProgress, activePhaseId: pd.phaseId ?? null, phaseStatuses: { ...m.playbookProgress.phaseStatuses, ...(pd.phaseId ? { [pd.phaseId]: 'running' as const } : {}) }, phaseStatusTexts: { ...m.playbookProgress.phaseStatusTexts, ...(pd.phaseId && pd.statusText ? { [pd.phaseId]: pd.statusText } : {}) } } };
              });
            } else if (eventName === 'playbook_phase_progress') {
              const pd = data as { phaseId?: string; conceptIndex?: number; totalConcepts?: number; statusText?: string };
              updateMsg((m) => {
                if (!m.playbookProgress) return m;
                return { ...m, playbookProgress: { ...m.playbookProgress, conceptProgress: pd.totalConcepts != null ? { current: pd.conceptIndex ?? 0, total: pd.totalConcepts } : m.playbookProgress.conceptProgress, phaseStatusTexts: { ...m.playbookProgress.phaseStatusTexts, ...(pd.phaseId && pd.statusText ? { [pd.phaseId]: pd.statusText } : {}) } } };
              });
            } else if (eventName === 'playbook_phase_complete') {
              const pd = data as { phaseId?: string; success?: boolean };
              updateMsg((m) => {
                if (!m.playbookProgress) return m;
                const newStatus: 'completed' | 'failed' = pd.success ? 'completed' : 'failed';
                return { ...m, playbookProgress: { ...m.playbookProgress, phaseStatuses: { ...m.playbookProgress.phaseStatuses, ...(pd.phaseId ? { [pd.phaseId]: newStatus } : {}) }, activePhaseId: m.playbookProgress.activePhaseId === pd.phaseId ? null : m.playbookProgress.activePhaseId } };
              });
            } else if (eventName === 'playbook_qa_result') {
              const pd = data as { conceptIndex?: number; attempt?: number; score?: number; passed?: boolean; status?: 'passed' | 'regenerating' | 'needs_review' };
              updateMsg((m) => {
                if (!m.playbookProgress) return m;
                const existing = m.playbookProgress.qaResults.filter((r) => r.conceptIndex !== (pd.conceptIndex ?? -1));
                return { ...m, playbookProgress: { ...m.playbookProgress, qaResults: [...existing, { conceptIndex: pd.conceptIndex ?? 0, attempt: pd.attempt ?? 1, score: pd.score ?? 0, passed: pd.passed ?? false, status: pd.status ?? 'needs_review' }].sort((a, b) => a.conceptIndex - b.conceptIndex) } };
              });
            } else if (eventName === 'playbook_complete') {
              const pd = data as { summary?: string };
              updateMsg((m) => {
                if (!m.playbookProgress) return m;
                return { ...m, playbookProgress: { ...m.playbookProgress, isDone: true, activePhaseId: null, completedSummary: pd.summary ?? null } };
              });
              // Recover any images the live SSE stream dropped during a long run.
              void useCanvasStore.getState().reconcileImagesFromServer(useCanvasStore.getState().activeProjectId);
            } else if (eventName === 'playbook_brief_ready') {
              const pd = data as { briefId?: string; title?: string; content?: string };
              if (pd.briefId && pd.title && pd.content) {
                updateMsg((m) => ({
                  ...m,
                  playbookProgress: m.playbookProgress
                    ? { ...m.playbookProgress, campaignBrief: { briefId: pd.briefId!, title: pd.title!, content: pd.content! } }
                    : m.playbookProgress,
                }));
              }
            } else if (eventName === 'canvas_image_pending') {
              const projectIdNow = useCanvasStore.getState().activeProjectId;
              useCanvasStore.getState()._writeProjectView(projectIdNow, { isCanvasOpen: true, activeView: 'chat-canvas' });
            } else if (eventName === 'canvas_image_ready') {
              const { imageUrl: imgUrl, prompt: imgPrompt, width: imgW, height: imgH, x: imgX, y: imgY, displayWidth: imgDW, displayHeight: imgDH, clientId: imgClientId, adStyle, adAngle, headline, bodyCopy, ctaText, conceptName, wavespeedPrompt } = data as { imageUrl?: string; prompt?: string; width?: number; height?: number; x?: number; y?: number; displayWidth?: number; displayHeight?: number; clientId?: string; adStyle?: string; adAngle?: string; headline?: string; bodyCopy?: string; ctaText?: string; conceptName?: string; wavespeedPrompt?: string };
              if (imgUrl) {
                const origW = imgW || 1080;
                const origH = imgH || 1080;
                const scale = Math.min(300 / origW, 300 / origH, 1);
                const dw = imgDW || Math.round(origW * scale);
                const dh = imgDH || Math.round(origH * scale);
                const posX = imgX ?? 60;
                const posY = imgY ?? 60;
                const newImgId = imgClientId || `img-gen-${Date.now()}-${Math.random().toString(36).slice(2)}`;
                {
                  const projectIdImg = useCanvasStore.getState().activeProjectId;
                  const projStateImg = projectIdImg != null ? useCanvasStore.getState().projects[projectIdImg] : null;
                  const alreadyPresent = projStateImg?.images.some((i) => i.id === newImgId) ?? false;
                  if (!alreadyPresent) {
                    const colorIndex = projStateImg?.images.length ?? 0;
                    const newImg = { id: newImgId, name: `agent_gen_${Date.now()}.jpg`, src: imgUrl, x: posX, y: posY, displayWidth: dw, displayHeight: dh, originalWidth: origW, originalHeight: origH, colorIndex, prompt: imgPrompt as string | undefined, adStyle, adAngle, headline, bodyCopy, ctaText, conceptName, wavespeedPrompt };
                    useCanvasStore.getState()._writeImages(projectIdImg, (imgs) => [...imgs, newImg]);
                    useCanvasStore.getState()._writeProjectView(projectIdImg, {
                      isCanvasOpen: true,
                      activeView: 'chat-canvas' as const,
                      pendingFitToRow: { minX: posX, minY: posY, maxX: posX + dw, maxY: posY + dh },
                    });
                  }
                }
              }
            }
          } catch { /* ignore parse errors */ }
        }
      }
    } catch (err) {
      updateMsg((m) => ({ ...m, isStreaming: false, agentThinking: undefined, content: 'Failed to start campaign workflow.' }));
    } finally {
      useCanvasStore.setState((s) => {
        const pKey = s.activeProjectId ?? 'new';
        return { typingByProject: { ...s.typingByProject, [pKey]: false } };
      });
      // Bulletproof: reconcile images on EVERY stream exit — including an
      // errored/aborted run where playbook_complete never fires — so anything
      // the live stream dropped still snaps in. No-op when already in sync.
      void useCanvasStore.getState().reconcileImagesFromServer(useCanvasStore.getState().activeProjectId);
    }
  }, []);

  const handleCheckpointAction = useCallback(async (
    workflowRunId: number,
    checkpointId: number,
    action: 'approved' | 'modified' | 'rejected',
    feedback?: string,
  ) => {
    const updateMsg = (fn: (m: CanvasChatMessage) => CanvasChatMessage) => {
      const projectId = useCanvasStore.getState().activeProjectId;
      useCanvasStore.getState()._writeMessages(projectId, (msgs) =>
        msgs.map((m) =>
          m.workflowState?.workflowRunId === workflowRunId ? fn(m) : m
        ),
      );
    };

    updateMsg((m) => ({
      ...m,
      agentThinking: action === 'rejected' ? 'Cancelling workflow…' : 'Processing your response…',
      workflowState: m.workflowState ? { ...m.workflowState, status: action === 'rejected' ? 'cancelled' as const : 'running' as const } : undefined,
    }));

    try {
      const resp = await fetch(`/api/workflows/${workflowRunId}/checkpoint`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify({ checkpointId, action, feedback }),
      });
      if (!resp.ok || !resp.body) return;

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop() || '';
        for (const part of parts) {
          const lines = part.split('\n');
          let eventName = 'message';
          let dataBuf = '';
          for (const line of lines) {
            if (line.startsWith('event:')) eventName = line.slice(6).trim();
            else if (line.startsWith('data:')) dataBuf += line.slice(5).trim();
          }
          try {
            const data = JSON.parse(dataBuf) as Record<string, unknown>;
            if (eventName === 'workflow_resumed') {
              updateMsg((m) => ({
                ...m,
                agentThinking: `Resuming: ${data.phaseName || 'next phase'}…`,
                workflowState: m.workflowState
                  ? { ...m.workflowState, status: 'running' as const, checkpointId: undefined, checkpointType: undefined, checkpointPayload: undefined }
                  : { status: 'running' as const, workflowRunId },
              }));
            } else if (eventName === 'workflow_phase_start') {
              updateMsg((m) => ({
                ...m,
                agentThinking: `Running: ${data.phaseName}…`,
                workflowThinkingFeed: [],
                workflowState: m.workflowState
                  ? { ...m.workflowState, status: 'running' as const, currentPhaseIndex: data.phaseIndex as number, currentPhaseName: data.phaseName as string }
                  : { status: 'running' as const, workflowRunId },
              }));
            } else if (eventName === 'workflow_phase_thinking') {
              updateMsg((m) => ({
                ...m,
                workflowThinkingFeed: [...(m.workflowThinkingFeed || []), { id: `t-${Date.now()}-${Math.random()}`, type: 'thinking' as const, text: String(data.text || ''), timestamp: Date.now() }],
              }));
            } else if (eventName === 'workflow_phase_tool_start') {
              updateMsg((m) => ({
                ...m,
                workflowThinkingFeed: [...(m.workflowThinkingFeed || []), { id: `ts-${Date.now()}-${Math.random()}`, type: 'tool_start' as const, text: String(data.friendlyLabel || data.toolName || data.text || ''), toolName: String(data.toolName || ''), timestamp: Date.now() }],
              }));
            } else if (eventName === 'workflow_phase_tool_result') {
              updateMsg((m) => ({
                ...m,
                workflowThinkingFeed: [...(m.workflowThinkingFeed || []), { id: `tr-${Date.now()}-${Math.random()}`, type: 'tool_result' as const, text: String(data.summary || data.text || data.toolName || 'Done'), toolName: String(data.toolName || ''), timestamp: Date.now() }],
              }));
            } else if (eventName === 'workflow_phase_text') {
              updateMsg((m) => ({
                ...m,
                workflowThinkingFeed: [...(m.workflowThinkingFeed || []), { id: `tx-${Date.now()}-${Math.random()}`, type: 'text' as const, text: String(data.text || ''), timestamp: Date.now() }],
              }));
            } else if (eventName === 'workflow_checkpoint') {
              updateMsg((m) => ({
                ...m,
                isStreaming: false,
                agentThinking: undefined,
                workflowState: m.workflowState
                  ? { ...m.workflowState, status: 'paused' as const, checkpointId: data.checkpointId as number, checkpointType: data.checkpointType as string, checkpointPayload: data.payload as WorkflowState['checkpointPayload'] }
                  : { status: 'paused' as const, workflowRunId, checkpointId: data.checkpointId as number, checkpointType: data.checkpointType as string, checkpointPayload: data.payload as WorkflowState['checkpointPayload'] },
              }));
            } else if (eventName === 'workflow_complete') {
              updateMsg((m) => ({ ...m, isStreaming: false, agentThinking: undefined, workflowState: m.workflowState ? { ...m.workflowState, status: 'completed' as const } : { status: 'completed' as const, workflowRunId } }));
            } else if (eventName === 'workflow_cancelled') {
              updateMsg((m) => ({ ...m, isStreaming: false, agentThinking: undefined, workflowState: m.workflowState ? { ...m.workflowState, status: 'cancelled' as const } : { status: 'cancelled' as const, workflowRunId } }));
            } else if (eventName === 'workflow_error') {
              updateMsg((m) => ({ ...m, isStreaming: false, agentThinking: undefined, workflowState: m.workflowState ? { ...m.workflowState, status: 'error' as const, errorMessage: data.error as string } : { status: 'error' as const, errorMessage: data.error as string, workflowRunId } }));
            } else if (eventName === 'agent_reasoning') {
              const pd = data as { text?: string; classificationTimeMs?: number };
              updateMsg((m) => ({
                ...m,
                classifierReasoning: pd.text ?? undefined,
                classificationTimeMs: pd.classificationTimeMs ?? undefined,
              }));
            } else if (eventName === 'agent_ack') {
              const pd = data as { text?: string };
              updateMsg((m) => ({ ...m, ackText: pd.text ?? undefined }));
            } else if (eventName === 'playbook_start') {
              const pd = data as { playbookName?: string; totalPhases?: number; phases?: Array<{ id: string; name: string; phaseType: string }> };
              updateMsg((m) => ({
                ...m,
                playbookProgress: {
                  playbookName: pd.playbookName ?? 'Campaign Playbook',
                  totalPhases: pd.totalPhases ?? (pd.phases?.length ?? 0),
                  phases: pd.phases ?? [],
                  phaseStatuses: Object.fromEntries((pd.phases ?? []).map((p) => [p.id, 'pending' as const])),
                  phaseStatusTexts: {},
                  activePhaseId: null,
                  qaResults: [],
                  conceptProgress: null,
                  completedSummary: null,
                  isDone: false,
                },
              }));
            } else if (eventName === 'playbook_phase_start') {
              const pd = data as { phaseId?: string; statusText?: string };
              updateMsg((m) => {
                if (!m.playbookProgress) return m;
                return { ...m, playbookProgress: { ...m.playbookProgress, activePhaseId: pd.phaseId ?? null, phaseStatuses: { ...m.playbookProgress.phaseStatuses, ...(pd.phaseId ? { [pd.phaseId]: 'running' as const } : {}) }, phaseStatusTexts: { ...m.playbookProgress.phaseStatusTexts, ...(pd.phaseId && pd.statusText ? { [pd.phaseId]: pd.statusText } : {}) } } };
              });
            } else if (eventName === 'playbook_phase_progress') {
              const pd = data as { phaseId?: string; conceptIndex?: number; totalConcepts?: number; statusText?: string };
              updateMsg((m) => {
                if (!m.playbookProgress) return m;
                return { ...m, playbookProgress: { ...m.playbookProgress, conceptProgress: pd.totalConcepts != null ? { current: pd.conceptIndex ?? 0, total: pd.totalConcepts } : m.playbookProgress.conceptProgress, phaseStatusTexts: { ...m.playbookProgress.phaseStatusTexts, ...(pd.phaseId && pd.statusText ? { [pd.phaseId]: pd.statusText } : {}) } } };
              });
            } else if (eventName === 'playbook_phase_complete') {
              const pd = data as { phaseId?: string; success?: boolean };
              updateMsg((m) => {
                if (!m.playbookProgress) return m;
                const newStatus: 'completed' | 'failed' = pd.success ? 'completed' : 'failed';
                return { ...m, playbookProgress: { ...m.playbookProgress, phaseStatuses: { ...m.playbookProgress.phaseStatuses, ...(pd.phaseId ? { [pd.phaseId]: newStatus } : {}) }, activePhaseId: m.playbookProgress.activePhaseId === pd.phaseId ? null : m.playbookProgress.activePhaseId } };
              });
            } else if (eventName === 'playbook_qa_result') {
              const pd = data as { conceptIndex?: number; attempt?: number; score?: number; passed?: boolean; status?: 'passed' | 'regenerating' | 'needs_review' };
              updateMsg((m) => {
                if (!m.playbookProgress) return m;
                const existing = m.playbookProgress.qaResults.filter((r) => r.conceptIndex !== (pd.conceptIndex ?? -1));
                return { ...m, playbookProgress: { ...m.playbookProgress, qaResults: [...existing, { conceptIndex: pd.conceptIndex ?? 0, attempt: pd.attempt ?? 1, score: pd.score ?? 0, passed: pd.passed ?? false, status: pd.status ?? 'needs_review' }].sort((a, b) => a.conceptIndex - b.conceptIndex) } };
              });
            } else if (eventName === 'playbook_complete') {
              const pd = data as { summary?: string };
              updateMsg((m) => {
                if (!m.playbookProgress) return m;
                return { ...m, playbookProgress: { ...m.playbookProgress, isDone: true, activePhaseId: null, completedSummary: pd.summary ?? null } };
              });
              // Recover any images the live SSE stream dropped during a long run.
              void useCanvasStore.getState().reconcileImagesFromServer(useCanvasStore.getState().activeProjectId);
            } else if (eventName === 'playbook_brief_ready') {
              const pd = data as { briefId?: string; title?: string; content?: string };
              if (pd.briefId && pd.title && pd.content) {
                updateMsg((m) => ({
                  ...m,
                  playbookProgress: m.playbookProgress
                    ? { ...m.playbookProgress, campaignBrief: { briefId: pd.briefId!, title: pd.title!, content: pd.content! } }
                    : m.playbookProgress,
                }));
              }
            } else if (eventName === 'canvas_image_pending') {
              const projectIdNow = useCanvasStore.getState().activeProjectId;
              useCanvasStore.getState()._writeProjectView(projectIdNow, { isCanvasOpen: true, activeView: 'chat-canvas' });
            } else if (eventName === 'canvas_image_ready') {
              const { imageUrl: imgUrl, prompt: imgPrompt, width: imgW, height: imgH, x: imgX, y: imgY, displayWidth: imgDW, displayHeight: imgDH, clientId: imgClientId, adStyle, adAngle, headline, bodyCopy, ctaText, conceptName, wavespeedPrompt } = data as { imageUrl?: string; prompt?: string; width?: number; height?: number; x?: number; y?: number; displayWidth?: number; displayHeight?: number; clientId?: string; adStyle?: string; adAngle?: string; headline?: string; bodyCopy?: string; ctaText?: string; conceptName?: string; wavespeedPrompt?: string };
              if (imgUrl) {
                const origW = imgW || 1080;
                const origH = imgH || 1080;
                const scale = Math.min(300 / origW, 300 / origH, 1);
                const dw = imgDW || Math.round(origW * scale);
                const dh = imgDH || Math.round(origH * scale);
                const posX = imgX ?? 60;
                const posY = imgY ?? 60;
                const newImgId = imgClientId || `img-gen-${Date.now()}-${Math.random().toString(36).slice(2)}`;
                {
                  const projectIdImg = useCanvasStore.getState().activeProjectId;
                  const projStateImg = projectIdImg != null ? useCanvasStore.getState().projects[projectIdImg] : null;
                  const alreadyPresent = projStateImg?.images.some((i) => i.id === newImgId) ?? false;
                  if (!alreadyPresent) {
                    const colorIndex = projStateImg?.images.length ?? 0;
                    const newImg = { id: newImgId, name: `agent_gen_${Date.now()}.jpg`, src: imgUrl, x: posX, y: posY, displayWidth: dw, displayHeight: dh, originalWidth: origW, originalHeight: origH, colorIndex, prompt: imgPrompt as string | undefined, adStyle, adAngle, headline, bodyCopy, ctaText, conceptName, wavespeedPrompt };
                    useCanvasStore.getState()._writeImages(projectIdImg, (imgs) => [...imgs, newImg]);
                    useCanvasStore.getState()._writeProjectView(projectIdImg, {
                      isCanvasOpen: true,
                      activeView: 'chat-canvas' as const,
                      pendingFitToRow: { minX: posX, minY: posY, maxX: posX + dw, maxY: posY + dh },
                    });
                  }
                }
              }
            }
          } catch { /* ignore parse errors */ }
        }
      }
    } catch { /* ignore */ }
  }, []);

  const sendPendingRef = useRef(false);
  const handleSend = useCallback(async () => {
    if (sendPendingRef.current) return;
    sendPendingRef.current = true;
    const submitScope = useCanvasStore.getState();
    const scopeUnchanged = () => {
      const current = useCanvasStore.getState();
      return current.activeProjectId === submitScope.activeProjectId && current.activeBrandId === submitScope.activeBrandId && current.folderProjectId === submitScope.folderProjectId;
    };
    try {
    const text = input.trim();
    if (!text && chatAttachments.length === 0 && !attachedFile) return;

    // v6.62 — brand-first (Dan's spec): a NEW task never starts without a
    // brand attached. In-task messages are untouched (older tasks may
    // legitimately predate brand scoping).
    if (!persistenceProjectId && !activeBrand) {
      setBrandGateError(
        !brandsLoaded
          ? 'Still loading your brands — give it a second and try again.'
          : brands.length === 0
            ? 'Add a brand first — everything here is built around your brand.'
            : 'Please select which brand you would like to work with (+ menu → Add to brand).',
      );
      return;
    }
    if (activeBrand?.id) {
      const readinessError = await checkBrandReady(activeBrand.id);
      if (!scopeUnchanged()) return;
      if (readinessError) {
        setBrandGateError(readinessError);
        return; // Preserve the message, attachments and settings for manual retry.
      }
    }
    setBrandGateError(null);
    // v7.40 — a real submit closes the Advanced panel (Dan: "when I click
    // submit the Advanced thing should auto close"). Placed AFTER the guards
    // so an empty or brand-blocked send leaves it open for the fix-up.
    setAdvancedOpen(false);

    // Detect aspect ratio from prompt and update store if found. v4.62 —
    // ONLY when a single ratio is selected: the detector matches loose
    // content words ("story", "square", "banner"), and a deliberate
    // multi-selection must never be silently collapsed by one of them.
    if (text && selectedAspectRatios.length === 1) {
      const detected = detectRatioFromPrompt(text);
      if (detected && detected !== selectedAspectRatio) {
        setSelectedAspectRatio(detected);
      }
    }

    // Detect ad copy intent from chat — trigger generation same as toolbar
    const copyIntentPatterns = [
      /\bgenerate\b.*\b(ad\s*copy|copy\s*variations?|ad\s*variations?)\b/i,
      /\b(ad\s*copy|copy\s*variations?)\b.*\bgenerate\b/i,
      /\bwrite\b.*\b(ad\s*copy|facebook\s*ad|ad\s*text)\b/i,
      /\b(ad\s*copy|facebook\s*ads?)\b.*\bwrite\b/i,
      /\bcreate\b.*\b(ad\s*copy|ad\s*text|facebook\s*ads?)\b/i,
    ];
    // v5.33 — editing a VIDEO-CLONE START FRAME from chat: a selected clone
    // frame + an instruction is a SURGICAL frame edit written back to the
    // draft (frame aspect, tile replaced in place, Generate uses the edited
    // frame) — never the generic variation agent, which produced square
    // restyled ads that didn't touch the draft (Dan's blue-drink request).
    if (text && !commentMode && selectedImageIds.length === 1 && persistenceProjectId) {
      const selImg = images.find((im) => im.id === selectedImageIds[0]);
      if (selImg && isVideoCloneFrameSrc(selImg.src)) {
        setInput('');
        setSelectedImageIds([]);
        void editVideoCloneFrameFromChat(selImg.id, text);
        return;
      }
    }

    const isCopyIntent = text && copyIntentPatterns.some((re) => re.test(text));
    if (isCopyIntent && selectedImageIds.length > 0 && persistenceProjectId) {
      const variationsMatch = text.match(/(\d+)\s*variation/i);
      const variationsPerAd = variationsMatch ? Math.min(10, Math.max(1, Number(variationsMatch[1]))) : 5;
      const intentImageIds = [...selectedImageIds];
      setInput('');
      setShowCopySidebar(true);
      setAdCopyGenerating(intentImageIds, true);
      (async () => {
        try {
          const response = await fetch('/api/canvas/generate-ad-copy', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              projectId: persistenceProjectId,
              imageClientIds: intentImageIds,
              variationsPerAd,
              additionalDirection: text,
            }),
          });
          if (!response.ok || !response.body) return;
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buf = '';
          while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buf += decoder.decode(value, { stream: true });
            const parts = buf.split('\n\n');
            buf = parts.pop() || '';
            for (const part of parts) {
              const lines = part.split('\n');
              let evtType = '';
              let dataStr = '';
              for (const line of lines) {
                if (line.startsWith('event: ')) evtType = line.slice(7).trim();
                if (line.startsWith('data: ')) dataStr = line.slice(6).trim();
              }
              if (evtType === 'copy_complete' && dataStr) {
                try {
                  const d = JSON.parse(dataStr);
                  if (d.variation) appendCanvasAdCopyVariation(d.imageClientId, d.variation);
                } catch {}
              }
            }
          }
        } catch (err) {
          console.error('[Chat copy intent] Error:', err);
        } finally {
          setAdCopyGenerating(intentImageIds, false);
        }
      })();
      return;
    }

    setInput('');

    if (!persistenceProjectId) {
      const taskLabel = text || (chatAttachments.length > 0 ? `Image${chatAttachments.length > 1 ? 's' : ''} (${chatAttachments.length})` : 'New task');
      const taskName = taskLabel.length > 40 ? taskLabel.slice(0, 40) + '…' : taskLabel;
      try {
        let effectiveParentId = pendingFolderId ?? folderProjectId ?? null;
        if (!effectiveParentId && activeBrand?.id) {
          const foldersResponse = await fetch('/api/canvas/projects?rootOnly=1');
          if (!foldersResponse.ok) throw new Error('Could not find your brand folder. Please try again.');
          const brandFolders = await foldersResponse.json();
          if (!scopeUnchanged()) return;
          const matchedFolder = Array.isArray(brandFolders) ? brandFolders.find((folder: { brandId?: number; brand_id?: number }) => (folder.brandId ?? folder.brand_id) === activeBrand.id) : undefined;
          effectiveParentId = matchedFolder?.id ?? null;
        }
        const resp = await fetch('/api/canvas/projects', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // v6.62 — persist the brand on the task row itself: the guard above
          // proved activeBrand exists, but without brandId in the body a loose
          // task (no folder parent to inherit from) stored brand_id NULL and
          // the brand chip vanished on reload.
          body: JSON.stringify({ name: taskName, color: '#6366F1', parentId: effectiveParentId, isFolder: false, brandId: activeBrand?.id }),
        });
        if (!resp.ok) throw new Error('Task creation failed');
        if (resp.ok) {
          const proj = await resp.json();
          if (!scopeUnchanged()) return;
          // Set activeProjectId AND mark this project as loaded immediately.
          // Without setting loadedAt, a fast click-away-and-back triggers
          // switchProject's re-fetch which would overwrite the freshly-typed
          // user message before our save has a chance to land.
          const store = useCanvasStore.getState();
          store._writeProjectMetadata(proj.id, {
            loadedAt: Date.now(),
            loading: false,
            brandId: proj.brandId ?? activeBrand?.id ?? null,
            adAccountId: null,
            parentId: proj.parentId ?? proj.parent_id ?? effectiveParentId,
          });
          useCanvasStore.setState({ activeProjectId: proj.id });
          // v6.22 — stamp the composer's Image/Video choice onto THIS task
          // (per-task medium), then reset the new-task tab back to its
          // Image default so the next new task starts clean (Dan's spec).
          store.setProjectGenerationMedium(proj.id, store.generationMedium);
          if (store.generationMedium === 'video') useCanvasStore.setState({ generationMedium: 'image' });
          // v7.16 — stamp the composer's image-model choice onto THIS task
          // (per-task model; the global keeps the choice as the next-task
          // default — sticky, unlike the medium tab).
          store.setProjectImageModel(proj.id, store.imageModel);
          store.setProjectImageTier(proj.id, store.imageTier);
          store.setProjectAdFilter(proj.id, store.adFilter);
          // v7.35 — video tasks also stamp their dial set (variant/resolution/
          // duration/audio/ratio) so the task keeps what it was created with.
          if (store.generationMedium === 'video') {
            store.setProjectVideoDials(proj.id, {
              model: store.videoModel,
              variant: store.videoVariant,
              resolution: store.videoResolution,
              duration: store.videoDuration,
              audio: store.videoAudio,
              aspectRatio: store.videoAspectRatio,
            });
          }
          if (effectiveParentId !== null) {
            queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', effectiveParentId, 'tasks'] });
          }
          setPendingFolderId(null);
          queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', 'rootOnly'] });
          queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', 'all'] });
        }
      } catch {
        setInput(text);
        setBrandGateError('Your task could not be saved in its brand folder. Please try again.');
        return;
      }
    }

    // Pass first attachment as attachedFileUrl (for Claude Vision); additional attachments as text refs
    const firstAttachUrl = attachedFile?.url ?? (chatAttachments.length > 0 ? chatAttachments[0].url : undefined);
    let finalText = text;
    if (chatAttachments.length > 1) {
      const extraRefs = chatAttachments.slice(1).map((a) => `[Attached image: ${a.url}]`).join('\n');
      finalText = text ? `${text}\n\n${extraRefs}` : extraRefs;
    }
    // Route all messages through the agent loop.
    // For attachment-only sends, the agent receives the image URL as inline context.
    // Legacy sendMessage is kept as a fallback for non-chat (API-initiated) flows only.
    if (firstAttachUrl) {
      const attachLine = `[Attached image: ${firstAttachUrl}]`;
      const agentText = finalText.trim() ? `${finalText}\n\n${attachLine}` : attachLine;
      sendAgentMessage(agentText);
    } else if (finalText.trim()) {
      // v6.05 — video-clone CHAT EDIT: in a project with a READY clone draft,
      // a plain instruction ("make the blue drink more transparent blue")
      // routes to the script chat-edit first — it rewrites the affected
      // prompts, explains the change, and posts a Generate button. Non-edit
      // messages come back applies:false and fall through to the agent.
      const editText = finalText;
      const editProjectId = persistenceProjectId;
      if (editProjectId && selectedImageIds.length === 0) {
        void (async () => {
          try {
            const { draftId, busy } = await latestEditableDraftId(editProjectId);
            if (draftId != null) {
              const r = await runVideoCloneChatEdit(editProjectId, draftId, editText);
              if (r === 'handled') return;
            } else if (busy) {
              // v6.06 — a generating draft gets an honest "still running"
              // reply, never the generic agent (which knows nothing about
              // the video pipeline).
              pushVideoBusyNotice(editProjectId, editText);
              return;
            }
          } catch { /* lookup/edit hiccup — normal agent path below */ }
          sendAgentMessage(editText);
        })();
      } else {
        sendAgentMessage(finalText);
      }
    }
    if (chatAttachments.length > 0) {
      setChatAttachments([]);
    }
    // DO NOT auto-clear selectedImageIds here. The user's mental model is
    // "this image stays selected until I click elsewhere or deselect it."
    // Previously the chat cleared selection after every send, which created
    // the bug where "make 5 ads like this, different angles" (two sends in
    // a row) only fired variations on the SECOND send — the first send had
    // an already-cleared selection from a prior message, so the agent saw
    // no reference image and responded with text instead of routing to
    // run_variations_v3. Selection lifecycle is now strictly user-driven:
    // click canvas background to deselect, or hit a different image.
    } finally {
      sendPendingRef.current = false;
    }
  }, [input, sendMessage, sendAgentMessage, attachedFile, chatAttachments, setChatAttachments, persistenceProjectId, pendingFolderId, folderProjectId, queryClient, selectedAspectRatio, selectedAspectRatios, setSelectedAspectRatio, selectedImageIds, setSelectedImageIds, activeBrand, brands.length, brandsLoaded]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // v4.54 — upload failures used to die silently ("it spins and then
    // nothing happens"): the 5MB server cap rejected normal phone photos and
    // the catch only console.error'd. Pre-check the size, surface the
    // server's real message, and show it in the composer.
    setUploadError(null);
    if (file.size > 25 * 1024 * 1024) {
      setUploadError(`"${file.name}" is too large — images must be under 25MB.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/upload', { method: 'POST', body: formData });
      if (!res.ok) {
        let msg = `Upload failed (${res.status})`;
        try { msg = (await res.json())?.message ?? msg; } catch { /* keep default */ }
        throw new Error(msg);
      }
      const { url } = await res.json();
      addChatAttachment({ url, name: file.name });
    } catch (err) {
      console.error('File upload error:', err);
      setUploadError(err instanceof Error ? err.message : 'Upload failed — please try again.');
      setTimeout(() => setUploadError(null), 10000);
    }
    finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAssignToFolder = async (folderId: number) => {
    if (!persistenceProjectId) {
      setPendingFolderId(folderId);
      setFolderProjectId(folderId);
      setPlusMenuOpen(false);
      setProjectSubOpen(false);
      return;
    }
    setAssigningProject(true);
    try {
      await fetch(`/api/canvas/tasks/${persistenceProjectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parentId: folderId }),
      });
      queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', 'all'] });
      queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', folderId, 'tasks'] });
    } catch (err) { console.error('Assign to folder error:', err); }
    finally {
      setAssigningProject(false);
      setPlusMenuOpen(false);
      setProjectSubOpen(false);
    }
  };

  const isEmpty = messages.length === 0;

  const inputBar = (
    <div
      className="flex-shrink-0 px-4 py-3"
      style={fullWidth ? { maxWidth: 740, margin: '0 auto', width: '100%', paddingLeft: 0, paddingRight: 0 } : {}}
    >
      {/* Canvas selection reference thumbnails — hidden during comment mode
          (in commentMode the canvas selection means "comment target," not
          "chat reference image"). */}
      {!commentMode && selectedImageIds.length > 0 && (() => {
        const selectedImgs = selectedImageIds
          .map((id) => images.find((img) => img.id === id))
          .filter(Boolean) as typeof images;
        if (selectedImgs.length === 0) return null;
        return (
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {selectedImgs.map((img) => (
              <div
                key={img.id}
                data-testid={`canvas-ref-thumbnail-${img.id}`}
                style={{ position: 'relative', flexShrink: 0 }}
              >
                <img
                  src={img.src}
                  alt=""
                  style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 7, border: '1.5px solid #6366F1', display: 'block' }}
                  onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }}
                />
                <button
                  data-testid={`remove-canvas-ref-${img.id}`}
                  onClick={() => setSelectedImageIds(selectedImageIds.filter((id) => id !== img.id))}
                  style={{
                    position: 'absolute',
                    top: -5,
                    right: -5,
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    background: '#6366F1',
                    border: 'none',
                    color: '#FFFFFF',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                  }}
                >
                  <X style={{ width: 9, height: 9 }} />
                </button>
              </div>
            ))}
            <span style={{ fontSize: 11, color: '#8C8578' }}>
              {selectedImgs.length} canvas {selectedImgs.length === 1 ? 'image' : 'images'} selected as reference
            </span>
          </div>
        );
      })()}

      {/* chatAttachments thumbnails (multi-image) */}
      {chatAttachments.length > 0 && (
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {chatAttachments.map((att) => (
            <div
              key={att.url}
              data-testid={`chat-attachment-${att.url}`}
              style={{ position: 'relative', flexShrink: 0 }}
            >
              <img
                src={att.url}
                alt={att.name}
                style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8, border: '1px solid #E5E2DC', display: 'block' }}
                onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }}
              />
              <button
                data-testid={`remove-attachment-${att.url}`}
                onClick={() => removeChatAttachment(att.url)}
                style={{
                  position: 'absolute',
                  top: -5,
                  right: -5,
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: '#1A1A1A',
                  border: 'none',
                  color: '#FFFFFF',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                }}
              >
                <X style={{ width: 10, height: 10 }} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Attached file preview */}
      {attachedFile && (
        <div className="flex items-center gap-2 mb-2 px-2 py-1.5 rounded-lg" style={{ background: '#F5F3EE', border: '1px solid #E5E2DC' }}>
          <img src={attachedFile.url} alt={attachedFile.name}
            className="w-8 h-8 rounded object-cover flex-shrink-0"
            style={{ border: '1px solid #E5E2DC' }}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          <span className="text-xs truncate flex-1" style={{ color: '#8C8578' }}>{attachedFile.name}</span>
          <button onClick={() => setAttachedFile(null)} className="flex-shrink-0 transition-colors" style={{ color: '#B5B0A8' }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.color = '#1A1A1A')}
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.color = '#B5B0A8')}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <div
        className="flex items-start gap-2 transition-all"
        style={{
          background: '#FFFFFF',
          border: '1px solid #DCDCDC',
          borderRadius: 16,
          minHeight: 104,
          padding: '14px 16px',
        }}
        onFocus={(e) => (e.currentTarget.style.borderColor = '#A8A49E')}
        onBlur={(e) => (e.currentTarget.style.borderColor = '#DCDCDC')}
      >
        {/* Input area + action row */}
        <div className="composer-toolbar-container flex flex-col flex-1 gap-2 min-w-0">
          <textarea
            ref={inputRef}
            data-testid="canvas-chat-input"
            value={input}
            onChange={(e) => { cancelPlaceholderAnimation(); setInput(e.target.value); }}
            onKeyDown={handleKeyDown}
            onFocus={cancelPlaceholderAnimation}
            placeholder={
              activeBrand ? `Ask anything about ${activeBrand.name}…`
              : placeholderText
            }
            rows={2}
            className="w-full resize-none bg-transparent outline-none leading-relaxed max-h-48"
            style={{ fontSize: 14, color: '#1A1A1A', minHeight: '3rem', cursor: 'text' }}
          />
          {uploadError && (
            <div style={{ fontSize: 11, color: '#DC2626', padding: '2px 4px 6px' }}>{uploadError}</div>
          )}
          {brandGateError && (
            <div data-testid="brand-gate-error" style={{ fontSize: 11, color: '#DC2626', padding: '2px 4px 6px' }}>{brandGateError}</div>
          )}
          {/* Keep context and send together; settings wrap as deliberate groups. */}
          <div className="composer-toolbar" data-testid="composer-toolbar">
            <div className="composer-toolbar-context">
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
            {/* + menu */}
            <div ref={plusMenuRef} style={{ position: 'relative' }}>
              <button
                data-testid="input-attach"
                onClick={() => { setPlusMenuOpen((v) => !v); setProjectSubOpen(false); }}
                disabled={isUploading}
                className="flex items-center justify-center transition-colors"
                style={{ width: 28, height: 28, borderRadius: '50%', background: plusMenuOpen ? '#E5E2DC' : '#F5F3EE', border: '1px solid #E5E2DC', flexShrink: 0 }}
                title="Attach or assign"
              >
                {isUploading
                  ? <Loader2 className="w-3 h-3 animate-spin" style={{ color: '#B5B0A8' }} />
                  : <Plus className="w-3 h-3" style={{ color: '#8C8578' }} />
                }
              </button>
              {plusMenuOpen && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: 0,
                    marginBottom: 6,
                    background: '#FFFFFF',
                    border: '1px solid #E5E2DC',
                    borderRadius: 10,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                    minWidth: 190,
                    zIndex: 100,
                    overflow: 'visible',
                  }}
                >
                  <button
                    data-testid="plus-menu-files"
                    onClick={() => { setPlusMenuOpen(false); fileInputRef.current?.click(); }}
                    className="flex items-center gap-2 w-full transition-colors"
                    style={{ padding: '9px 14px', fontSize: 13, color: '#1A1A1A', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', borderRadius: '10px 10px 0 0' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                  >
                    <ImageIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#8C8578' }} />
                    Add files or photos
                  </button>
                  <div style={{ height: 1, background: '#F0EDE8', margin: '0 10px' }} />
                  <div
                    style={{ position: 'relative' }}
                    onMouseEnter={() => { if (subMenuCloseTimer.current) { clearTimeout(subMenuCloseTimer.current); subMenuCloseTimer.current = null; } setProjectSubOpen(true); }}
                    onMouseLeave={() => { subMenuCloseTimer.current = setTimeout(() => setProjectSubOpen(false), 200); }}
                  >
                    {/* v3.87 — a canvas's brand can't change after start: once
                        attached, show WHICH brand and grey the item out. */}
                    <button
                      data-testid="plus-menu-project"
                      disabled={!!activeBrand}
                      className="flex items-center gap-2 w-full transition-colors"
                      style={{ padding: '9px 14px', fontSize: 13, color: activeBrand ? '#B5B0A8' : '#1A1A1A', background: 'transparent', border: 'none', cursor: activeBrand ? 'default' : 'pointer', textAlign: 'left', borderRadius: '0 0 10px 10px' }}
                      onMouseEnter={(e) => { if (!activeBrand) (e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                    >
                      <FolderInput className="w-3.5 h-3.5 flex-shrink-0" style={{ color: activeBrand ? '#C5C1BB' : '#8C8578' }} />
                      <span className="flex-1">{activeBrand ? `Brand: ${activeBrand.name}` : 'Add to brand'}</span>
                      {!activeBrand && <span style={{ color: '#B5B0A8', fontSize: 14, lineHeight: 1 }}>›</span>}
                    </button>
                    {projectSubOpen && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: '100%',
                          marginLeft: 4,
                          background: '#FFFFFF',
                          border: '1px solid #E5E2DC',
                          borderRadius: 10,
                          boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                          minWidth: 160,
                          maxHeight: 200,
                          overflowY: 'auto',
                          zIndex: 101,
                        }}
                        onMouseEnter={() => { if (subMenuCloseTimer.current) { clearTimeout(subMenuCloseTimer.current); subMenuCloseTimer.current = null; } setProjectSubOpen(true); }}
                        onMouseLeave={() => { subMenuCloseTimer.current = setTimeout(() => setProjectSubOpen(false), 200); }}
                      >
                        {folders.length === 0 ? (
                          <p style={{ padding: '8px 14px', fontSize: 12, color: '#B5B0A8' }}>No projects yet</p>
                        ) : (
                          folders.map((f) => (
                            <button
                              key={f.id}
                              data-testid={`plus-menu-folder-${f.id}`}
                              onClick={() => handleAssignToFolder(f.id)}
                              disabled={assigningProject}
                              className="flex items-center gap-2 w-full transition-colors"
                              style={{ padding: '7px 14px', fontSize: 12, color: '#3D3A36', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F0EDE8'; }}
                              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                            >
                              {f.name}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            {/* 🔗 link */}
            {activeBrand && (
              <div
                data-testid="brand-context-indicator"
                className="flex items-center gap-1"
                style={{
                  fontSize: 12,
                  color: '#8C8578',
                  flexShrink: 0,
                  userSelect: 'none',
                  pointerEvents: 'none',
                }}
              >
                <FolderInput className="w-3 h-3" style={{ color: '#8C8578' }} />
                <span title={activeBrand.name}>{activeBrand.name}</span>
              </div>
            )}
            {/* v6.22 — this task was created in Video mode: image playbooks
                are disabled server-side; show which pipeline this chat runs. */}
            {activeTaskMedium === 'video' && (
              <div data-testid="video-mode-indicator" className="flex items-center gap-1" style={{ fontSize: 12, color: '#8C8578', flexShrink: 0, userSelect: 'none', pointerEvents: 'none' }}>
                <Clapperboard className="w-3 h-3" style={{ color: '#8C8578' }} />
                <span>Video</span>
              </div>
            )}
            <BrandIdentityToggle />
            </div>
            <div className="composer-toolbar-settings">
            {/* v7.35 — video mode swaps the image pickers for the video dial
                set (model · standard/turbo · ratio · resolution · time ·
                audio · video ref). */}
            {activeTaskMedium === 'video' ? (
              <VideoComposerDials />
            ) : (<>
            {/* v7.16 — image model selector (also drives video's frame/card renders) */}
            <div className="composer-toolbar-group">
            <ImageModelPicker value={activeImageModel} open={modelPopoverOpen} setOpen={setModelPopoverOpen} btnRef={modelBtnRef} onPick={pickImageModel} />
            <ImageTierPicker family={activeImageModel} value={activeImageTier} open={tierPopoverOpen} setOpen={setTierPopoverOpen} btnRef={tierBtnRef} onPick={pickImageTier} />
            </div>
            <div className="composer-toolbar-group">
            <AdvancedButton open={advancedOpen} filter={activeAdFilter} onToggle={() => setAdvancedOpen((v) => !v)} />
            {/* Aspect ratio selector */}
            <div ref={ratioBtnRef} style={{ position: 'relative', flexShrink: 0 }}>
              <button
                data-testid="ratio-selector-btn"
                onClick={() => setRatioPopoverOpen((v) => !v)}
                className="flex items-center gap-1 transition-colors"
                style={{
                  height: 28,
                  padding: '0 8px',
                  borderRadius: 8,
                  background: ratioPopoverOpen ? '#E5E2DC' : '#F5F3EE',
                  border: '1px solid #E5E2DC',
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#3D3A36',
                  cursor: 'pointer',
                }}
                title="Aspect ratio"
              >
                {selectedAspectRatios.length > 1 ? (selectedAspectRatios.length === 2 ? selectedAspectRatios.join(' + ') : `${selectedAspectRatios.length} ratios`) : selectedAspectRatio}
                <ChevronDown className="w-2.5 h-2.5" style={{ color: '#8C8578' }} />
              </button>
              {ratioPopoverOpen && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    right: 0,
                    marginBottom: 6,
                    background: '#FFFFFF',
                    border: '1px solid #E5E2DC',
                    borderRadius: 10,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                    minWidth: 210,
                    zIndex: 100,
                    overflow: 'hidden',
                  }}
                >
                  {ASPECT_RATIO_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      data-testid={`ratio-option-${opt.value.replace(':', '-')}`}
                      onClick={() => toggleSelectedAspectRatio(opt.value)}
                      className="flex items-center gap-2.5 w-full transition-colors"
                      style={{
                        padding: '9px 14px',
                        fontSize: 12,
                        color: '#1A1A1A',
                        background: selectedAspectRatios.includes(opt.value) ? '#F5F3EE' : 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = selectedAspectRatios.includes(opt.value) ? '#F5F3EE' : 'transparent'; }}
                    >
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: 3,
                          border: `1.5px solid ${selectedAspectRatios.includes(opt.value) ? '#1A1A1A' : '#C5C2BC'}`,
                          background: selectedAspectRatios.includes(opt.value) ? '#1A1A1A' : 'transparent',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {selectedAspectRatios.includes(opt.value) && (
                          <div style={{ width: 6, height: 6, borderRadius: 1, background: '#FFFFFF' }} />
                        )}
                      </div>
                      <div>
                        <span style={{ fontWeight: 600, marginRight: 6 }}>{opt.label}</span>
                        <span style={{ color: '#8C8578' }}>{opt.desc}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            </div>
            </>)}
            </div>
            <div className="composer-toolbar-actions">
            {/* Low-credit indicator */}
            {showLowCredits && (
              <div
                data-testid="chat-low-credits"
                title={`You have ${usageBalance!.toLocaleString()} credits remaining.`}
                style={{ fontSize: 11, color: '#B45309', marginRight: 4, whiteSpace: 'nowrap' }}
              >
                ⚠ Low on credits
              </div>
            )}
            {/* ↑ send */}
            <button
              data-testid="canvas-chat-send"
              onClick={handleSend}
              disabled={!input.trim() && chatAttachments.length === 0 && !attachedFile}
              className="flex items-center justify-center transition-colors"
              style={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                background: (input.trim() || chatAttachments.length > 0 || !!attachedFile) ? '#1A1A1A' : '#E5E2DC',
                flexShrink: 0,
              }}
            >
              <ArrowUp style={{ width: 14, height: 14, color: (input.trim() || chatAttachments.length > 0 || !!attachedFile) ? '#FFFFFF' : '#B5B0A8' }} />
            </button>
          </div>
          </div>
          {/* v7.37 — Advanced angle/style panel, the input's full width */}
          {advancedOpen && activeTaskMedium === 'image' && (
            <AdvancedPanel filter={activeAdFilter} onChange={changeAdFilter} />
          )}
        </div>
      </div>
    </div>
  );

  if (isLoading && !projectLoading) {
    return <ChatSkeleton />;
  }

  if (fullWidth && isEmpty && !persistenceProjectId) {
    /* Empty chat-only state: centered big input */
    return (
      <div className="flex flex-col h-full overflow-hidden" style={{ background: '#F8F8F7', fontFamily: 'Manrope, sans-serif' }}>
        {tourRequest && brandsLoaded && (
          <FirstRunTour
            mode={tourRequest}
            userId={tourAuthUser?.id}
            hasBrand={brands.length > 0}
            maxBrands={tourAuthUser?.entitlements?.maxBrands ?? null}
            onPrefill={(text) => {
              cancelPlaceholderAnimation();
              setInput(text);
              // v7.45 — attach the brand the wizard created, so the prompt is
              // ready to send. Without this the first send hits the brand gate
              // ("Add a brand first"), which is a dead end right after a tour
              // that just showed them their brand.
              if (!activeBrandId && brands[0]) setActiveBrandId(brands[0].id);
              window.setTimeout(() => inputRef.current?.focus(), 0);
            }}
            onAddBrand={() => setActiveView('brands')}
            onClose={() => setTourRequest(null)}
          />
        )}
        <style>{`
          @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
          @keyframes indeterminate { 0% { transform: translateX(-100%); } 100% { transform: translateX(400%); } }
          @keyframes fadeInImg { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: scale(1); } }
          @keyframes thinkingDot { 0%, 100% { opacity: 0.2; } 50% { opacity: 1; } }
          @keyframes thinkingFade { 0% { opacity: 0; } 100% { opacity: 1; } }
          [data-testid="canvas-chat-input"]::placeholder { color: #B5B0A8; }
        `}</style>

        {/* Connection error banners — only show while a turn is actually
            in flight. After the run completes (typing flips false), the
            banner is misleading even if sseError was set by a transient
            mid-stream agent_error event. The success-path cleanup in
            canvas-store also clears sseError, but this gate is defense
            in depth in case some other path leaves the flag set. */}
        {sseError && isTyping && (
          <div className="flex-shrink-0 bg-red-50 border-b border-red-100 px-4 py-2 flex items-center gap-2">
            <WifiOff className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
            <span className="text-xs text-red-600 flex-1">Connection lost. Send a new message to reconnect.</span>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col items-center justify-center px-6" style={{ position: 'relative', minHeight: '80vh' }}>
            <div style={{ width: '100%', maxWidth: 740 }}>
            {/* Welcome text */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <img src="/admoji-logo-new.jpg" style={{ width: 56, height: 56, borderRadius: 14, objectFit: 'cover' }} alt="Admoji" />
              </div>
              <p style={{ fontSize: 20, fontWeight: 600, color: '#1A1A1A', marginBottom: 6 }}>What would you like to work on?</p>
              {activeBrand ? (
                <p style={{ fontSize: 13, color: '#8C8578' }}>
                  Your AI ad strategist for <span style={{ fontWeight: 600 }}>{activeBrand.name}</span>
                </p>
              ) : (
                <p style={{ fontSize: 13, color: '#8C8578' }}>Your AI ad strategist</p>
              )}
            </div>

            {/* chatAttachments thumbnails (multi-image) */}
            {chatAttachments.length > 0 && (
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {chatAttachments.map((att) => (
                  <div
                    key={att.url}
                    data-testid={`chat-attachment-${att.url}`}
                    style={{ position: 'relative', flexShrink: 0 }}
                  >
                    <img
                      src={att.url}
                      alt={att.name}
                      style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8, border: '1px solid #E5E2DC', display: 'block' }}
                      onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }}
                    />
                    <button
                      data-testid={`remove-attachment-${att.url}`}
                      onClick={() => removeChatAttachment(att.url)}
                      style={{
                        position: 'absolute',
                        top: -5,
                        right: -5,
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        background: '#1A1A1A',
                        border: 'none',
                        color: '#FFFFFF',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                      }}
                    >
                      <X style={{ width: 10, height: 10 }} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Attached file preview */}
            {attachedFile && (
              <div className="flex items-center gap-2 mb-2 px-2 py-1.5 rounded-lg" style={{ background: '#F5F3EE', border: '1px solid #E5E2DC' }}>
                <img src={attachedFile.url} alt={attachedFile.name}
                  className="w-8 h-8 rounded object-cover flex-shrink-0"
                  style={{ border: '1px solid #E5E2DC' }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                <span className="text-xs truncate flex-1" style={{ color: '#8C8578' }}>{attachedFile.name}</span>
                <button onClick={() => setAttachedFile(null)} className="flex-shrink-0 transition-colors" style={{ color: '#B5B0A8' }}
                  onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.color = '#1A1A1A')}
                  onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.color = '#B5B0A8')}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {/* Big input box (v6.62 — wrapped so the no-brand gate can cover it) */}
            <div style={{ position: 'relative' }}>
            {/* v6.62 — brand-first gate (Dan's spec): no brands in the system
                yet → the composer is covered until one exists. Tasks are
                brand-scoped, so there is nothing useful to type before that.
                Gated on query SUCCESS: a failed /api/brands fetch (retry:false
                app default) must not lock out a user who has brands. */}
            {brandsLoaded && brands.length === 0 && (
              <div
                data-testid="no-brand-gate"
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 20,
                  borderRadius: 16,
                  background: 'rgba(248,248,247,0.78)',
                  backdropFilter: 'blur(3px)',
                  WebkitBackdropFilter: 'blur(3px)',
                  border: '1px solid #E5E2DC',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                }}
              >
                <span style={{ fontSize: 13, color: '#3D3A36', fontWeight: 500 }}>
                  Set up your first brand to start creating
                </span>
                <button
                  data-testid="no-brand-gate-add-brand"
                  onClick={() => setActiveView('brands')}
                  className="flex items-center gap-1.5 transition-colors"
                  style={{
                    padding: '9px 18px',
                    borderRadius: 10,
                    background: '#1A1A1A',
                    color: '#FFFFFF',
                    border: 'none',
                    fontSize: 13.5,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#3D3A36'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#1A1A1A'; }}
                >
                  Add Brand <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            <div
              className="flex flex-col gap-2 transition-all"
              style={{
                background: '#FFFFFF',
                border: '1px solid #DCDCDC',
                borderRadius: 16,
                minHeight: 120,
                padding: '16px 18px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = '#A8A49E')}
              onBlur={(e) => (e.currentTarget.style.borderColor = '#DCDCDC')}
            >
              {/* v6.22 — Image / Video tabs (Dan's spec): hard-codes which
                  pipeline this task runs. Image = current ad playbooks;
                  Video = video mode (image playbooks disabled server-side,
                  text-to-video playbook coming). Default: Image. */}
              <div className="flex items-center" style={{ borderBottom: '1px solid #F0EDE8', margin: '-6px -18px 4px', padding: '0 12px' }}>
                {([
                  { key: 'image' as const, label: 'Image', Icon: ImageIcon },
                  { key: 'video' as const, label: 'Video', Icon: Clapperboard },
                ]).map(({ key, label, Icon }) => {
                  const active = generationMedium === key;
                  // v7.04 — video is admin-only while it's being built: the
                  // tab reads "Video Coming Soon…" and does nothing for
                  // everyone else (the server 403s the routes regardless).
                  const locked = key === 'video' && !videoEnabled;
                  return (
                    <button
                      key={key}
                      data-testid={`media-tab-${key}`}
                      disabled={locked}
                      title={locked ? videoGateCopy.long : undefined}
                      onClick={() => {
                        if (locked) return;
                        setGenerationMedium(key);
                        // Video mode has no image-angle builds: clear an
                        // attached angle set so the Build Campaign submit
                        // path (image full-funnel) can't fire from video.
                        if (key === 'video') setSelectedAngleSet(null);
                      }}
                      className="flex items-center gap-1.5 transition-colors"
                      style={{
                        padding: '7px 12px 9px',
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: locked ? '#C4C0B8' : active ? '#1A1A1A' : '#8C8578',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: active && !locked ? '2px solid #1A1A1A' : '2px solid transparent',
                        marginBottom: -1,
                        cursor: locked ? 'default' : 'pointer',
                      }}
                      onMouseEnter={(e) => { if (!active && !locked) (e.currentTarget as HTMLButtonElement).style.color = '#3D3A36'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = locked ? '#C4C0B8' : active ? '#1A1A1A' : '#8C8578'; }}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {label}
                      {locked && (
                        <span style={{ fontSize: 10.5, fontWeight: 500, color: '#B5B0A8', fontStyle: 'italic' }}>
                          {videoGateCopy.short}…
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {selectedAngleSet ? (
                /* v4.32 — Ad Angles selected: composer collapses to a slim
                   non-typable preview; submit becomes Build Campaign. */
                <div data-testid="home-angleset-chip" className="flex items-center gap-2" style={{ padding: '10px 12px', background: '#F5F3EE', border: '1px solid #E5E2DC', borderRadius: 10 }}>
                  <Sparkles className="w-3.5 h-3.5" style={{ color: '#8f2740', flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#1A1A1A', whiteSpace: 'nowrap' }}>Ad Angles</span>
                  <span style={{ fontSize: 13, color: '#3D3A36', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedAngleSet.name}</span>
                  <span style={{ fontSize: 11, color: '#8C8578', whiteSpace: 'nowrap' }}>{selectedAngleSet.angleCount} angles · Full Funnel</span>
                  {angleBuildError && (
                    <span style={{ fontSize: 11, color: '#dc2626', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{angleBuildError}</span>
                  )}
                  <button
                    data-testid="home-angleset-clear"
                    onClick={() => setSelectedAngleSet(null)}
                    aria-label="Clear selected angle set"
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#8C8578', cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: 2 }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
              <textarea
                ref={inputRef}
                data-testid="canvas-chat-input"
                value={input}
                onChange={(e) => { cancelPlaceholderAnimation(); setInput(e.target.value); }}
                onKeyDown={handleKeyDown}
                onFocus={cancelPlaceholderAnimation}
                placeholder={
                  activeBrand ? `Ask anything about ${activeBrand.name}…`
                  : placeholderText
                }
                rows={3}
                className="w-full resize-none bg-transparent outline-none leading-relaxed max-h-48"
                style={{ fontSize: 15, color: '#1A1A1A', minHeight: '4rem', cursor: 'text' }}
                autoFocus
              />
              )}
              {uploadError && (
                <div style={{ fontSize: 11, color: '#DC2626', padding: '2px 4px 6px' }}>{uploadError}</div>
              )}
              {brandGateError && (
                <div data-testid="brand-gate-error-home" style={{ fontSize: 11, color: '#DC2626', padding: '2px 4px 6px' }}>{brandGateError}</div>
              )}
              {/* Same responsive control groups as the docked composer. */}
              <div className="composer-toolbar-container">
              <div className="composer-toolbar" data-testid="composer-toolbar">
            <div className="composer-toolbar-context">
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                {/* + menu */}
                <div ref={plusMenuRef} style={{ position: 'relative' }}>
                  <button
                    data-testid="input-attach"
                    onClick={() => { setPlusMenuOpen((v) => !v); setProjectSubOpen(false); }}
                    disabled={isUploading}
                    className="flex items-center justify-center transition-colors"
                    style={{ width: 28, height: 28, borderRadius: '50%', background: plusMenuOpen ? '#E5E2DC' : '#F5F3EE', border: '1px solid #E5E2DC', flexShrink: 0 }}
                    title="Attach or assign"
                  >
                    {isUploading
                      ? <Loader2 className="w-3 h-3 animate-spin" style={{ color: '#B5B0A8' }} />
                      : <Plus className="w-3 h-3" style={{ color: '#8C8578' }} />
                    }
                  </button>
                  {plusMenuOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '100%',
                        left: 0,
                        marginBottom: 6,
                        background: '#FFFFFF',
                        border: '1px solid #E5E2DC',
                        borderRadius: 10,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                        minWidth: 190,
                        zIndex: 100,
                        overflow: 'visible',
                      }}
                    >
                      <button
                        data-testid="plus-menu-files"
                        onClick={() => { setPlusMenuOpen(false); fileInputRef.current?.click(); }}
                        className="flex items-center gap-2 w-full transition-colors"
                        style={{ padding: '9px 14px', fontSize: 13, color: '#1A1A1A', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', borderRadius: '10px 10px 0 0' }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                      >
                        <ImageIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#8C8578' }} />
                        Add files or photos
                      </button>
                      <div style={{ height: 1, background: '#F0EDE8', margin: '0 10px' }} />
                      <div
                        style={{ position: 'relative' }}
                        onMouseEnter={() => { if (subMenuCloseTimer.current) { clearTimeout(subMenuCloseTimer.current); subMenuCloseTimer.current = null; } setProjectSubOpen(true); }}
                        onMouseLeave={() => { subMenuCloseTimer.current = setTimeout(() => setProjectSubOpen(false), 200); }}
                      >
                        {/* v3.87 — brand locked after canvas start (see compact input) */}
                        <button
                          data-testid="plus-menu-project"
                          disabled={!!activeBrand}
                          className="flex items-center gap-2 w-full transition-colors"
                          style={{ padding: '9px 14px', fontSize: 13, color: activeBrand ? '#B5B0A8' : '#1A1A1A', background: 'transparent', border: 'none', cursor: activeBrand ? 'default' : 'pointer', textAlign: 'left', borderRadius: '0 0 10px 10px' }}
                          onMouseEnter={(e) => { if (!activeBrand) (e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                        >
                          <FolderInput className="w-3.5 h-3.5 flex-shrink-0" style={{ color: activeBrand ? '#C5C1BB' : '#8C8578' }} />
                          <span className="flex-1">{activeBrand ? `Brand: ${activeBrand.name}` : 'Add to brand'}</span>
                          {!activeBrand && <span style={{ color: '#B5B0A8', fontSize: 14, lineHeight: 1 }}>›</span>}
                        </button>
                        {projectSubOpen && (
                          <div
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: '100%',
                              marginLeft: 4,
                              background: '#FFFFFF',
                              border: '1px solid #E5E2DC',
                              borderRadius: 10,
                              boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                              minWidth: 160,
                              maxHeight: 200,
                              overflowY: 'auto',
                              zIndex: 101,
                            }}
                            onMouseEnter={() => { if (subMenuCloseTimer.current) { clearTimeout(subMenuCloseTimer.current); subMenuCloseTimer.current = null; } setProjectSubOpen(true); }}
                            onMouseLeave={() => { subMenuCloseTimer.current = setTimeout(() => setProjectSubOpen(false), 200); }}
                          >
                            {folders.length === 0 ? (
                              <p style={{ padding: '8px 14px', fontSize: 12, color: '#B5B0A8' }}>No projects yet</p>
                            ) : (
                              folders.map((f) => (
                                <button
                                  key={f.id}
                                  data-testid={`plus-menu-folder-${f.id}`}
                                  onClick={() => handleAssignToFolder(f.id)}
                                  disabled={assigningProject}
                                  className="flex items-center gap-2 w-full transition-colors"
                                  style={{ padding: '7px 14px', fontSize: 12, color: '#3D3A36', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F0EDE8'; }}
                                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                                >
                                  {f.name}
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                      {/* v4.32 — Ad Angles: hover flyout of buildable copy-set folders.
                          v6.22 — hidden in video mode: an angle-set build is the
                          image full-funnel pipeline. */}
                      {generationMedium !== 'video' && (<>
                      <div style={{ height: 1, background: '#F0EDE8', margin: '0 10px' }} />
                      <div
                        style={{ position: 'relative' }}
                        onMouseEnter={() => { if (anglesSubCloseTimer.current) { clearTimeout(anglesSubCloseTimer.current); anglesSubCloseTimer.current = null; } setAnglesSubOpen(true); }}
                        onMouseLeave={() => { anglesSubCloseTimer.current = setTimeout(() => setAnglesSubOpen(false), 200); }}
                      >
                        <button
                          data-testid="plus-menu-adangles"
                          className="flex items-center gap-2 w-full transition-colors"
                          style={{ padding: '9px 14px', fontSize: 13, color: '#3D3A36', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                        >
                          <Sparkles className="w-3.5 h-3.5" style={{ color: '#8C8578' }} />
                          <span className="flex-1">Ad Angles</span>
                          <span style={{ color: '#B5B0A8', fontSize: 14, lineHeight: 1 }}>›</span>
                        </button>
                        {anglesSubOpen && (
                          <div
                            style={{ position: 'absolute', top: 0, left: '100%', marginLeft: 4, background: '#FFFFFF', border: '1px solid #E5E2DC', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 220, maxHeight: 220, overflowY: 'auto', zIndex: 101 }}
                            onMouseEnter={() => { if (anglesSubCloseTimer.current) { clearTimeout(anglesSubCloseTimer.current); anglesSubCloseTimer.current = null; } }}
                            onMouseLeave={() => { anglesSubCloseTimer.current = setTimeout(() => setAnglesSubOpen(false), 200); }}
                          >
                            {!activeBrand ? (
                              <p style={{ padding: '8px 14px', fontSize: 12, color: '#B5B0A8' }}>Attach a brand first</p>
                            ) : buildableAngleSets.length === 0 ? (
                              <p style={{ padding: '8px 14px', fontSize: 12, color: '#B5B0A8' }}>No angle folders yet</p>
                            ) : (
                              buildableAngleSets.map((st) => (
                                <button
                                  key={st.id}
                                  data-testid={`plus-menu-angleset-${st.id}`}
                                  onClick={() => { setSelectedAngleSet({ id: st.id, name: st.name, angleCount: st.angleCount }); setPlusMenuOpen(false); setAnglesSubOpen(false); setInput(''); }}
                                  className="flex flex-col gap-0.5 w-full transition-colors"
                                  style={{ padding: '8px 14px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F0EDE8'; }}
                                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                                >
                                  <span style={{ fontSize: 12.5, fontWeight: 600, color: '#3D3A36', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: 260 }}>{st.name}</span>
                                  <span style={{ fontSize: 11, color: '#8C8578' }}>{st.angleCount} angles</span>
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                      </>)}
                    </div>
                  )}
                </div>
                {/* Folder context indicator — shown when chat is inside a project folder */}
                {isInsideProject && activeProject?.name && (
                  <button
                    data-testid="folder-context-icon"
                    title={activeProject.name}
                    className="flex items-center justify-center flex-shrink-0"
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 7,
                      background: '#EBF3FB',
                      border: '1px solid #C9DFF0',
                      cursor: 'default',
                    }}
                  >
                    <Folder className="w-3.5 h-3.5" style={{ color: '#4A90C4' }} />
                  </button>
                )}
                {activeBrand && (
                  <div
                    data-testid="brand-context-indicator"
                    className="flex items-center gap-1"
                    style={{
                      fontSize: 12,
                      color: '#8C8578',
                      flexShrink: 0,
                      userSelect: 'none',
                      pointerEvents: 'none',
                    }}
                  >
                    <FolderInput className="w-3 h-3" style={{ color: '#8C8578' }} />
                    <span title={activeBrand.name}>{activeBrand.name}</span>
                  </div>
                )}
                <BrandIdentityToggle />
                </div>
                <div className="composer-toolbar-settings">
                {/* v7.35 — Video tab shows the video dial set instead of the
                    image pickers. */}
                {activeTaskMedium === 'video' ? (
                  <VideoComposerDials />
                ) : (<>
                {/* v7.16 — image model selector (also drives video's frame/card renders) */}
                <div className="composer-toolbar-group">
            <ImageModelPicker value={activeImageModel} open={modelPopoverOpen} setOpen={setModelPopoverOpen} btnRef={modelBtnRef} onPick={pickImageModel} />
            <ImageTierPicker family={activeImageModel} value={activeImageTier} open={tierPopoverOpen} setOpen={setTierPopoverOpen} btnRef={tierBtnRef} onPick={pickImageTier} />
            </div>
            <div className="composer-toolbar-group">
            {/* v7.37 — hidden while an angle set is selected: Build Campaign renders THAT set's fixed concepts, so the filter would be a lie here */}
            {!selectedAngleSet && <AdvancedButton open={advancedOpen} filter={activeAdFilter} onToggle={() => setAdvancedOpen((v) => !v)} />}
                {/* Aspect ratio selector */}
                <div ref={ratioBtnRef} style={{ position: 'relative', flexShrink: 0 }}>
                  <button
                    data-testid="ratio-selector-btn"
                    onClick={() => setRatioPopoverOpen((v) => !v)}
                    className="flex items-center gap-1 transition-colors"
                    style={{
                      height: 28,
                      padding: '0 8px',
                      borderRadius: 8,
                      background: ratioPopoverOpen ? '#E5E2DC' : '#F5F3EE',
                      border: '1px solid #E5E2DC',
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#3D3A36',
                      cursor: 'pointer',
                    }}
                    title="Aspect ratio"
                  >
                    {selectedAspectRatios.length > 1 ? (selectedAspectRatios.length === 2 ? selectedAspectRatios.join(' + ') : `${selectedAspectRatios.length} ratios`) : selectedAspectRatio}
                    <ChevronDown className="w-2.5 h-2.5" style={{ color: '#8C8578' }} />
                  </button>
                  {ratioPopoverOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '100%',
                        right: 0,
                        marginBottom: 6,
                        background: '#FFFFFF',
                        border: '1px solid #E5E2DC',
                        borderRadius: 10,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                        minWidth: 210,
                        zIndex: 100,
                        overflow: 'hidden',
                      }}
                    >
                      {ASPECT_RATIO_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          data-testid={`ratio-option-${opt.value.replace(':', '-')}`}
                          onClick={() => toggleSelectedAspectRatio(opt.value)}
                          className="flex items-center gap-2.5 w-full transition-colors"
                          style={{
                            padding: '9px 14px',
                            fontSize: 12,
                            color: '#1A1A1A',
                            background: selectedAspectRatios.includes(opt.value) ? '#F5F3EE' : 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = selectedAspectRatios.includes(opt.value) ? '#F5F3EE' : 'transparent'; }}
                        >
                          <div
                            style={{
                              width: 20,
                              height: 20,
                              borderRadius: 3,
                              border: `1.5px solid ${selectedAspectRatios.includes(opt.value) ? '#1A1A1A' : '#C5C2BC'}`,
                              background: selectedAspectRatios.includes(opt.value) ? '#1A1A1A' : 'transparent',
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {selectedAspectRatios.includes(opt.value) && (
                              <div style={{ width: 6, height: 6, borderRadius: 1, background: '#FFFFFF' }} />
                            )}
                          </div>
                          <div>
                            <span style={{ fontWeight: 600, marginRight: 6 }}>{opt.label}</span>
                            <span style={{ color: '#8C8578' }}>{opt.desc}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                </div>
            </>)}
            </div>
            <div className="composer-toolbar-actions">
                {showLowCredits && (
                  <div
                    data-testid="chat-low-credits"
                    title={`You have ${usageBalance!.toLocaleString()} credits remaining.`}
                    style={{ fontSize: 11, color: '#B45309', marginRight: 4, whiteSpace: 'nowrap' }}
                  >
                    ⚠ Low on credits
                  </div>
                )}
                {selectedAngleSet ? (
                  <>
                    <button
                      data-testid="home-build-campaign"
                      onClick={() => { void startHomeAngleBuild(); }}
                      disabled={angleBuildStarting}
                      className="flex items-center justify-center transition-colors"
                      style={{ height: 30, padding: '0 14px', borderRadius: 999, background: '#8f2740', color: '#fff', fontSize: 12.5, fontWeight: 700, border: 'none', cursor: angleBuildStarting ? 'wait' : 'pointer', flexShrink: 0, opacity: angleBuildStarting ? 0.7 : 1 }}
                    >
                      {angleBuildStarting ? 'Starting…' : 'Build Campaign'}
                    </button>

                  </>
                ) : (
                <button
                  data-testid="canvas-chat-send"
                  onClick={handleSend}
                  disabled={!input.trim() && chatAttachments.length === 0 && !attachedFile}
                  className="flex items-center justify-center transition-colors"
                  style={{ width: 30, height: 30, borderRadius: '50%', background: (input.trim() || chatAttachments.length > 0 || !!attachedFile) ? '#1A1A1A' : '#E5E2DC', flexShrink: 0 }}
                >
                  <ArrowUp style={{ width: 14, height: 14, color: (input.trim() || chatAttachments.length > 0 || !!attachedFile) ? '#FFFFFF' : '#B5B0A8' }} />
                </button>
                )}
              </div>
          </div>
              </div>
              {/* v7.37 — Advanced angle/style panel, the input's full width */}
              {advancedOpen && activeTaskMedium === 'image' && !selectedAngleSet && (
                <AdvancedPanel filter={activeAdFilter} onChange={changeAdFilter} />
              )}
            </div>
            </div>

            {/* v3.91 — quick-start shortcut buttons under the input. Two sets:
                setup actions for a fresh account (no brands yet) vs navigation
                once they're set up. Hollow pills, matching the app style. */}
            {(() => {
              const isSetUp = brands.length > 0;
              const chips: Array<{ label: string; onClick: () => void; plus?: boolean }> = isSetUp
                ? [
                    { label: 'Brands & Products', onClick: () => setActiveView('brands') },
                    { label: 'Ad Angles', onClick: () => { setPendingBrandsTab('angles'); setActiveView('brands'); } },
                    { label: 'Winning Ads', onClick: () => setActiveView('ad-library') },
                    { label: 'Analytics', onClick: () => setActiveView('analytics') },
                  ]
                : [
                    { label: 'New Brand', onClick: () => setActiveView('brands'), plus: true },
                    { label: 'Create Ad Angles', onClick: () => { setPendingBrandsTab('angles'); setActiveView('brands'); }, plus: true },
                    { label: 'Winning Ads', onClick: () => setActiveView('ad-library') },
                    { label: 'Connect Meta Account', onClick: () => setMetaSettingsOpen(true), plus: true },
                  ];
              return (
                <div className="mt-4 flex flex-wrap justify-center gap-2" data-testid="welcome-shortcuts">
                  {chips.map((c) => (
                    <button
                      key={c.label}
                      data-testid={`welcome-shortcut-${c.label.replace(/\s+/g, '-').toLowerCase()}`}
                      onClick={c.onClick}
                      className="inline-flex items-center transition-colors"
                      style={{
                        background: 'transparent',
                        border: '1px solid #D1CDC6',
                        borderRadius: 9999,
                        padding: '6px 14px',
                        fontSize: 13,
                        color: '#3D3A36',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE'; (e.currentTarget as HTMLButtonElement).style.borderColor = '#B5B0A8'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.borderColor = '#D1CDC6'; }}
                    >
                      {c.label}
                      {c.plus && <Plus style={{ width: 13, height: 13, marginLeft: 5, color: '#8C8578' }} />}
                    </button>
                  ))}
                </div>
              );
            })()}

            </div>
          </div>
          <DiscoverSections />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden" style={{ background: '#F8F8F7', fontFamily: 'Manrope, sans-serif' }}>
      {/* Keyframe animations injected once */}
      <style>{`
        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        @keyframes indeterminate { 0% { transform: translateX(-100%); } 100% { transform: translateX(400%); } }
        @keyframes fadeInImg { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: scale(1); } }
        @keyframes thinkingDot { 0%, 100% { opacity: 0.2; } 50% { opacity: 1; } }
        @keyframes thinkingFade { 0% { opacity: 0; } 100% { opacity: 1; } }
        [data-testid="canvas-chat-input"]::placeholder { color: #B5B0A8; }
      `}</style>

      {!fullWidth && (
        /* Header — only in narrow mode */
        <div className="flex-shrink-0 flex items-center gap-2" style={{ padding: '14px 20px', background: '#F8F8F7', borderBottom: showHistory ? '1px solid #E5E2DC' : 'none' }}>
          <div className="flex items-center flex-1" style={{ gap: 8 }}>
            <span style={{ fontSize: 16, fontWeight: 600, color: '#1A1A1A' }}>AskMoji</span>
          </div>
          {isInsideProject && (
            <button
              data-testid="button-history-panel"
              onClick={() => setShowHistory((v) => !v)}
              title="History"
              style={{
                width: 28, height: 28, borderRadius: '50%', border: '1px solid #E5E2DC',
                background: showHistory ? '#E5E2DC' : '#F5F3EE',
                display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
              }}
            >
              <History className="w-3.5 h-3.5" style={{ color: '#8C8578' }} />
            </button>
          )}
        </div>
      )}

      {/* History Panel */}
      {showHistory && isInsideProject && (
        <div
          className="flex-shrink-0 overflow-y-auto"
          style={{ maxHeight: 280, background: '#FAFAF9', borderBottom: '1px solid #E5E2DC', padding: '12px 16px' }}
          data-testid="panel-history"
        >
          <div className="flex items-center justify-between mb-3">
            <span style={{ fontSize: 12, fontWeight: 600, color: '#8C8578', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Canvas History
            </span>
            <button
              data-testid="button-history-close"
              onClick={() => setShowHistory(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
            >
              <X className="w-3.5 h-3.5" style={{ color: '#B5B0A8' }} />
            </button>
          </div>
          {historyImages.length === 0 ? (
            <p style={{ fontSize: 12, color: '#B5B0A8', textAlign: 'center', paddingTop: 16, paddingBottom: 8 }}>
              No images generated yet
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {historyImages.map((img) => (
                <button
                  key={img.clientId}
                  data-testid={`history-image-${img.clientId}`}
                  onClick={() => { panToImage(img.clientId); setShowHistory(false); }}
                  title={img.prompt || img.name}
                  style={{
                    width: 56, height: 56, borderRadius: 8, overflow: 'hidden', border: '1px solid #E5E2DC',
                    padding: 0, cursor: 'pointer', flexShrink: 0, background: '#F0EDE8', position: 'relative',
                  }}
                >
                  <img
                    src={img.src}
                    alt={img.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Connection error banners — gated on isTyping (see comment above). */}
      {sseError && isTyping && (
        <div className="flex-shrink-0 bg-red-50 border-b border-red-100 px-4 py-2 flex items-center gap-2">
          <WifiOff className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
          <span className="text-xs text-red-600 flex-1">Connection lost. Send a new message to reconnect.</span>
        </div>
      )}
      {!sseConnected && !sseError && isTyping && (
        <div className="flex-shrink-0 bg-amber-50 border-b border-amber-100 px-4 py-2 flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 text-amber-500 animate-spin flex-shrink-0" />
          <span className="text-xs text-amber-700">Reconnecting…</span>
        </div>
      )}


      {/* Chat breadcrumb — shows brand/task path above messages */}
      {persistenceProjectId && (activeTask?.name || breadcrumbBrand?.name) && (
        <div
          className="flex-shrink-0 px-4"
          style={fullWidth ? { maxWidth: 740, margin: '0 auto', width: '100%', paddingLeft: 16, paddingRight: 16, paddingTop: 8, paddingBottom: 0 } : { paddingTop: 8, paddingBottom: 0 }}
        >
          <div className="flex items-center gap-1" style={{ fontSize: 12, color: '#B5B0A8', lineHeight: 1.4 }}>
            {breadcrumbBrand?.name && (
              <>
                <button
                  data-testid="breadcrumb-folder-link"
                  onClick={() => {
                    useCanvasStore.setState({ activeProjectId: null });
                    const projectIdNow = useCanvasStore.getState().activeProjectId;
                    useCanvasStore.getState()._writeProjectView(projectIdNow, { activeView: 'project' });
                  }}
                  className="transition-colors truncate max-w-[120px]"
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#B5B0A8', fontSize: 12 }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#8C8578'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#B5B0A8'; }}
                  title={breadcrumbBrand.name}
                >
                  {breadcrumbBrand.name}
                </button>
                {activeTask?.name && <span aria-hidden="true" style={{ color: '#D4D0CA', margin: '0 2px' }}>»</span>}
              </>
            )}
            {activeTask?.name && (
              <span
                data-testid="breadcrumb-task-name"
                className="truncate max-w-[180px]"
                title={activeTask.name}
              >
                {activeTask.name}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto py-5 space-y-6 min-h-0"
        style={fullWidth ? { padding: '20px 0' } : { padding: '20px 16px' }}
        onScroll={(e) => {
          const el = e.currentTarget;
          const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
          setUserScrolledUp(!atBottom);
        }}
      >
        {isEmpty && (conversationLoading || projectLoading) && (
          <div className="flex flex-col items-center justify-center h-full gap-3 py-8">
            <div style={{ width: 24, height: 24, border: '2px solid #E5E2DC', borderTopColor: '#B5B0A8', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}
        {/* Empty-chat state — v3.42: the legacy hardcoded suggestion chips
            ("Create 4 ads" / "Remove backgrounds" / ...) are gone. Some
            referenced flows that no longer exist and the list predated the
            playbook system entirely; a fresh project chat now just shows the
            input prompt. */}
        {isEmpty && !conversationLoading && !projectLoading && images.length === 0 && !isTyping && (
          <div className="flex flex-col items-center justify-center h-full gap-3 py-8">
            <p style={{ fontSize: 13, color: '#B5B0A8', textAlign: 'center' }}>
              {activeBrand ? `Ask anything about ${activeBrand.name} — or describe the ads you want to make.` : 'Ask anything — or describe the ads you want to make.'}
            </p>
          </div>
        )}
        {messages.filter((msg) => {
          const c = msg.content || '';
          return !c.includes('paused_at_checkpoint') && !c.includes('[Orchestrator workflow executed]');
        }).map((msg, msgIdx, arr) => {
          const isLastMessage = msgIdx === arr.length - 1;
          const isCompletedAssistant = msg.role === 'assistant' && !msg.isStreaming;
          const precedingRequest = arr.slice(0, msgIdx).reverse().find((entry) => entry.role === 'user')?.content ?? '';
          const followUps = canvasFollowUps(msg, images, precedingRequest);
          const hasFollowUps = isLastMessage && isCompletedAssistant && followUps.length > 0;

          return (
            <div
              key={msg.id}
              style={fullWidth ? { maxWidth: 740, margin: '0 auto', padding: '0 16px' } : {}}
            >
              {msg.role === 'user' ? (
                /* User message — muted bubble, right-anchored with left-aligned text */
                <div className="flex justify-end">
                  <div style={{ maxWidth: '70%' }}>
                    <div data-testid={`user-message-bubble-${msg.id}`} style={{ background: '#EDEBE7', borderRadius: 16, padding: '10px 14px' }}>
                      {msg.thumbnails && msg.thumbnails.length > 0 && (
                        <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          {msg.thumbnails.map((thumbUrl, ti) => (
                            <img
                              key={ti}
                              src={thumbUrl}
                              alt="Attached"
                              data-testid={`msg-thumbnail-${msg.id}-${ti}`}
                              style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8, display: 'block' }}
                            />
                          ))}
                        </div>
                      )}
                      {/* v7.22 — bubble sits right (justify-end above), but the
                          TEXT inside reads left-aligned (Dan: "align left on
                          the right side, like this current chat"). */}
                      <p className="whitespace-pre-wrap" style={{ fontSize: 14, color: '#2e3338', fontFamily: 'Manrope, sans-serif', fontWeight: 500, lineHeight: 1.5, textAlign: 'left' }}>{
                        // v4.29 — "[Attached image: https://…]" is transport
                        // plumbing for the agent; the thumbnail already shows
                        // the attachment, so never render the raw URL.
                        (msg.content || '').replace(/^Make these ads from set\s*(?:#|number\s*)?\d+\s*$/i, 'Make these ads').replace(/\n*\[Attached image:\s*https?:\/\/[^\]]+\]/gi, '').trim()
                      }</p>
                    </div>
                    <div className="text-right mt-1">
                      <span style={{ fontSize: 10, color: '#777b80', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{formatTime(msg.timestamp)}</span>
                    </div>
                  </div>
                </div>
              ) : (
                /* AI message — avatar + name header, then flowing content or thinking indicator */
                <div>
                  {/* Header row */}
                  <div className="flex items-center gap-2 mb-2">
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#777b80' }}>AskMoji</span>
                    {!msg.isStreaming && (
                      <span className="ml-auto" style={{ fontSize: 12, color: '#B5B0A8' }}>{formatTime(msg.timestamp)}</span>
                    )}
                  </div>

                  {/* Message body */}
                  <div>
                    {/* Web research source cards — shown ABOVE text for web research messages */}
                    {msg.webResearchSources !== undefined && msg.webResearchSources.length > 0 && (
                      <WebResearchSources
                        sources={msg.webResearchSources}
                        status={msg.webResearchStatus}
                        hasReport={!!(msg.webResearchReport && msg.webResearchStatus === 'complete')}
                        onOpenReport={() => {
                          const report = msg.webResearchReport || msg.content;
                          if (report) {
                            openDocumentOverlay('Web Research Report', report, persistenceProjectId);
                          }
                        }}
                      />
                    )}
                    {msg.webResearchStatus && (!msg.webResearchSources || msg.webResearchSources.length === 0) && (
                      <WebResearchSources
                        sources={[]}
                        status={msg.webResearchStatus}
                      />
                    )}

                    {/* Agent plan checklist card — only shown when there are no AgentStepCards to avoid duplicating step info */}
                    {msg.agentPlan && !msg.agentTaskGroups?.length && (
                      <AgentPlanCard plan={msg.agentPlan} />
                    )}

                    {/* Tool progress card — unified PlaybookTaskCard-style card for agent task groups */}
                    {msg.isAgentMessage && msg.agentTaskGroups && msg.agentTaskGroups.length > 0 && (
                      <ChatToolProgressCard
                        groups={msg.agentTaskGroups}
                        isStreaming={isWorking && !!msg.isStreaming}
                      />
                    )}

                    {/* Fallback: flat agent action cards (legacy format / no task groups) */}
                    {msg.isAgentMessage && msg.agentCards && msg.agentCards.length > 0 && !msg.agentTaskGroups?.length && (
                      <div className="mb-3">
                        {msg.agentCards.map((card) => (
                          <AgentActionCardView key={card.id} card={card} />
                        ))}
                      </div>
                    )}

                    {/* Worker progress list — shown inline during research phase; hidden when task card is active (task card shows phases instead) */}
                    {!msg.playbookProgress && msg.workerProgress && msg.workerProgress.length > 0 && (msg.isStreaming || msg.workerProgress.some((w) => w.status === 'running')) && (
                      <div
                        data-testid={`worker-progress-list-${msg.id}`}
                        style={{ marginBottom: 8, display: 'flex', flexDirection: 'column', gap: 2 }}
                      >
                        {msg.workerProgress.map((worker) => (
                          <div
                            key={worker.id}
                            data-testid={`worker-progress-entry-${worker.id}`}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#8C8C8A' }}
                          >
                            <span style={{ width: 14, textAlign: 'center', flexShrink: 0 }}>
                              {worker.status === 'running' ? '⟳' : worker.status === 'completed' ? '✓' : '✗'}
                            </span>
                            <span style={{ color: worker.status === 'failed' ? '#B45309' : worker.status === 'completed' ? '#6B7280' : '#8C8C8A' }}>
                              {worker.name}
                              {worker.status === 'completed' && worker.durationMs !== undefined && (
                                <span style={{ marginLeft: 4, opacity: 0.7 }}>({(worker.durationMs / 1000).toFixed(1)}s)</span>
                              )}
                              {worker.status === 'failed' && (
                                <span style={{ marginLeft: 4, opacity: 0.8 }}>(skipped)</span>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Rolling, public progress from recorded events only. */}
                    {(msg.isStreaming || msg.playbookProgress || msg.agentTaskGroups?.length || msg.conceptsRun || msg.webResearchStatus) && (
                      <ThinkingBlock
                        msgId={msg.id}
                        progress={{ conceptDraftProgress: msg.conceptDraftProgress, playbookProgress: msg.playbookProgress, agentTaskGroups: msg.agentTaskGroups, conceptsRun: msg.conceptsRun, webResearchStatus: msg.webResearchStatus }}
                        isStreaming={isWorking && !!msg.isStreaming}
                      />
                    )}

                    {/* Ack text — quick "On it — generating N ads now." line.
                        Rendered in the same italic-grey style as the classifier
                        reasoning so it reads as part of the thinking, not body text. */}
                    {msg.ackText && (
                      <div
                        data-testid={`agent-ack-${msg.id}`}
                        style={{
                          padding: '0 6px',
                          marginBottom: 12,
                          fontSize: 12,
                          color: '#7a7e85',
                          fontStyle: 'normal',
                          lineHeight: 1.5,
                        }}
                      >
                        {msg.ackText}
                      </div>
                    )}

                    {/* Live progress line — streams orchestrator + research-worker status
                        while the canvas is still opening. Shows below the classifier
                        reasoning block so the two coexist. Hidden once real task cards
                        or main content start flowing. */}
                    {isTyping && msg.isStreaming && msg.agentThinking
                      && (!msg.agentTaskGroups || msg.agentTaskGroups.length === 0)
                      && !msg.content && (
                        <div
                          data-testid={`agent-thinking-live-${msg.id}`}
                          style={{
                            padding: '6px 14px',
                            marginBottom: 12,
                            fontSize: 12,
                            color: '#7a7e85',
                            fontStyle: 'normal',
                            lineHeight: 1.5,
                            opacity: 0.9,
                          }}
                        >
                          {msg.agentThinking}
                        </div>
                    )}

                    {/* Thinking state — no inline indicator, bottom status line handles it */}
                    {msg.isStreaming && !msg.content && !msg.agentTaskGroups?.length && (!msg.isAgentMessage || (!msg.agentCards?.length)) ? (
                      <div style={{ minHeight: 20 }} />
                    ) : (
                      /* Flowing text content — ack text appears before task card */
                      <div style={{ fontSize: 14, color: '#3D3A36', lineHeight: 1.7 }}>
                        <AssistantMessageContent
                          content={msg.content}
                          adAccountId={activeAdAccountId ?? undefined}
                          sources={msg.webResearchSources}
                          adDataCache={adDataCache}
                          onOpenSidebar={openSidebar}
                        />
                        {/* Inline blinking cursor removed — BottomStatusLine's
                            rotating "Pondering…/Directing…" verbs already signal
                            activity. Keeping both was visually noisy. */}

                        {/* Image generation progress — hidden when task card is showing (task card handles it) */}
                        {!msg.playbookProgress && msg.isStreaming && msg.imageSlots && msg.imageSlots.some((s) => !s.resolved && !s.failed) && (
                          <GenerationProgressBar slots={msg.imageSlots} />
                        )}

                        {/* Generated image thumbnails — hidden when task card is showing (task card has its own thumbnail grid) */}
                        {!msg.playbookProgress && msg.imageSlots && msg.imageSlots.length > 0 && (
                          <div className="flex flex-nowrap gap-2 mt-3 overflow-x-auto pb-1">
                            {msg.imageSlots.map((slot, i) => (
                              <ChatImageSlotView key={i} slot={slot} aspectRatio={msg.aspectRatio || selectedAspectRatio} />
                            ))}
                          </div>
                        )}

                        {/* Sidebar document summary card — suppressed when the message
                            already has inline AnalyticsCards (analytics tools fired this turn).
                            The analytics cards carry the same info more richly; showing both
                            was creating a duplicate "report" block next to the KPI strip. */}
                        {msg.sidebarDoc && !msg.isStreaming && (!msg.toolResults || msg.toolResults.length === 0) && (
                          <div
                            data-testid={`sidebar-doc-card-${msg.id}`}
                            style={{
                              marginTop: 12,
                              padding: '12px 14px',
                              borderRadius: 10,
                              border: '1px solid #E5E2DC',
                              background: '#FAFAF9',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                            }}
                          >
                            <FileText style={{ width: 16, height: 16, color: '#8C8578', flexShrink: 0 }} />
                            <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: '#3D3A36', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {msg.sidebarDoc.title}
                            </span>
                            <button
                              data-testid={`sidebar-doc-view-btn-${msg.id}`}
                              onClick={() => openSidebar({ type: 'report', title: msg.sidebarDoc!.title, markdown: msg.sidebarDoc!.content })}
                              style={{
                                flexShrink: 0,
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: 12,
                                fontWeight: 600,
                                color: '#1A1A1A',
                                padding: '4px 8px',
                                borderRadius: 6,
                                transition: 'background 0.15s',
                              }}
                              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F0EDE8'; }}
                              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
                            >
                              View full report →
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Playbook Task Card — real-time progress for ad generation (after text content) */}
                    {msg.playbookProgress && (
                      <PlaybookTaskCard
                        progress={msg.playbookProgress}
                        isActive={!!msg.isStreaming}
                        messageId={msg.id}
                        messageImageSlots={msg.imageSlots}
                      />
                    )}

                    {/* Generated image thumbnails fallback — shown for agent-flow messages when
                        imageSlots aren't tracked, ensuring the user sees the image even if the
                        canvas panel isn't open. Hidden when imageSlots is present (avoids duplicate). */}
                    {msg.thumbnails && msg.thumbnails.length > 0 && !(msg.imageSlots && msg.imageSlots.length > 0) && (
                      <div className="flex flex-nowrap gap-2 mt-3 overflow-x-auto pb-1">
                        {msg.thumbnails.map((url, i) => {
                          const { w: tw, h: th } = getRatioDimensions(msg.aspectRatio || selectedAspectRatio);
                          return (
                            <img
                              key={i}
                              src={url}
                              alt="Generated"
                              data-testid={`chat-thumbnail-${msg.id}-${i}`}
                              className="rounded-xl flex-shrink-0 object-cover cursor-pointer hover:scale-[1.02] hover:shadow-md transition-all duration-200"
                              style={{ width: tw, height: th, border: '1px solid #E5E2DC' }}
                              onClick={() => {
                                const match = images.find((img) => img.src === url);
                                if (match) panToImage(match.id);
                              }}
                            />
                          );
                        })}
                      </div>
                    )}

                    {/* Workflow nudge */}
                    {msg.workflowNudge && (
                      <WorkflowNudgeMessage
                        nudge={msg.workflowNudge}
                        onSend={(m) => { if (!isTyping) sendMessage(m); }}
                        onGoDeep={(skeletonType, origMsg) => { if (!isTyping) handleNudgeGoDeep(skeletonType, origMsg); }}
                        onKeepSimple={(origMsg) => { if (!isTyping) handleNudgeKeepSimple(origMsg); }}
                        disabled={isTyping}
                      />
                    )}

                    {/* Workflow confirmation card — shown when status is 'confirming' */}
                    {msg.workflowState?.status === 'confirming' && msg.workflowState.workflowRunId != null && (
                      <WorkflowConfirmationCard
                        wf={msg.workflowState}
                        onConfirm={() => { if (!isTyping) confirmWorkflow(msg.workflowState!.workflowRunId!); }}
                        onKeepSimple={() => {
                          if (isTyping) return;
                          const origMsg = msg.workflowState?.originalMessage;
                          const runId = msg.workflowState!.workflowRunId!;
                          const projectIdNow = useCanvasStore.getState().activeProjectId;
                          useCanvasStore.getState()._writeMessages(projectIdNow, (msgs) =>
                            msgs.map((m) =>
                              m.workflowState?.workflowRunId === runId
                                ? { ...m, workflowState: { ...m.workflowState, status: 'cancelled' as const } }
                                : m
                            )
                          );
                          if (origMsg) handleKeepSimple(runId, origMsg);
                        }}
                        disabled={isTyping}
                      />
                    )}

                    {/* Workflow progress stepper — shown when running/paused/completed/cancelled/error.
                        Suppressed for playbook-based runs (e.g. campaign-v2) which have their own UI. */}
                    {msg.workflowState && msg.workflowState.status !== 'confirming' && msg.workflowState.skeletonKey !== 'campaign-v2' && (
                      <WorkflowProgressStepper wf={msg.workflowState} />
                    )}

                    {/* Suggestion pills — shown when the agent needs guidance on variation type */}
                    {msg.suggestionPills && !isTyping && (
                      <div className="mt-3 space-y-2">
                        <p className="text-xs text-muted-foreground">{msg.suggestionPills.question}</p>
                        <div className="flex flex-wrap gap-2">
                          {msg.suggestionPills.pills.map((pill) => (
                            <button
                              key={pill.value}
                              data-testid={`pill-suggestion-${pill.value}`}
                              onClick={() => sendMessage(pill.value)}
                              className="rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors"
                              title={pill.description}
                            >
                              {pill.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Parallel workflow task cards — shown when multiple tasks execute in parallel */}
                    {msg.workflowTaskCards && msg.workflowTaskCards.length > 0 && (
                      <WorkflowTaskGroup
                        cards={msg.workflowTaskCards}
                        planTitle={msg.workflowPlanTitle}
                      />
                    )}

                    {/* Live thinking feed — shown while workflow is running */}
                    {msg.workflowState?.status === 'running' && msg.workflowThinkingFeed && msg.workflowThinkingFeed.length > 0 && (
                      <details className="my-2 text-sm text-[#777b80]"><summary className="cursor-pointer">Work details</summary><WorkflowThinkingFeed feed={msg.workflowThinkingFeed} /></details>
                    )}

                    {/* Strategy review checkpoint */}
                    {msg.workflowState?.status === 'paused' && msg.workflowState.checkpointType === 'strategy_review' && (
                      <StrategyReviewCard
                        payload={msg.workflowState.checkpointPayload}
                        onSend={(m) => { if (!isTyping) sendMessage(m); }}
                        onCheckpointAction={msg.workflowState.workflowRunId && msg.workflowState.checkpointId
                          ? (action, feedback) => {
                              if (!isTyping) handleCheckpointAction(msg.workflowState!.workflowRunId!, msg.workflowState!.checkpointId!, action, feedback);
                            }
                          : undefined}
                        disabled={isTyping}
                      />
                    )}

                    {/* Gallery review checkpoint */}
                    {msg.workflowState?.status === 'paused' && msg.workflowState.checkpointType === 'gallery_review' && (
                      <GalleryReviewCard
                        payload={msg.workflowState.checkpointPayload}
                        onSend={(m) => { if (!isTyping) sendMessage(m); }}
                        onCheckpointAction={msg.workflowState.workflowRunId && msg.workflowState.checkpointId
                          ? (action, feedback) => {
                              if (!isTyping) handleCheckpointAction(msg.workflowState!.workflowRunId!, msg.workflowState!.checkpointId!, action, feedback);
                            }
                          : undefined}
                        disabled={isTyping}
                      />
                    )}

                    {/* Inline document card for research reports (agent_document event) —
                        suppressed when analytics tool results are present. The analytics
                        cards below carry richer KPI data and the "View Details →" slide-out
                        renders the full breakdown, so the grey auto-generated "report" card
                        is redundant and noisy on performance queries. */}
                    {msg.agentDocument && !msg.isStreaming && (!msg.toolResults || msg.toolResults.length === 0) && (
                      <InlineDocumentCard doc={msg.agentDocument} />
                    )}

                    {/* v5.61 — Video Script card: click opens the script doc
                        panel for the clone draft (Dan: script must live in
                        chat like our other artifacts). */}
                    {msg.videoScriptCard && (
                      <div
                        data-testid="video-script-chat-card"
                        className="rounded-xl overflow-hidden cursor-pointer my-2"
                        style={{ border: '1px solid #E5E2DC', background: '#FAFAF8', maxWidth: 340 }}
                        onClick={() => openSidebar({ type: 'video_script', title: 'Video Script', draftId: msg.videoScriptCard!.draftId })}
                      >
                        <div className="px-4 pt-3 pb-3">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 flex-shrink-0" style={{ color: '#6B7FE6' }} />
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A' }}>{msg.videoScriptCard.title}</span>
                          </div>
                          <div className="flex items-center gap-1" style={{ marginTop: 8 }}>
                            <span style={{ fontSize: 12, color: '#6B7FE6', fontWeight: 500 }}>Open script</span>
                            <ExternalLink className="w-3 h-3" style={{ color: '#6B7FE6' }} />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* v6.77 — Angle Set card: click opens the Angles doc
                        panel with the full copy sheet for every angle. */}
                    {msg.angleSetCard && (
                      <div
                        data-testid="angle-set-chat-card"
                        className="rounded-xl overflow-hidden cursor-pointer my-2"
                        style={{ border: '1px solid #E5E2DC', background: '#FAFAF8', maxWidth: 340 }}
                        onClick={() => openSidebar({ type: 'angle_set', title: msg.angleSetCard!.title, angleSetId: msg.angleSetCard!.setId })}
                      >
                        <div className="px-4 pt-3 pb-3">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 flex-shrink-0" style={{ color: '#9F2C53' }} />
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A' }}>{msg.angleSetCard.title}</span>
                          </div>
                          <div className="flex items-center gap-1" style={{ marginTop: 8 }}>
                            <span style={{ fontSize: 12, color: '#9F2C53', fontWeight: 500 }}>
                              Open angle sheet{typeof msg.angleSetCard.angleCount === 'number' ? ` (${msg.angleSetCard.angleCount} concepts)` : ''}
                            </span>
                            <ExternalLink className="w-3 h-3" style={{ color: '#9F2C53' }} />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* v6.05 — Generate-video action card: posted by the chat
                        edit flow ("changes saved — make the new video"). */}
                    {msg.videoGenerateCard && (
                      <div
                        data-testid="video-generate-chat-card"
                        className="rounded-xl overflow-hidden my-2"
                        style={{ border: '1px solid #E5E2DC', background: '#FAFAF8', maxWidth: 340 }}
                      >
                        <div className="px-4 pt-3 pb-3">
                          <div className="flex items-center gap-2">
                            <Play className="w-4 h-4 flex-shrink-0" style={{ color: '#6B7FE6' }} />
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A' }}>{msg.videoGenerateCard.title}</span>
                          </div>
                          <button
                            data-testid="button-video-generate-card"
                            style={{ marginTop: 10, padding: '7px 14px', borderRadius: 8, border: 'none', background: '#1A1A1A', color: '#FFFFFF', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                            onClick={(e) => {
                              const btn = e.currentTarget as HTMLButtonElement;
                              if (btn.disabled) return;
                              btn.disabled = true;
                              btn.textContent = 'Generating…';
                              btn.style.background = '#D0CECA';
                              const pid = persistenceProjectId;
                              if (pid != null && msg.videoGenerateCard) {
                                void runVideoCloneGenerate(msg.videoGenerateCard.draftId, pid).catch((err) =>
                                  console.error('[canvas-chat] generate from card failed:', err),
                                );
                              }
                            }}
                          >
                            <Play className="w-3.5 h-3.5" /> Generate the video
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Ad cards row — rendered below message text */}
                    {msg.adCards && msg.adCards.length > 0 && (
                      <AdCardsRow
                        cards={msg.adCards}
                        onSelect={(card) => setSelectedAdCard(card)}
                      />
                    )}

                    {/* Analytics cards — rendered below message text when analytics tools were called */}
                    {msg.toolResults && msg.toolResults.length > 0 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                        {msg.toolResults.map((tr, i) => (
                          <AnalyticsCard
                            key={i}
                            result={{ analysis: tr.analysis, renderCard: tr.renderCard, heartbeatLinks: tr.heartbeatLinks }}
                            toolName={tr.toolName}
                          />
                        ))}
                      </div>
                    )}

                    {/* Per-message task steps (inline accordion) — only for image generation intents */}
                    {msg.taskSteps && msg.taskSteps.length > 0 && (msg.intent === 'creation' || msg.intent === 'edit') && (
                      <InlineTaskSteps steps={msg.taskSteps} />
                    )}

                    {/* Research task progress card — shown on last assistant message when a research task is active */}
                    {isLastMessage && activeResearchTask && msg.id.startsWith('m-research-') && (
                      <div ref={taskProgressCardRef}>
                        <TaskProgressCard
                          task={activeResearchTask}
                          isTyping={isTyping}
                          onSendMessage={(suggestion) => {
                            if (!isTyping) sendAgentMessage(suggestion);
                          }}
                          onViewReport={async () => {
                            if (documentOverlayContent) {
                              openDocumentOverlay(documentOverlayTitle || activeResearchTask.plan.title, documentOverlayContent, persistenceProjectId);
                            } else {
                              try {
                                const resp = await fetch(`/api/canvas/tasks/${activeResearchTask.taskId}/document`);
                                if (resp.ok) {
                                  const data = await resp.json();
                                  openDocumentOverlay(data.title, data.document, persistenceProjectId);
                                } else {
                                  console.error('[ViewReport] Server fetch failed:', resp.status, resp.statusText);
                                }
                              } catch (err) {
                                console.error('[ViewReport] Fetch error:', err);
                              }
                            }
                          }}
                        />
                        {/* Conversational AI summary — streams in after steps complete, before document card */}
                        {(msg.researchSummary || (activeResearchTask.documentReady && msg.isStreaming)) && (
                          <p
                            data-testid="text-research-summary"
                            style={{
                              marginTop: 14,
                              fontSize: 14,
                              color: '#3D3A36',
                              lineHeight: 1.65,
                            }}
                          >
                            {msg.researchSummary || ''}
                            {msg.isStreaming && !activeResearchTask.documentReady && (
                              <span className="inline-block w-1.5 h-3.5 animate-pulse ml-0.5 rounded-sm align-middle" style={{ background: '#1A1A1A' }} />
                            )}
                          </p>
                        )}
                        {activeResearchTask.documentReady && (
                          <DocumentCard
                            title={documentOverlayTitle || activeResearchTask.plan.title}
                            preview={activeResearchTask.documentPreview || ''}
                            wordCount={activeResearchTask.documentWordCount}
                            onClick={async () => {
                              const resolvedTitle = documentOverlayTitle || activeResearchTask.plan.title;
                              if (documentOverlayContent) {
                                openDocumentOverlay(resolvedTitle, documentOverlayContent, persistenceProjectId);
                              } else {
                                openDocumentOverlay(resolvedTitle, '', persistenceProjectId);
                                try {
                                  const resp = await fetch(`/api/canvas/tasks/${activeResearchTask.taskId}/document`);
                                  if (resp.ok) {
                                    const data = await resp.json();
                                    openDocumentOverlay(data.title || resolvedTitle, data.document || '', persistenceProjectId);
                                  } else {
                                    console.error('[OpenFullReport] Server fetch failed:', resp.status, resp.statusText);
                                  }
                                } catch (err) {
                                  console.error('[OpenFullReport] Fetch error:', err);
                                }
                              }
                            }}
                          />
                        )}
                      </div>
                    )}
                  </div>

                  {/* Angle stage picker — shown when angles-to-campaign needs stage selection */}
                  {isLastMessage && isCompletedAssistant && anglePickerStages && (
                    <div className="mt-3">
                      <StagePicker
                        availableStages={anglePickerStages}
                        onSubmit={(message) => {
                          useCanvasStore.setState({ anglePickerStages: null });
                          sendAgentMessage(message);
                        }}
                        onCancel={() => useCanvasStore.setState({ anglePickerStages: null })}
                      />
                    </div>
                  )}

                  {/* Suggested follow-ups */}
                  {hasFollowUps && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {followUps.map((chip, i) => {
                        const label = chip.label;
                        return (
                        <button
                          key={i}
                          data-testid={`follow-up-chip-${i}`}
                          onClick={() => {
                            if (isTyping) return;
                            if (chip.action === 'angles') openSidebar({type:'angle_set',title:chip.title!,angleSetId:chip.id!});
                            else if (chip.action === 'script') openSidebar({type:'video_script',title:chip.title!,draftId:chip.id!});
                            else if (chip.action === 'brief' || chip.action === 'report') openSidebar({type:'report',title:chip.title!,markdown:chip.content!});
                            else if (chip.action === 'images') {
                              setCanvasOpen(true);
                              setSelectedImageIds(chip.imageIds!);
                              if (chip.imageIds?.[0]) panToImage(chip.imageIds[0]);
                            } else if (chip.action === 'plan') {
                              void sendAgentMessage(chip.value!);
                            }
                          }}
                          disabled={isTyping}
                          className="inline-flex items-center gap-1.5 transition-colors"
                          style={{
                            background: 'transparent',
                            border: '1px solid #D1CDC6',
                            borderRadius: 9999,
                            padding: '5px 12px',
                            fontSize: 12,
                            color: '#3D3A36',
                            cursor: isTyping ? 'not-allowed' : 'pointer',
                            opacity: isTyping ? 0.5 : 1,
                          }}
                          onMouseEnter={(e) => { if (!isTyping) { (e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE'; (e.currentTarget as HTMLButtonElement).style.borderColor = '#B5B0A8'; } }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.borderColor = '#D1CDC6'; }}
                        >
                          <span>{label}</span>
                          <span style={{ color: '#8C8578', fontSize: 13 }}>→</span>
                        </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        <div ref={messagesEndRef} style={fullWidth ? { maxWidth: 740, margin: '0 auto' } : {}} />
      </div>

      {/* Brand Selection Widget */}
      {brandSelectionNeeded && (
        <div
          data-testid="brand-selection-widget"
          style={{
            padding: fullWidth ? '0 16px 12px' : '0 16px 12px',
            background: 'transparent',
          }}
        >
          <div style={fullWidth ? { maxWidth: 740, margin: '0 auto' } : {}}>
            <div
              style={{
                background: '#fff',
                border: '1px solid #E5E3DF',
                borderRadius: 12,
                padding: '14px 16px 16px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
              }}
            >
              <p style={{ margin: '0 0 10px', fontSize: 13.5, color: '#3D3A36', lineHeight: 1.5 }}>
                Where should I add this research?
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {brandSelectionNeeded.options.map((option, i) => (
                  <button
                    key={i}
                    data-testid={`brand-selection-option-${option.type}${option.brandId ? `-${option.brandId}` : ''}`}
                    onClick={() => {
                      const { target, projectId } = brandSelectionNeeded;
                      clearBrandSelection();
                      const pid = projectId ?? persistenceProjectId;
                      if (option.type === 'new_brand') {
                        if (pid) {
                          sendRoutedResearchMessage({ target, type: 'new_brand', projectId: pid });
                        } else {
                          sendResearchMessage(target);
                        }
                      } else if (option.type === 'competitor' && option.brandId) {
                        if (!pid) return;
                        sendRoutedResearchMessage({ target, type: 'competitor', brandId: option.brandId, projectId: pid });
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      background: '#F8F8F7',
                      border: '1px solid #E5E3DF',
                      borderRadius: 8,
                      padding: '10px 14px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 0.15s',
                      fontSize: 13.5,
                      color: '#1A1A1A',
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F0EEEB'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F8F8F7'; }}
                  >
                    <span style={{ fontSize: 16 }}>{option.type === 'new_brand' ? '✦' : '⊕'}</span>
                    <span style={{ flex: 1 }}>{option.label}</span>
                  </button>
                ))}
              </div>
              <button
                data-testid="brand-selection-cancel"
                onClick={() => clearBrandSelection()}
                style={{
                  marginTop: 10,
                  background: 'transparent',
                  border: 'none',
                  color: '#9CA3AF',
                  fontSize: 12,
                  cursor: 'pointer',
                  padding: '2px 0',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Input Area — sticky bottom */}
      {fullWidth ? (
        <div className="flex-shrink-0" style={{ padding: '0 16px 12px', background: '#F8F8F7' }}>
          <div style={{ maxWidth: 740, margin: '0 auto' }}>
            {activeResearchTask && (
              <TaskBottomBar
                task={activeResearchTask}
                conversationId={persistenceProjectId}
                onViewReport={async () => {
                  if (documentOverlayContent) {
                    openDocumentOverlay(documentOverlayTitle || activeResearchTask.plan.title, documentOverlayContent, persistenceProjectId);
                  } else {
                    try {
                      const resp = await fetch(`/api/canvas/tasks/${activeResearchTask.taskId}/document`);
                      if (resp.ok) {
                        const data = await resp.json();
                        openDocumentOverlay(data.title, data.document, persistenceProjectId);
                      } else {
                        console.error('[TaskBottomBar] Server fetch failed:', resp.status, resp.statusText);
                      }
                    } catch (err) {
                      console.error('[TaskBottomBar] Fetch error:', err);
                    }
                  }
                }}
                onDismiss={() => dismissResearchTask(convKey)}
                onScrollToProgress={() => {
                  taskProgressCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }}
              />
            )}
            {/* TaskProgressBarView suppressed — ChatToolProgressCard now renders tool progress inline in the message */}
            <BottomStatusLine isTyping={isWorking} progress={messages.slice().reverse().find(message => message.role === 'assistant' && message.isStreaming)} />
          {inputBar}
          </div>
        </div>
      ) : (
        <div className="flex-shrink-0" style={{ background: '#F8F8F7', fontFamily: 'Manrope, sans-serif' }}>
          {activeResearchTask && (
            <div style={{ padding: '0 16px' }}>
              <TaskBottomBar
                task={activeResearchTask}
                conversationId={persistenceProjectId}
                onViewReport={async () => {
                  if (documentOverlayContent) {
                    openDocumentOverlay(documentOverlayTitle || activeResearchTask.plan.title, documentOverlayContent, persistenceProjectId);
                  } else {
                    try {
                      const resp = await fetch(`/api/canvas/tasks/${activeResearchTask.taskId}/document`);
                      if (resp.ok) {
                        const data = await resp.json();
                        openDocumentOverlay(data.title, data.document, persistenceProjectId);
                      } else {
                        console.error('[TaskBottomBar] Server fetch failed:', resp.status, resp.statusText);
                      }
                    } catch (err) {
                      console.error('[TaskBottomBar] Fetch error:', err);
                    }
                  }
                }}
                onDismiss={() => dismissResearchTask(convKey)}
                onScrollToProgress={() => {
                  taskProgressCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }}
              />
            </div>
          )}
          {/* TaskProgressBarView suppressed — ChatToolProgressCard now renders tool progress inline in the message */}
          <BottomStatusLine isTyping={isWorking} progress={messages.slice().reverse().find(message => message.role === 'assistant' && message.isStreaming)} />
          {inputBar}
        </div>
      )}

      {/* Variants confirm modal — fires when the create-variants playbook
          hits the 12–24 soft threshold and needs user confirmation */}
      {pendingVariantsConfirm && (
        <div
          data-testid="variants-confirm-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={dismissVariantsConfirm}
        >
          <div
            className="max-w-md rounded bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold">
              Generate {pendingVariantsConfirm.total} variants?
            </h2>
            <p className="mt-2 text-sm text-neutral-600">
              This will regenerate {pendingVariantsConfirm.sourceCount} source ad
              {pendingVariantsConfirm.sourceCount === 1 ? '' : 's'} in{' '}
              {pendingVariantsConfirm.formats.length} format
              {pendingVariantsConfirm.formats.length === 1 ? '' : 's'} (
              {pendingVariantsConfirm.formats.join(', ')}).
            </p>
            <p className="mt-2 text-sm font-medium text-neutral-700">
              Estimated cost: {pendingVariantsConfirm.estimatedCredits.toLocaleString()} credits.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={dismissVariantsConfirm}
                className="rounded border border-neutral-300 bg-white px-3 py-1.5 text-sm hover:bg-neutral-50"
                data-testid="variants-confirm-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmVariantsAndSend}
                className="rounded bg-black px-3 py-1.5 text-sm text-white hover:bg-neutral-800"
                data-testid="variants-confirm-submit"
              >
                Generate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ad detail sidebar */}
      <AdDetailSidebar
        ad={selectedAdCard}
        onClose={() => setSelectedAdCard(null)}
        onSendToCanvas={(card) => {
          if (card.thumbnail_url) {
            const img = new window.Image();
            img.onload = () => {
              const { addImageToCanvas } = useCanvasStore.getState();
              addImageToCanvas(card.thumbnail_url, img.naturalWidth || 300, img.naturalHeight || 300, `${card.name}.jpg`, img.naturalWidth, img.naturalHeight);
            };
            img.onerror = () => {
              const { addImageToCanvas } = useCanvasStore.getState();
              addImageToCanvas(card.thumbnail_url, 300, 300, `${card.name}.jpg`, 300, 300);
            };
            img.src = card.thumbnail_url;
          }
          setSelectedAdCard(null);
        }}
      />

      {/* v3.91 — Connect Meta Account shortcut → Settings on the Connections tab. */}
      {metaSettingsOpen && (
        <SettingsModal initialTab="connections" onClose={() => setMetaSettingsOpen(false)} />
      )}

    </div>
  );
}
